import { useState, useEffect, useRef, useCallback } from "react";
import RegistrationFlow from "./Registration";
import { getAudioCtx, unlockAudio, ensureAudioRunning, waitForAudioUnlock } from "./audioEngine";

// ─── API BASE URL ─────────────────────────────────────────────────────────────
// In development the CRA proxy (package.json "proxy") forwards /api/* to localhost:3001,
// so API_BASE can be empty. In production (or for native apps) set REACT_APP_API_URL
// to the deployed API origin, e.g. https://api.rikai.tech
const API_BASE = process.env.REACT_APP_API_URL || "";

// Legal pages open in a new browser tab. CRA's dev proxy doesn't forward navigation
// requests (Accept: text/html), so in dev we point directly to the API server.
// In production, relative paths work via the same routing that serves the API.
const LEGAL_BASE = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:3001" : "");
const LEGAL_PP  = `${LEGAL_BASE}/api/legal/participant/privacy_policy/page`;
const LEGAL_TOS = `${LEGAL_BASE}/api/legal/participant/terms_of_service/page`;

// ─── THEME — Rik.ai brand palette ─────────────────────────────────────────────
const C = {
  bg: "#06080F", surface: "#0F1117", card: "#131520", border: "#1E2231",
  accent: "#8B5CF6", accentGlow: "#8B5CF630", teal: "#22D3EE", tealGlow: "#22D3EE20",
  blue: "#3B82F6", amber: "#F59E0B", red: "#EF4444", text: "#FFFFFF", muted: "#94A3B8", subtle: "#1E2231",
};

// All participant data, invitations, and surveys come from the API (see API_BASE above).
// Dashboard: GET /api/participants/:id/invitations
// Auth:      POST /api/auth/login → { user: { id, name, email } }

const AGENT_PROFILES = {
  Jal: {
    emoji:"J", avatar:"/avatars/jal.png", color:"#22D3EE", element:"Water",
    interviewStyle:"Empathy Mode",
    tagline:"Empathy Mode · Rapport Building",
    useCase:"Best for sensitive research — consumer emotions, healthcare, financial stress, life decisions",
    personality:`Interview Style: Empathy Mode.
Your role is rapport building — create a space where the participant feels emotionally safe to share honestly.

Conversation style:
- Warm, gentle, unhurried. Match the participant's pace and never push ahead.
- Ask open questions that invite feelings, not just facts ("How did that feel?" over "What did you do?").
- Reflect back what you heard before moving on — show you genuinely listened.
- Sit comfortably with pauses. Never fill silences awkwardly.
- Stay consistently calm and reassuring, even if the topic becomes difficult.

Do: Acknowledge emotions directly. Validate before probing. Use the participant's own words in your follow-up.
Don't: Rush to the next question. Use clinical or formal language. Show surprise or judgment at any answer.`,
  },
  Vayu: {
    emoji:"V", avatar:"/avatars/vayu.png", color:"#A78BFA", element:"Air",
    interviewStyle:"Discovery Mode",
    tagline:"Discovery Mode · Exploration",
    useCase:"Best for exploratory research — new categories, unaided awareness, early-stage concept testing",
    personality:`Interview Style: Discovery Mode.
Your role is open exploration — uncover what participants think and feel before they've been primed or directed.

Conversation style:
- Light, curious, and conversational — like a smart friend who genuinely wants to understand.
- Prefer open, divergent questions. Follow surprising answers with "Tell me more about that."
- Keep the energy casual but attentive. Never make the participant feel they're being tested.
- Comfortable with brief pauses but keep the energy moving gently.
- Low structure — let the conversation breathe and go where it naturally flows.

Do: Explore tangents that reveal unexpected insights. Show genuine curiosity. Celebrate surprising answers.
Don't: Lead the participant toward a particular answer. Summarise too early — let them finish their thought. Use jargon.`,
  },
  Agni: {
    emoji:"A", avatar:"/avatars/agni.png", color:"#F59E0B", element:"Fire",
    interviewStyle:"Insight Mode",
    tagline:"Insight Mode · Depth Probing",
    useCase:"Best for opinion research — brand loyalty, competitor comparison, product pain points, NPS",
    personality:`Interview Style: Insight Mode.
Your role is depth probing — surface the reasoning and beliefs behind participants' initial answers.

Conversation style:
- Warm and encouraging — make the participant feel their opinion is valuable and worth elaborating.
- Ask "why" and "what makes you say that" more than any other style, but always gently.
- Follow up on contradictions or strong emotions — those are the most valuable signals.
- Keep momentum and energy up — you are the most energised of all the interview styles.
- Bring enthusiasm that makes the participant want to share more.

Do: Praise specific answers genuinely. Push for concrete examples ("Can you give me a real situation where that happened?").
Don't: Let vague answers pass without a gentle follow-up. Be confrontational or turn encouragement into pressure.`,
  },
  Prithvi: {
    emoji:"P", avatar:"/avatars/prithvi.png", color:"#10B981", element:"Earth",
    interviewStyle:"Clarity Mode",
    tagline:"Clarity Mode · Validation",
    useCase:"Best for structured research — usage habits, purchase behaviour, satisfaction scoring, claims testing",
    personality:`Interview Style: Clarity Mode.
Your role is validation — help participants articulate their thoughts precisely so data is clean and actionable.

Conversation style:
- Calm, grounded, and structured. Create a stable, low-pressure environment.
- Ask clear, specific questions. When an answer is ambiguous, gently clarify before moving on.
- Give participants adequate time to think — never interpret silence as being finished.
- Steady and professional throughout, with consistent pacing.

Do: Confirm your understanding before moving on ("So if I'm hearing you correctly…"). Ask for specifics when answers are broad.
Don't: Allow ambiguous answers to pass without gentle clarification. Show impatience. Add personal commentary to what they've shared.`,
  },
  Aakash: {
    emoji:"A", avatar:"/avatars/aakash.png", color:"#6366F1", element:"Space",
    interviewStyle:"Reflection Mode",
    tagline:"Reflection Mode · Deep Thinking",
    useCase:"Best for strategic research — future trends, innovation, life stage transitions, long-horizon decisions",
    personality:`Interview Style: Reflection Mode.
Your role is to facilitate deep thinking — help participants access considered views they may not have fully articulated before.

Conversation style:
- Spacious and thoughtful — create an atmosphere where thinking is valued, not rushed.
- Ask questions that open up rather than narrow down ("What would you imagine…" / "Looking back…").
- Sit comfortably with complexity and ambiguity. Acknowledge when a question is genuinely difficult.
- Very high silence tolerance — silences are productive. Never rush them.
- Intellectually engaged but never urgent.

Do: Ask perspective-shifting questions. Let participants revise or contradict themselves — that's insight. Build on what they've said rather than jumping to the next question.
Don't: Rush the pace. Simplify complex answers. Expect quick or definitive responses — ambiguity is valuable data.`,
  },
};

// Resolve a survey's raw `agent` field to a current roster name.
// Survey rows in the DB may carry retired pre-rebrand names (Aria, Marcus,
// Lena, Kai) or different casing ("jal" from the customer portal). Without
// this, the UI shows the retired name while avatar/voice silently fall back
// to Jal's — a mismatched identity the participant can see and hear.
// Legacy names map to the closest current persona (matching old voice gender/style).
const LEGACY_AGENT_MAP = { aria:"Jal", marcus:"Agni", lena:"Vayu", kai:"Prithvi" };
function resolveAgentName(raw){
  if(raw){
    const key=raw.charAt(0).toUpperCase()+raw.slice(1).toLowerCase();
    if(AGENT_PROFILES[key]) return key;
    const legacy=LEGACY_AGENT_MAP[raw.toLowerCase()];
    if(legacy) return legacy;
  }
  return "Jal";
}
export { resolveAgentName, AGENT_PROFILES };
const AGENT_GUARDRAIL = `
Conduct this interview with professionalism at all times. You must:
- NEVER use profanity, offensive language, or inappropriate content of any kind.
- ONLY discuss topics directly relevant to this survey. If the participant raises unrelated subjects, acknowledge briefly and steer back to the survey.
- NEVER invent, assume, or fabricate information about the participant, the brand, or any facts not provided to you. If something is outside your knowledge, say so simply.
- NEVER be dismissive, sarcastic, aggressive, or condescending. Always respond with patience and respect, even if the participant is off-topic or unclear.`;

// Renders the agent avatar image if available, falls back to styled letter initial
function AgentAvatar({ agent, size=40, speaking=false, style={} }) {
  const [imgError, setImgError] = useState(false);
  const letter = agent?.emoji || "?";
  const color  = agent?.color || "#8B5CF6";
  const base   = { width:size, height:size, borderRadius:"50%", border:`2px solid ${speaking ? color : "transparent"}`,
                   boxShadow: speaking ? `0 0 ${size/3}px ${color}60` : "none", transition:"all 0.3s", flexShrink:0, ...style };
  if (agent?.avatar && !imgError) {
    return <img src={agent.avatar} alt={letter} style={{ ...base, objectFit:"cover" }} onError={()=>setImgError(true)} />;
  }
  return <div style={{ ...base, background:`${color}20`, display:"flex", alignItems:"center", justifyContent:"center",
                       fontSize:size*0.4, fontWeight:700, color }}>{letter}</div>;
}

// ─── TEMP DEBUG — VAD investigation badge ────────────────────────────────────
// On-screen VAD state for the iPad speech-detection investigation. Reads the
// mutable vadDebugRef (written every monitor frame in startListening) on a
// 250 ms poll so it never forces InterviewPage re-renders.
// REMOVE after testing: this component, the vadDebugRef writes in
// startListening, and the <VadDebugBadge> render in the interview layout.
function VadDebugBadge({ vadRef }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    const id = setInterval(() => setS({ ...vadRef.current }), 250);
    return () => clearInterval(id);
  }, [vadRef]);
  if (!s || !s.t) return null;
  const stale = Date.now() - s.t > 1000; // monitor loop not running (between turns)
  const fmt = v => (v == null ? "—" : Number(v).toExponential(1));
  return (
    <div style={{ position:"fixed", bottom:10, left:10, zIndex:10000, background:"rgba(0,0,0,0.78)", color:"#4ade80", fontFamily:"monospace", fontSize:10, lineHeight:1.6, padding:"6px 10px", borderRadius:6, pointerEvents:"none", whiteSpace:"pre", border:"1px solid #4ade8040" }}>
{`VAD DEBUG ${stale ? "(idle — between turns)" : ""}
engine:  ${s.engine || "—"}${s.demoted ? " (silero demoted)" : ""}
rms:     ${fmt(s.rms)}   floor: ${fmt(s.floor)}
ratio:   ${s.ratio != null ? s.ratio.toFixed(2) : "—"}   need: ${s.enterRatio != null ? s.enterRatio.toFixed(1) : "—"}x
calib:   ${s.calibDone ? "done" : "running…"}   speaking: ${s.speaking ? "YES" : "no"}   detected: ${s.detected ? "YES" : "no"}`}
    </div>
  );
}

// ─── SHARED UI ────────────────────────────────────────────────────────────────
function GlowDot({ color=C.teal, size=8, pulse=false }) {
  return <span style={{ display:"inline-block", width:size, height:size, borderRadius:"50%", background:color, boxShadow:`0 0 ${size*1.5}px ${color}`, animation:pulse?"pulseDot 2s infinite":"none", flexShrink:0 }} />;
}
function Tag({ label, color=C.blue }) {
  return <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color, border:`1px solid ${color}40`, background:`${color}15`, borderRadius:4, padding:"2px 8px" }}>{label}</span>;
}
function Card({ children, style={}, onClick, className="" }) {
  const [hov,setHov]=useState(false);
  return (
    <div onClick={onClick} onMouseEnter={()=>onClick&&setHov(true)} onMouseLeave={()=>setHov(false)}
      className={className}
      style={{ background:C.card, border:`1px solid ${hov?C.blue:C.border}`, borderRadius:12, padding:20, cursor:onClick?"pointer":"default", transform:hov?"translateY(-2px)":"none", transition:"all 0.2s", ...style }}>
      {children}
    </div>
  );
}
function Btn({ children, onClick, variant="primary", size="md", style={}, disabled=false, className="" }) {
  const [hov,setHov]=useState(false);
  const vs={ primary:{background:C.blue,color:"#fff",border:"none"}, outline:{background:"transparent",color:C.blue,border:`1px solid ${C.blue}`}, ghost:{background:"transparent",color:C.muted,border:`1px solid ${C.border}`}, danger:{background:C.red,color:"#fff",border:"none"}, teal:{background:C.teal,color:C.bg,border:"none"} };
  const sz={ sm:{fontSize:12,padding:"6px 14px"}, md:{fontSize:13,padding:"9px 20px"}, lg:{fontSize:15,padding:"12px 28px"} };
  return (
    <button onClick={onClick} disabled={disabled} onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      className={className}
      style={{ borderRadius:8, fontWeight:600, cursor:disabled?"not-allowed":"pointer", fontFamily:"inherit", letterSpacing:"0.01em", whiteSpace:"nowrap", opacity:disabled?0.45:hov?0.85:1, transform:hov&&!disabled?"scale(1.02)":"none", transition:"all 0.15s", ...vs[variant], ...sz[size], ...style }}>
      {children}
    </button>
  );
}
function ProgressBar({ value, max=100, color=C.blue, height=6 }) {
  return (
    <div style={{ background:C.subtle, borderRadius:99, height, overflow:"hidden" }}>
      <div style={{ width:`${Math.min(100,(value/max)*100)}%`, height:"100%", background:color, borderRadius:99, transition:"width 0.6s ease", boxShadow:`0 0 8px ${color}80` }} />
    </div>
  );
}
function SoundWave({ active, color=C.teal, bars=16, analyserRef }) {
  const [levels,setLevels]=useState(Array(bars).fill(0.04));
  const rafRef=useRef(null);
  const dataArrayRef=useRef(null);
  const smoothRef=useRef(Array(bars).fill(0.04));
  useEffect(()=>{
    if(active){
      const tick=()=>{
        if(smoothRef.current.length!==bars) smoothRef.current=Array(bars).fill(0.04);
        const analyser=analyserRef?.current;
        if(analyser){
          // Real audio analysis from Web Audio AnalyserNode
          if(!dataArrayRef.current||dataArrayRef.current.length!==analyser.frequencyBinCount){
            dataArrayRef.current=new Uint8Array(analyser.frequencyBinCount);
          }
          analyser.getByteFrequencyData(dataArrayRef.current);
          const data=dataArrayRef.current;
          const binCount=data.length;
          // Map bars to frequency range (focus on speech/music range)
          const newLevels=Array(bars).fill(0).map((_,i)=>{
            const start=Math.floor((i/bars)*binCount*0.6);
            const end=Math.max(start+1,Math.floor(((i+1)/bars)*binCount*0.6));
            let sum=0; for(let j=start;j<end;j++) sum+=data[j];
            const raw=sum/(end-start)/255;
            // Minimal smoothing for near-instant response
            smoothRef.current[i]=Math.min(1,smoothRef.current[i]*0.2+Math.max(0.06,raw*2.2)*0.8);
            return smoothRef.current[i];
          });
          setLevels(newLevels);
        } else {
          // Fallback: sinusoidal animation while audio is loading
          const t=Date.now()/350;
          const newLevels=Array(bars).fill(0).map((_,i)=>{
            const wave=0.45+0.35*Math.sin(t+i*0.7)+0.15*Math.sin(t*1.7+i*1.3);
            const target=Math.max(0.22,wave+(-0.08+Math.random()*0.16));
            smoothRef.current[i]=smoothRef.current[i]*0.25+target*0.75;
            return smoothRef.current[i];
          });
          setLevels(newLevels);
        }
        rafRef.current=requestAnimationFrame(tick);
      };
      rafRef.current=requestAnimationFrame(tick);
    } else {
      cancelAnimationFrame(rafRef.current);
      // Fast decay to near-zero
      const decay=()=>{
        let anyAbove=false;
        smoothRef.current=smoothRef.current.map(l=>{
          const next=l*0.65;
          if(next>0.015) anyAbove=true;
          return next;
        });
        setLevels([...smoothRef.current]);
        if(anyAbove) rafRef.current=requestAnimationFrame(decay);
        else setLevels(Array(bars).fill(0.04));
      };
      rafRef.current=requestAnimationFrame(decay);
    }
    return()=>cancelAnimationFrame(rafRef.current);
  },[active,bars,analyserRef]);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:3, height:36, overflow:"hidden" }}>
      {levels.map((l,i)=>(
        <div key={i} style={{ width:3, minHeight:3, height:`${Math.min(1,Math.max(0.04,l))*100}%`, borderRadius:2, background:active?color:C.subtle, boxShadow:active?`0 0 6px ${color}99`:"none" }} />
      ))}
    </div>
  );
}

// ─── CONSENT GATE SCREEN ─────────────────────────────────────────────────────
// Shown after login when the participant hasn't accepted the current version of
// the Privacy Policy or Terms of Service. Blocks access to the dashboard until
// both required documents are accepted.
function ConsentGateScreen({ participant, documents, apiFetch, onAccepted }) {
  const [ppChecked,  setPpChecked]  = useState(false);
  const [tosChecked, setTosChecked] = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");

  const needsPP  = documents.includes("privacy_policy");
  const needsToS = documents.includes("terms_of_service");
  // If only one document is required, pre-tick the other
  const ppOk  = !needsPP  || ppChecked;
  const tosOk = !needsToS || tosChecked;
  const canProceed = ppOk && tosOk;

  const handleAccept = async () => {
    setLoading(true); setError("");
    try {
      const res = await apiFetch(`${API_BASE}/api/legal/consent`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ participantId: participant.id, documents }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.error || "Failed to record consent. Please try again.");
        setLoading(false); return;
      }
      onAccepted();
    } catch {
      setError("Could not reach server. Please try again.");
      setLoading(false);
    }
  };

  const card = { background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:"32px 28px", maxWidth:520, margin:"0 auto", width:"100%" };
  const lbl  = { display:"flex", alignItems:"flex-start", gap:12, cursor:"pointer", padding:"14px 16px", background:`${C.surface}`, border:`1px solid ${C.border}`, borderRadius:10 };

  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", padding:"24px 16px", fontFamily:"'Poppins','Segoe UI',Arial,sans-serif" }}>
      <div style={card}>
        {/* Header */}
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:52, height:52, borderRadius:"50%", background:`linear-gradient(135deg,${C.purple}30,${C.blue}20)`, border:`1px solid ${C.purple}40`, marginBottom:16 }}>
            <span style={{ fontSize:24 }}>📋</span>
          </div>
          <h2 style={{ fontSize:20, fontWeight:700, color:C.text, margin:0, marginBottom:8 }}>Our terms have been updated</h2>
          <p style={{ color:C.muted, fontSize:13, lineHeight:1.6, margin:0 }}>
            Please review and accept the updated{" "}
            {needsPP && needsToS ? "Privacy Policy and Terms of Service" : needsPP ? "Privacy Policy" : "Terms of Service"}
            {" "}to continue using Rik.ai.
          </p>
        </div>

        {/* Checkboxes */}
        <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:24 }}>
          {needsToS && (
            <label style={lbl}>
              <input type="checkbox" checked={tosChecked} onChange={e => setTosChecked(e.target.checked)}
                style={{ marginTop:2, flexShrink:0, accentColor:C.accent, width:16, height:16, cursor:"pointer" }} />
              <span style={{ color:C.muted, fontSize:13, lineHeight:1.55 }}>
                I have read and agree to the updated{" "}
                <a href={LEGAL_TOS} target="_blank" rel="noopener" style={{ color:C.accent }} onClick={e=>e.stopPropagation()}>Terms of Service</a>.
              </span>
            </label>
          )}
          {needsPP && (
            <label style={lbl}>
              <input type="checkbox" checked={ppChecked} onChange={e => setPpChecked(e.target.checked)}
                style={{ marginTop:2, flexShrink:0, accentColor:C.accent, width:16, height:16, cursor:"pointer" }} />
              <span style={{ color:C.muted, fontSize:13, lineHeight:1.55 }}>
                I have read and agree to the updated{" "}
                <a href={LEGAL_PP} target="_blank" rel="noopener" style={{ color:C.accent }} onClick={e=>e.stopPropagation()}>Privacy Policy</a>.
              </span>
            </label>
          )}
        </div>

        {error && <p style={{ color:"#F87171", fontSize:12, marginBottom:16, padding:"10px 14px", background:"rgba(248,113,113,0.08)", borderRadius:8 }}>{error}</p>}

        <Btn onClick={handleAccept} disabled={!canProceed || loading} style={{ width:"100%" }} size="lg">
          {loading ? "Saving…" : "Accept and Continue →"}
        </Btn>

        <p style={{ color:C.muted, fontSize:11, textAlign:"center", marginTop:12, lineHeight:1.5 }}>
          You must accept the updated terms to continue. If you have questions, contact{" "}
          <a href="mailto:contact@rikai.tech" style={{ color:C.muted, textDecoration:"underline" }}>contact@rikai.tech</a>.
        </p>
      </div>
    </div>
  );
}

// ─── LOGIN PAGE ───────────────────────────────────────────────────────────────
function LoginPage({ onLogin, onRegister, verifyStatus, onVerifyStatusClear }) {
  const [tab,      setTab]      = useState("login");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [unverified,      setUnverified]      = useState(false);
  const [resendLoading,   setResendLoading]   = useState(false);
  const [resendDone,      setResendDone]      = useState(false);
  const [resendCooldown,  setResendCooldown]  = useState(0);

  // Cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const handleResendVerif = async () => {
    setResendLoading(true); setResendDone(false);
    try {
      await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setResendDone(true); setResendCooldown(60);
    } catch { /* ignore */ }
    setResendLoading(false);
  };

  // Forgot password flow state
  const [fpStep,       setFpStep]       = useState(null); // null | "email" | "otp"
  const [fpEmail,      setFpEmail]      = useState("");
  const [fpOtp,        setFpOtp]        = useState("");
  const [fpNewPass,    setFpNewPass]    = useState("");
  const [fpConfirm,    setFpConfirm]    = useState("");
  const [fpDevOtp,     setFpDevOtp]     = useState("");
  const [fpError,      setFpError]      = useState("");
  const [fpSuccess,    setFpSuccess]    = useState("");


  const inputStyle = (hasError=false) => ({
    width:"100%", background:C.surface, border:`1px solid ${hasError?C.red:C.border}`,
    borderRadius:8, padding:"11px 14px", color:C.text, fontSize:14,
    fontFamily:"inherit", boxSizing:"border-box", outline:"none",
  });
  const labelStyle = { display:"block", color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", fontWeight:700, marginBottom:8 };

  const handleLogin = async () => {
    setError(""); setUnverified(false); setResendDone(false);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError("Enter a valid email address."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.unverified) { setUnverified(true); setLoading(false); return; }
        setError(data.error || "Invalid email or password.");
        setLoading(false); return;
      }
      onLogin(data.user, null, null, data);
    } catch (e) {
      setError("Could not reach server. Is the API running?");
    }
    setLoading(false);
  };

  const handleForgotSendOtp = async () => {
    setFpError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fpEmail.trim())) { setFpError("Enter a valid email address."); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: fpEmail }),
      });
      const data = await res.json();
      if (!res.ok) { setFpError(data.error || "Could not send OTP."); setLoading(false); return; }
      setFpDevOtp(data._devOtp || "");
      setFpStep("otp");
    } catch { setFpError("Could not reach server."); }
    setLoading(false);
  };

  const handleForgotReset = async () => {
    setFpError("");
    if (fpNewPass !== fpConfirm) { setFpError("Passwords do not match."); return; }
    if (fpNewPass.length < 8)   { setFpError("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: fpEmail, otp: fpOtp, newPassword: fpNewPass }),
      });
      const data = await res.json();
      if (!res.ok) { setFpError(data.error || "Could not reset password."); setLoading(false); return; }
      setFpSuccess("Password reset! You can now sign in with your new password.");
      setFpStep(null);
      setTab("login");
      setEmail(fpEmail);
    } catch { setFpError("Could not reach server."); }
    setLoading(false);
  };


  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Poppins','Segoe UI',Arial,sans-serif" }}>
      <style>{`*{box-sizing:border-box;} @keyframes pulseDot{0%,100%{opacity:1}50%{opacity:0.3}} @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}} input{outline:none;}`}</style>
      <div style={{ width:"100%", maxWidth:460, padding:24, animation:"fadeUp 0.4s ease" }}>

        {/* Email verification status banner */}
        {verifyStatus==="success" && (
          <div style={{ background:`${C.teal}18`, border:`1px solid ${C.teal}40`, borderRadius:10, padding:"12px 16px", marginBottom:20, color:C.teal, fontSize:13, display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:16 }}>✓</span>
            <span>Email verified! You can now sign in.</span>
            <button onClick={onVerifyStatusClear} style={{ marginLeft:"auto", background:"none", border:"none", color:C.teal, cursor:"pointer", fontSize:16, lineHeight:1 }}>×</button>
          </div>
        )}
        {verifyStatus==="error" && (
          <div style={{ background:"#FF475718", border:"1px solid #FF475740", borderRadius:10, padding:"12px 16px", marginBottom:20, color:C.red, fontSize:13, display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:16 }}>✗</span>
            <span>Verification link is invalid or expired. Request a new one below.</span>
            <button onClick={onVerifyStatusClear} style={{ marginLeft:"auto", background:"none", border:"none", color:C.red, cursor:"pointer", fontSize:16, lineHeight:1 }}>×</button>
          </div>
        )}

        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:36 }}>
          <img src="/rik-logo.png" alt="Rik.ai" style={{ height:96, width:"auto", margin:"0 auto 14px", display:"block" }} onError={e=>{e.currentTarget.style.display="none";}} />
          <h1 style={{ margin:0, lineHeight:1.1 }}>
            <span style={{ fontSize:42, fontWeight:700, fontFamily:"'Poppins',sans-serif", color:C.text, letterSpacing:"-1px" }}>Rik</span><span style={{ fontSize:42, fontWeight:300, fontFamily:"'Poppins',sans-serif", letterSpacing:"-1px", background:"linear-gradient(135deg,#8B5CF6,#3B82F6,#22D3EE)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text", paddingLeft:3 }}>.ai</span>
          </h1>
          <p style={{ margin:"10px 0 0", color:C.muted, fontSize:11, letterSpacing:"0.22em", textTransform:"uppercase" }}>The new verse of market intelligence</p>
        </div>

        <Card style={{ padding:28 }}>
          {/* Tabs — hidden during forgot-password flow */}
          {!fpStep && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:4, background:C.surface, borderRadius:10, padding:4, marginBottom:24 }}>
              {[["login","Sign In"],["register","Register"]].map(([t,l])=>(
                <button key={t} onClick={()=>{setTab(t);setError("");}}
                  style={{ padding:"8px 0", borderRadius:7, border:"none", background:tab===t?C.card:"transparent", color:tab===t?C.text:C.muted, fontWeight:tab===t?600:400, fontSize:12, cursor:"pointer", fontFamily:"inherit", transition:"all 0.2s" }}>
                  {l}
                </button>
              ))}
            </div>
          )}

          {/* ── SIGN IN ── */}
          {tab==="login" && !fpStep && (
            <>
              {fpSuccess && (
                <div style={{ background:`${C.teal}18`, border:`1px solid ${C.teal}`, borderRadius:8, padding:"10px 14px", marginBottom:16, color:C.teal, fontSize:13 }}>
                  {fpSuccess}
                </div>
              )}
              <div style={{ marginBottom:14 }}>
                <label style={labelStyle}>Email address</label>
                <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com"
                  onKeyDown={e=>e.key==="Enter"&&handleLogin()} style={inputStyle()}
                  onFocus={e=>e.target.style.borderColor=C.teal} onBlur={e=>e.target.style.borderColor=C.border} />
              </div>
              <div style={{ marginBottom:6 }}>
                <label style={labelStyle}>Password</label>
                <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••"
                  onKeyDown={e=>e.key==="Enter"&&handleLogin()} style={inputStyle(!!error||unverified)}
                  onFocus={e=>e.target.style.borderColor=C.teal} onBlur={e=>e.target.style.borderColor=(error||unverified)?C.red:C.border} />
                {error && <p style={{ color:C.red, fontSize:12, marginTop:6 }}>{error}</p>}
                {unverified && (
                  <div style={{ background:"#FF475718", border:"1px solid #FF475740", borderRadius:8, padding:"12px 14px", marginTop:10 }}>
                    <p style={{ margin:"0 0 8px", color:C.red, fontSize:13 }}>Your email address has not been verified.</p>
                    {resendDone
                      ? <p style={{ margin:0, color:C.teal, fontSize:12 }}>Verification email sent. Check your inbox.</p>
                      : <button
                          onClick={handleResendVerif}
                          disabled={resendLoading || resendCooldown > 0}
                          style={{ background:"transparent", border:`1px solid ${C.red}60`, borderRadius:6, padding:"6px 14px", color:C.red, fontSize:12, cursor: resendCooldown > 0 || resendLoading ? "default" : "pointer", fontFamily:"inherit" }}>
                          {resendLoading ? "Sending…" : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend verification email →"}
                        </button>
                    }
                  </div>
                )}
              </div>
              <div style={{ textAlign:"right", marginTop:6, marginBottom:0 }}>
                <span style={{ color:C.teal, fontSize:12, cursor:"pointer" }}
                  onClick={()=>{ setFpStep("email"); setFpEmail(email); setFpError(""); setFpSuccess(""); }}>
                  Forgot password?
                </span>
              </div>
              <Btn onClick={handleLogin} disabled={loading||!email||!password} style={{ width:"100%", marginTop:16 }} size="lg">
                {loading ? "Signing in…" : "Sign In →"}
              </Btn>
              <p style={{ textAlign:"center", color:C.muted, fontSize:12, marginTop:16 }}>
                New here?{" "}
                <span style={{ color:C.teal, cursor:"pointer", fontWeight:600 }} onClick={()=>{setTab("register");setError("");}}>
                  Create an account →
                </span>
              </p>
            </>
          )}

          {/* ── FORGOT PASSWORD: Step 1 — Enter email ── */}
          {tab==="login" && fpStep==="email" && (
            <>
              <div style={{ marginBottom:20 }}>
                <h3 style={{ color:C.text, fontSize:16, fontWeight:700, margin:"0 0 6px" }}>Reset your password</h3>
                <p style={{ color:C.muted, fontSize:13, margin:0, lineHeight:1.6 }}>
                  Enter your account email. We'll send you a one-time code to reset your password.
                </p>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={labelStyle}>Email address</label>
                <input type="email" value={fpEmail} onChange={e=>setFpEmail(e.target.value)} placeholder="your@email.com"
                  onKeyDown={e=>e.key==="Enter"&&handleForgotSendOtp()} style={inputStyle(!!fpError)}
                  onFocus={e=>e.target.style.borderColor=C.teal} onBlur={e=>e.target.style.borderColor=fpError?C.red:C.border} />
                {fpError && <p style={{ color:C.red, fontSize:12, marginTop:6 }}>{fpError}</p>}
              </div>
              <Btn onClick={handleForgotSendOtp} disabled={loading||!fpEmail} style={{ width:"100%", marginTop:4 }} size="lg">
                {loading ? "Sending…" : "Send OTP →"}
              </Btn>
              <p style={{ textAlign:"center", color:C.muted, fontSize:12, marginTop:14 }}>
                <span style={{ color:C.teal, cursor:"pointer" }} onClick={()=>{setFpStep(null);setFpError("");}}>
                  ← Back to Sign In
                </span>
              </p>
            </>
          )}

          {/* ── FORGOT PASSWORD: Step 2 — Enter OTP + new password ── */}
          {tab==="login" && fpStep==="otp" && (
            <>
              <div style={{ marginBottom:16 }}>
                <h3 style={{ color:C.text, fontSize:16, fontWeight:700, margin:"0 0 6px" }}>Enter OTP &amp; new password</h3>
                <p style={{ color:C.muted, fontSize:13, margin:0, lineHeight:1.6 }}>
                  A reset code was sent to <strong style={{ color:C.text }}>{fpEmail}</strong>.
                </p>
              </div>
              {fpDevOtp && (
                <div style={{ background:`${C.amber}18`, border:`1px solid ${C.amber}40`, borderRadius:8, padding:"9px 14px", marginBottom:14, fontSize:12, color:C.muted }}>
                  <span style={{ fontWeight:700, color:C.amber }}>Dev mode:</span> OTP is <strong style={{ color:C.text, letterSpacing:"0.12em" }}>{fpDevOtp}</strong>
                  <br/><span style={{ fontSize:11 }}>(Email delivery not yet wired — use this code)</span>
                </div>
              )}
              <div style={{ marginBottom:12 }}>
                <label style={labelStyle}>One-time code</label>
                <input value={fpOtp} onChange={e=>setFpOtp(e.target.value.replace(/\D/g,"").slice(0,6))} placeholder="6-digit code"
                  style={inputStyle(!!fpError)} maxLength={6}
                  onFocus={e=>e.target.style.borderColor=C.teal} onBlur={e=>e.target.style.borderColor=fpError?C.red:C.border} />
              </div>
              <div style={{ marginBottom:12 }}>
                <label style={labelStyle}>New password</label>
                <input type="password" value={fpNewPass} onChange={e=>setFpNewPass(e.target.value)} placeholder="Min 8 characters"
                  style={inputStyle(!!fpError)}
                  onFocus={e=>e.target.style.borderColor=C.teal} onBlur={e=>e.target.style.borderColor=fpError?C.red:C.border} />
              </div>
              <div style={{ marginBottom:6 }}>
                <label style={labelStyle}>Confirm new password</label>
                <input type="password" value={fpConfirm} onChange={e=>setFpConfirm(e.target.value)} placeholder="Repeat password"
                  onKeyDown={e=>e.key==="Enter"&&handleForgotReset()} style={inputStyle(!!fpError)}
                  onFocus={e=>e.target.style.borderColor=C.teal} onBlur={e=>e.target.style.borderColor=fpError?C.red:C.border} />
                {fpError && <p style={{ color:C.red, fontSize:12, marginTop:6 }}>{fpError}</p>}
              </div>
              <Btn onClick={handleForgotReset} disabled={loading||!fpOtp||!fpNewPass||!fpConfirm} style={{ width:"100%", marginTop:12 }} size="lg">
                {loading ? "Resetting…" : "Reset Password →"}
              </Btn>
              <p style={{ textAlign:"center", color:C.muted, fontSize:12, marginTop:14, lineHeight:1.6 }}>
                Didn't get a code?{" "}
                <span style={{ color:C.teal, cursor:"pointer" }} onClick={()=>{ setFpStep("email"); setFpOtp(""); setFpDevOtp(""); setFpError(""); }}>
                  Resend OTP
                </span>
                {" · "}
                <span style={{ color:C.teal, cursor:"pointer" }} onClick={()=>{setFpStep(null);setFpError("");}}>
                  Cancel
                </span>
              </p>
            </>
          )}

          {/* ── REGISTER ── */}
          {tab==="register" && (
            <div style={{ textAlign:"center", padding:"8px 0 4px" }}>
              <div style={{ fontSize:48, marginBottom:16 }}>👋</div>
              <h3 style={{ color:C.text, fontSize:18, fontWeight:700, margin:"0 0 8px" }}>New to Rik.ai?</h3>
              <p style={{ color:C.muted, fontSize:13, marginBottom:24, lineHeight:1.7 }}>
                Creating an account takes about 5 minutes. We'll collect your profile details and verify your device so you're ready for any survey.
              </p>
              <div style={{ display:"grid", gap:8, marginBottom:24, textAlign:"left" }}>
                {[["📱","Mobile OTP verification"],["👤","Basic demographic profile"],["🛒","Shopping & lifestyle preferences"],["📷","Camera & mic device check"]].map(([icon,text])=>(
                  <div key={text} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", background:C.surface, borderRadius:8, border:`1px solid ${C.border}` }}>
                    <span style={{ fontSize:18 }}>{icon}</span>
                    <span style={{ color:C.text, fontSize:13 }}>{text}</span>
                  </div>
                ))}
              </div>
              <Btn onClick={onRegister} style={{ width:"100%" }} size="lg">Start Registration →</Btn>
              <p style={{ color:C.muted, fontSize:12, marginTop:12 }}>
                Already registered?{" "}
                <span style={{ color:C.teal, cursor:"pointer", fontWeight:600 }} onClick={()=>{setTab("login");setError("");}}>Sign in</span>
              </p>
            </div>
          )}

        </Card>

        <p style={{ textAlign:"center", color:C.muted, fontSize:11, marginTop:20 }}>
          <a href={LEGAL_PP} target="_blank" rel="noopener" style={{ color:C.muted, textDecoration:"underline" }}>Privacy Policy</a>
          &nbsp;&middot;&nbsp;
          <a href={LEGAL_TOS} target="_blank" rel="noopener" style={{ color:C.muted, textDecoration:"underline" }}>Terms of Service</a>
        </p>
      </div>
    </div>
  );
}

// ─── DASHBOARD PAGE ───────────────────────────────────────────────────────────
function DashboardPage({ participant, onLaunchSurvey, apiFetch, deletionPending, deletionScheduledFor, onRestoreAccount, onInvitationsLoaded, onPayoutNotifsLoaded }) {
  const [invitations, setInvitations] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [fetchError,  setFetchError]  = useState("");
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreError,   setRestoreError]   = useState("");

  // Referral banner state — fetch once if participant is eligible
  const [referralBanner,    setReferralBanner]    = useState(null); // { shareLink, referralCode, stats }
  const [referralBannerCopied, setReferralBannerCopied] = useState(false);

  useEffect(() => {
    apiFetch(`${API_BASE}/api/participants/${participant.id}/invitations`)
      .then(r => r.json())
      .then(d => {
        const invs = d.invitations || [];
        setInvitations(invs);
        setLoading(false);
        // Notify header bell with pending (not-yet-started) invitations
        if (onInvitationsLoaded) {
          onInvitationsLoaded(invs.filter(inv => inv.status === "pending" && !inv.resumeSessionId));
        }
      })
      .catch(() => { setFetchError("Could not load surveys. Is the API running?"); setLoading(false); });
  }, [participant.id]); // eslint-disable-line

  // Surface UPI action items in the header bell: missing UPI (with earnings
  // waiting) or a pending penny-drop verification. Email/SMS wiring is deferred.
  useEffect(() => {
    if (!onPayoutNotifsLoaded) return;
    let cancelled = false;
    Promise.all([
      apiFetch(`${API_BASE}/api/participants/${participant.id}/payment-details`).then(r => r.json()).catch(() => ({})),
      apiFetch(`${API_BASE}/api/participants/${participant.id}/payouts`).then(r => r.json()).catch(() => ({})),
    ]).then(([pd, po]) => {
      if (cancelled) return;
      const detail  = pd?.paymentDetail || null;
      const payouts = po?.payouts || [];
      const open    = payouts.filter(p => p.status === "under_review" || p.status === "pending_payout");
      const notifs  = [];
      if (!detail || !detail.maskedUpiId) {
        // No UPI on file — only nudge once there's money waiting to be paid out.
        if (open.length > 0) {
          notifs.push({ id: "upi-missing", type: "upi_missing", count: open.length, amount: open.reduce((s, p) => s + (p.amount || 0), 0) });
        }
      } else if (detail.upiVerificationStatus === "pending_verification") {
        notifs.push({ id: "upi-verify", type: "upi_verify", maskedUpiId: detail.maskedUpiId });
      }
      onPayoutNotifsLoaded(notifs);
    });
    return () => { cancelled = true; };
  }, [participant.id]); // eslint-disable-line

  // Fetch referral info for banner if participant is eligible
  useEffect(() => {
    if (!participant?.referralEligible) return;
    apiFetch(`${API_BASE}/api/participants/${participant.id}/referral`)
      .then(r => r.json())
      .then(d => { if (d.referralCode) setReferralBanner(d); })
      .catch(() => {});
  }, [participant?.id, participant?.referralEligible]); // eslint-disable-line

  // pending: not yet started. resumable: started but abandoned. completed: fully done.
  const pending   = invitations.filter(inv => inv.status === "pending" && !inv.resumeSessionId);
  const resumable = invitations.filter(inv => inv.status === "pending" && inv.resumeSessionId);
  const completed = invitations.filter(inv => inv.status === "completed");

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })
    : "";

  if (loading) return (
    <div style={{ maxWidth:780, margin:"0 auto", textAlign:"center", paddingTop:80 }}>
      <div style={{ color:C.muted, fontSize:14 }}>Loading your surveys…</div>
    </div>
  );

  const handleRestore = async () => {
    setRestoreLoading(true); setRestoreError("");
    try { await onRestoreAccount(); }
    catch { setRestoreError("Could not restore account. Please try again."); }
    setRestoreLoading(false);
  };

  return (
    <div style={{ maxWidth:780, margin:"0 auto" }}>
      {/* Deletion pending warning banner */}
      {deletionPending && (
        <div style={{ background:"#3d1a1a", border:"1px solid #8b2c2c", borderRadius:10, padding:"14px 18px", marginBottom:24, display:"flex", flexWrap:"wrap", alignItems:"center", gap:12 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ color:"#f87171", fontWeight:700, fontSize:14, marginBottom:4 }}>Account scheduled for deletion</div>
            <div style={{ color:"#fca5a5", fontSize:13 }}>
              Your account will be permanently deleted on{" "}
              <strong>{deletionScheduledFor ? new Date(deletionScheduledFor).toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"}) : "soon"}</strong>.
              All your data will be erased. You can cancel this within the cooling period.
            </div>
            {restoreError && <div style={{ color:"#f87171", fontSize:12, marginTop:4 }}>{restoreError}</div>}
          </div>
          <Btn variant="outline" size="sm" onClick={handleRestore} disabled={restoreLoading}
            style={{ borderColor:"#f87171", color:"#f87171", whiteSpace:"nowrap" }}>
            {restoreLoading ? "Restoring…" : "Restore My Account"}
          </Btn>
        </div>
      )}
      {/* Referral banner — shown only to eligible (completed first survey) participants */}
      {!deletionPending && referralBanner && (
        <div style={{ background:`linear-gradient(135deg,${C.blue}15,${C.teal}10)`, border:`1px solid ${C.blue}35`, borderRadius:12, padding:"14px 18px", marginBottom:20, display:"flex", flexWrap:"wrap", alignItems:"center", gap:12 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ color:C.blue, fontWeight:700, fontSize:14, marginBottom:3 }}>Earn ₹30 for every friend who completes their first survey</div>
            <div style={{ color:C.muted, fontSize:12 }}>
              Share your code <span style={{ fontFamily:"monospace", color:C.text, fontWeight:700, letterSpacing:"0.08em" }}>{referralBanner.referralCode}</span>
              {referralBanner.stats?.qualified > 0 && <span style={{ marginLeft:8, color:C.teal }}>· {referralBanner.stats.qualified} qualified · ₹{referralBanner.stats.totalEarned} earned</span>}
            </div>
          </div>
          <button
            onClick={()=>{
              navigator.clipboard?.writeText(referralBanner.shareLink || "").then(()=>{ setReferralBannerCopied(true); setTimeout(()=>setReferralBannerCopied(false),2000); });
            }}
            style={{ background: referralBannerCopied ? `${C.teal}20` : `${C.blue}20`, border:`1px solid ${referralBannerCopied ? C.teal : C.blue}60`, borderRadius:8, padding:"8px 16px", color: referralBannerCopied ? C.teal : C.blue, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", flexShrink:0 }}>
            {referralBannerCopied ? "Link Copied!" : "Copy Referral Link"}
          </button>
        </div>
      )}

      {/* Welcome */}
      <div style={{ marginBottom:32 }}>
        <h2 style={{ margin:0, fontSize:22, fontWeight:700, color:C.text }}>Welcome back, {participant.name.split(" ")[0]}</h2>
        <p style={{ margin:"6px 0 0", color:C.muted, fontSize:14 }}>
          {deletionPending
            ? <span style={{ color:"#f87171" }}>Your account is scheduled for deletion. Restore it above to continue.</span>
            : fetchError
              ? <span style={{ color:C.red }}>{fetchError}</span>
              : <>You have{" "}
                  <span style={{ color:(pending.length+resumable.length)>0?C.amber:C.muted, fontWeight:600 }}>
                    {pending.length+resumable.length} pending interview{(pending.length+resumable.length)!==1?"s":""}
                  </span>{" "}waiting for you.</>
          }
        </p>
      </div>

      {/* All content below is hidden while deletion is pending */}
      {deletionPending ? null : pending.length>0&&(
        <section style={{ marginBottom:36 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
            <GlowDot color={C.amber} pulse size={8} />
            <h3 style={{ margin:0, fontSize:13, fontWeight:700, color:C.text, textTransform:"uppercase", letterSpacing:"0.09em" }}>Awaiting Your Response</h3>
          </div>
          <div style={{ display:"grid", gap:14 }}>
            {pending.map(inv => {
              const s     = inv.survey;
              const agentName = resolveAgentName(s.agent);
              const agent = AGENT_PROFILES[agentName];
              return (
                <Card key={inv.id} onClick={()=>onLaunchSurvey(s, inv.id, null)} className="dash-card">
                  <div className="dash-card-head">
                    <div className="dash-card-icon"><AgentAvatar agent={agent} size={40} /></div>
                    <div className="dash-card-name" style={{ color:C.text }}>{s.name}</div>
                    <Btn variant="teal" size="sm" className="dash-card-btn">Start →</Btn>
                  </div>
                  <div className="dash-card-meta">
                    <Tag label={s.category||"Survey"} color={agent.color} />
                    <span style={{ color:C.muted, fontSize:12 }}>~{s.estimatedMins} min</span>
                    <span style={{ color:C.muted, fontSize:12 }}>·</span>
                    <span style={{ color:C.muted, fontSize:12 }}>with {resolveAgentName(s.agent)}</span>
                    {inv.invitedAt&&<><span style={{ color:C.muted, fontSize:12 }}>·</span><span style={{ color:C.muted, fontSize:12 }}>Invited {fmtDate(inv.invitedAt)}</span></>}
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Resumable — started but abandoned */}
      {!deletionPending && resumable.length>0&&(
        <section style={{ marginBottom:36 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
            <GlowDot color={C.blue} pulse size={8} />
            <h3 style={{ margin:0, fontSize:13, fontWeight:700, color:C.text, textTransform:"uppercase", letterSpacing:"0.09em" }}>In Progress</h3>
          </div>
          <div style={{ display:"grid", gap:14 }}>
            {resumable.map(inv => {
              const s     = inv.survey;
              const agent = AGENT_PROFILES[resolveAgentName(s.agent)];
              const blocked = inv.resumeBlocked; // "disabled" | "max_reached" | "window_expired" | undefined
              const expired = inv.resumeExpired;
              const canResume = !blocked;
              // Human-readable reason the session cannot be resumed
              const blockedMsg = blocked === "disabled"       ? "Resume is not available for this survey."
                               : blocked === "max_reached"    ? "Maximum resume attempts reached — please start fresh."
                               : blocked === "window_expired" ? "Resume window has expired — please start fresh."
                               : null;
              return (
                <Card key={inv.id}
                  onClick={canResume ? ()=>onLaunchSurvey(s, inv.id, inv.resumeSessionId, inv.resumeLanguage) : ()=>onLaunchSurvey(s, inv.id, null, null)}
                  className="dash-card"
                  style={{ borderColor: canResume ? `${C.blue}40` : `${C.amber}40`, opacity: canResume ? 1 : 0.85 }}>
                  <div className="dash-card-head">
                    <div className="dash-card-icon"><AgentAvatar agent={agent} size={40} /></div>
                    <div className="dash-card-name" style={{ color:C.text }}>{s.name}</div>
                    {canResume
                      ? <Btn variant="outline" size="sm" className="dash-card-btn">Resume →</Btn>
                      : <Btn variant="outline" size="sm" className="dash-card-btn">Start Fresh →</Btn>
                    }
                  </div>
                  <div className="dash-card-meta">
                    <Tag label={s.category||"Survey"} color={agent.color} />
                    <span style={{ color:C.muted, fontSize:12 }}>~{s.estimatedMins} min</span>
                    <span style={{ color:C.muted, fontSize:12 }}>·</span>
                    <span style={{ color:C.muted, fontSize:12 }}>with {resolveAgentName(s.agent)}</span>
                    <span style={{ color:C.muted, fontSize:12 }}>·</span>
                    {canResume
                      ? <span style={{ color:C.teal, fontSize:12, fontWeight:500 }}>Partially completed</span>
                      : <span style={{ color:C.amber, fontSize:12, fontWeight:500 }}>{expired ? "Session expired" : "Cannot resume"}</span>
                    }
                  </div>
                  {blockedMsg && (
                    <div style={{ marginTop:8, fontSize:12, color:C.amber, lineHeight:1.5 }}>{blockedMsg}</div>
                  )}
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Completed */}
      {!deletionPending && completed.length>0&&(
        <section>
          <h3 style={{ margin:"0 0 14px", fontSize:13, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.09em" }}>Completed</h3>
          <div style={{ display:"grid", gap:10 }}>
            {completed.map(inv => {
              const s     = inv.survey;
              const agent = AGENT_PROFILES[resolveAgentName(s.agent)];
              return (
                <div key={inv.id} className="dash-card" style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, opacity:0.65 }}>
                  <div className="dash-card-head">
                    <div className="dash-card-icon"><AgentAvatar agent={agent} size={40} style={{ opacity:0.6 }} /></div>
                    <div className="dash-card-name" style={{ color:C.text }}>{s.name}</div>
                    <Tag label="Done" color={C.teal} />
                  </div>
                  <div className="dash-card-meta" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:6 }}>
                    <span style={{ color:C.muted, fontSize:12 }}>Completed{inv.completedAt?` · ${fmtDate(inv.completedAt)}`:""}</span>
                    {inv.payoutStatus && (()=>{
                      const payoutStatusMap = {
                        under_review:   { label:"Under Review",   bg:`${C.amber}20`,  color:C.amber },
                        pending_payout: { label:"Payout Pending", bg:`${C.blue}20`,  color:C.blue },
                        paid:           { label:`Paid ₹${inv.payoutAmount||""}`, bg:`${C.teal}20`, color:C.teal },
                        cancelled:      { label:"Cancelled",      bg:`${C.border}40`, color:C.muted },
                        on_hold:        { label:"On Hold",        bg:`${C.amber}15`,  color:C.amber },
                      };
                      const pm = payoutStatusMap[inv.payoutStatus];
                      if (!pm) return null;
                      return (
                        <span style={{ background:pm.bg, color:pm.color, fontSize:11, fontWeight:700, padding:"3px 8px", borderRadius:6, flexShrink:0 }}>
                          {pm.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {pending.length===0&&resumable.length===0&&completed.length===0&&!fetchError&&(
        <Card style={{ textAlign:"center", padding:48 }}>
          <div style={{ fontSize:44, marginBottom:12 }}>📭</div>
          <div style={{ color:C.text, fontSize:16, fontWeight:600, marginBottom:6 }}>No interviews yet</div>
          <div style={{ color:C.muted, fontSize:13 }}>When a researcher invites you to a survey, it will appear here.</div>
        </Card>
      )}
    </div>
  );
}

// ─── SURVEY LOGIC UTILITIES ───────────────────────────────────────────────────

// Evaluates a condition object against an answer string.
// Returns true (pass) or false (fail). No condition = always pass.
// Used by both screener evaluation and skip logic.
function evaluateScreenerCondition(condition, answer) {
  if (!condition) return true;
  const ans  = (answer  || "").trim().toLowerCase();
  const val  = (condition.value || "").toLowerCase();
  switch (condition.operator) {
    case "equals":       return ans === val;
    case "not_equals":   return ans !== val;
    case "contains":     return ans.includes(val);
    case "not_contains": return !ans.includes(val);
    case "any":          return true;
    default:             return true;
  }
}

// Resolves the next question index after a participant answers questionIndex.
// Parses skipLogic JSON on the answered question and evaluates its condition.
// Returns jumpToIndex if the condition matches, otherwise questionIndex + 1.
// skipLogic shape: { condition: { operator, value }, jumpToIndex: number }
function resolveNextQuestion(questionIndex, answer, questions) {
  const q = questions[questionIndex];
  if (!q?.skipLogic) return questionIndex + 1;
  let logic = q.skipLogic;
  if (typeof logic === "string") {
    try { logic = JSON.parse(logic); } catch { return questionIndex + 1; }
  }
  if (evaluateScreenerCondition(logic.condition, answer) && typeof logic.jumpToIndex === "number") {
    return logic.jumpToIndex;
  }
  return questionIndex + 1;
}

// ─── DISQUALIFIED PAGE ────────────────────────────────────────────────────────
function DisqualifiedPage({ survey, onBack }) {
  return (
    <div style={{ maxWidth:520, margin:"0 auto", textAlign:"center", paddingTop:40, animation:"fadeUp 0.4s ease" }}>
      <div style={{ fontSize:56, marginBottom:20 }}>🙏</div>
      <h2 style={{ color:C.text, fontSize:22, fontWeight:700, marginBottom:12 }}>
        Thank you for your time
      </h2>
      <p style={{ color:C.muted, fontSize:14, lineHeight:1.7, marginBottom:8 }}>
        Based on your responses, this survey isn't the right fit for your profile right now.
      </p>
      <p style={{ color:C.muted, fontSize:14, lineHeight:1.7, marginBottom:32 }}>
        This isn't a reflection on you — researchers target very specific groups.
        Other surveys may be a great match for you.
      </p>

      <Card style={{ marginBottom:32, padding:"16px 20px", textAlign:"left" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ fontSize:28 }}>📋</div>
          <div>
            <div style={{ color:C.text, fontSize:14, fontWeight:600, marginBottom:6 }}>{survey?.name}</div>
            <Tag label="Not eligible this time" color={C.muted} />
          </div>
        </div>
      </Card>

      <Btn size="lg" onClick={onBack} style={{ width:"100%" }}>← Back to Dashboard</Btn>
    </div>
  );
}

// ─── SETUP PAGE ───────────────────────────────────────────────────────────────
function EnvCheckModal({ modality, survey, participantId, interviewToken, onConfirm }) {
  const isVideo = modality === "video";
  const [consentTicked, setConsentTicked] = useState(false);
  const [submitting,    setSubmitting]    = useState(false);

  // Record per-survey facial-analysis consent, then proceed
  const handleConfirm = async () => {
    // Unlock audio FIRST, while still synchronously inside the tap — iOS discards
    // the gesture token at the first await, and this is the last tap before TTS.
    unlockAudio();
    if (isVideo && !consentTicked) return;
    if (isVideo && survey?.id) {
      setSubmitting(true);
      try {
        if (interviewToken) {
          // Supplier participant — no Rik.ai account; use supplier-consent endpoint
          const r = await fetch(`${API_BASE}/api/supplier-consent`, {
            method:  "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${interviewToken}` },
            body:    JSON.stringify({ consentType: "facial_analysis" }),
          });
          if (r.ok) {
            const d = await r.json();
            if (d.withdrawalToken) onConfirm({ withdrawalToken: d.withdrawalToken });
            else onConfirm({});
            return;
          }
        } else if (participantId) {
          // Registered participant — use standard consent endpoint
          await fetch(`/api/participants/${participantId}/consent`, {
            method:      "POST",
            headers:     { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ consentType: "facial_analysis", surveyId: survey.id, version: "1.0" }),
          });
        }
      } catch (e) {
        console.warn("[Consent] Failed to record per-survey consent:", e.message);
      }
      setSubmitting(false);
    }
    onConfirm({});
  };
  const tips = isVideo
    ? [
        { icon:"🔇", title:"Find a quiet space", desc:"Move to a room with no background noise, TV, or music. Background noise can cause responses to be rejected." },
        { icon:"💡", title:"Good lighting is required", desc:"Sit facing a window or a lamp. Avoid having a bright light behind you. Your face must be clearly visible." },
        { icon:"😊", title:"Face the camera directly", desc:"Keep your face centred in the frame throughout the interview. Partial or obscured faces affect quality scores." },
        { icon:"🎙", title:"Speak clearly and close to your mic", desc:"Stay close to your device's microphone. Speak at a normal pace — not too fast, not too slow." },
        { icon:"⚠️", title:"Quality affects eligibility", desc:"Poor audio or video quality, or a face that isn't detected, may disqualify your session from the survey.", warn:true },
      ]
    : [
        { icon:"🔇", title:"Find a quiet space", desc:"Move away from background noise — TV, music, or chatter can prevent your responses from being captured." },
        { icon:"🎙", title:"Speak clearly and close to your mic", desc:"Keep your device close. Speak at a comfortable, clear pace." },
        { icon:"🎧", title:"Headphones help (optional)", desc:"If available, headphones with a mic improve audio quality significantly." },
        { icon:"⚠️", title:"Quality affects eligibility", desc:"Poor audio quality or responses that can't be transcribed may disqualify your session.", warn:true },
      ];
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20, backdropFilter:"blur(4px)" }}>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, maxWidth:500, width:"100%", boxShadow:"0 24px 80px rgba(0,0,0,0.6)", animation:"fadeUp 0.25s ease", maxHeight:"calc(100dvh - 40px)", overflowY:"auto", display:"flex", flexDirection:"column" }}>
        {/* Header */}
        <div style={{ padding:"22px 24px 18px", borderBottom:`1px solid ${C.border}` }}>
          <div style={{ fontSize:28, marginBottom:8 }}>{isVideo ? "🎥" : "🎙"}</div>
          <div style={{ color:C.text, fontSize:17, fontWeight:700, marginBottom:4 }}>Before you begin</div>
          <div style={{ color:C.muted, fontSize:13 }}>Please take a moment to set up your environment for the best experience.</div>
        </div>
        {/* Tips */}
        <div style={{ padding:"16px 24px", display:"flex", flexDirection:"column", gap:12 }}>
          {tips.map(t=>(
            <div key={t.title} style={{ display:"flex", gap:12, alignItems:"flex-start", background:t.warn?`${C.amber}10`:"transparent", border:t.warn?`1px solid ${C.amber}35`:"none", borderRadius:t.warn?8:0, padding:t.warn?"10px 12px":0 }}>
              <span style={{ fontSize:18, flexShrink:0, marginTop:1 }}>{t.icon}</span>
              <div>
                <div style={{ color:t.warn?C.amber:C.text, fontSize:13, fontWeight:600, marginBottom:2 }}>{t.title}</div>
                <div style={{ color:t.warn?`${C.amber}cc`:C.muted, fontSize:12, lineHeight:1.55 }}>{t.desc}</div>
              </div>
            </div>
          ))}
        </div>
        {/* Per-survey consent — required for video modality (DPDP Act 2023) */}
        {isVideo && (
          <div style={{ padding:"0 24px 14px", flexShrink:0 }}>
            <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer", background:`${C.teal}08`, border:`1px solid ${C.teal}25`, borderRadius:8, padding:"10px 12px" }}>
              <input type="checkbox" checked={consentTicked} onChange={e=>setConsentTicked(e.target.checked)}
                style={{ marginTop:2, flexShrink:0, accentColor:C.teal, width:15, height:15, cursor:"pointer" }} />
              <span style={{ color:C.muted, fontSize:12, lineHeight:1.6 }}>
                I consent to <strong style={{ color:C.text }}>facial expression analysis</strong> for this interview.
                Rik.ai will process facial expression data (not identity) on behalf of a{survey?.category ? ` ${survey.category} company` : " research company"} for market research.
                Results are shared in anonymised form. My identity will not be disclosed.
                {interviewToken && <> You will receive a link at the end of the session to withdraw this consent within 90 days.</>}
              </span>
            </label>
          </div>
        )}

        {/* Actions */}
        <div style={{ padding:"16px 24px 22px", display:"flex", gap:10, borderTop:`1px solid ${C.border}`, flexShrink:0 }}>
          <Btn variant="primary" onClick={handleConfirm} disabled={submitting || (isVideo && !consentTicked)} style={{ flex:1 }}>
            {submitting ? "Starting…" : isVideo ? "✅ I'm ready — start interview" : "✅ Ready — start interview"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function SetupPage({ survey, participant, onStart, onBack, interviewToken, multilingualSupport, supportedLanguages, lockedLanguage }) {
  const agentName = resolveAgentName(survey?.agent);
  const agent     = AGENT_PROFILES[agentName];
  const modality = (survey?.modality || "video").toLowerCase();
  const [showEnvModal, setShowEnvModal] = useState(false);

  // Language picker: only for profileless (supplier) participants when multilingual is on
  const showLangPicker = !participant && multilingualSupport && survey?.multilingualEnabled && (supportedLanguages || []).length > 1;
  // lockedLanguage is the language stored on the resumed session — only lock if it's actually in the supported list
  const effectiveLocked = showLangPicker && lockedLanguage && (supportedLanguages || []).includes(lockedLanguage) ? lockedLanguage : null;
  const langLocked = !!effectiveLocked;
  const [selectedLanguage, setSelectedLanguage] = useState(effectiveLocked || (supportedLanguages || [])[0] || "English");
  // Sync state if effectiveLocked becomes available after config loads (race condition fix)
  useEffect(() => { if (effectiveLocked) setSelectedLanguage(effectiveLocked); }, [effectiveLocked]); // eslint-disable-line
  // When locked, bypass selectedLanguage state — always use the authoritative locked value
  const activeLanguage = langLocked ? effectiveLocked : selectedLanguage;

  const formatLabel    = modality === "video" ? "Conversational video interview"
                       : modality === "voice" ? "Conversational voice interview"
                       : "Text-based chat interview";
  const recordingLabel = modality === "video" ? "Video, audio & transcript captured"
                       : modality === "voice" ? "Audio & transcript captured"
                       : "Transcript captured";
  const startIcon      = modality === "video" ? "🎥" : modality === "voice" ? "🎙" : "💬";

  // Survey closed — show a friendly dead-end screen
  if (survey?.status === "closed") {
    return (
      <div style={{ maxWidth:620, margin:"0 auto" }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:C.muted, fontSize:13, cursor:"pointer", fontFamily:"inherit", marginBottom:20, padding:0, display:"flex", alignItems:"center", gap:6 }}>
          ← Back to dashboard
        </button>
        <div style={{ textAlign:"center", padding:"48px 24px", background:C.surface, borderRadius:16, border:`1px solid ${C.border}` }}>
          <div style={{ fontSize:40, marginBottom:16 }}>🔒</div>
          <h2 style={{ color:C.text, fontSize:20, fontWeight:700, marginBottom:8 }}>Survey Closed</h2>
          <p style={{ color:C.muted, fontSize:13, lineHeight:1.65, maxWidth:360, margin:"0 auto 24px" }}>
            This survey has reached its target and is no longer accepting responses. Thank you for your interest.
          </p>
          <Btn variant="outline" onClick={onBack}>Back to Dashboard</Btn>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth:620, margin:"0 auto" }}>
      <button onClick={onBack} style={{ background:"none", border:"none", color:C.muted, fontSize:13, cursor:"pointer", fontFamily:"inherit", marginBottom:20, padding:0, display:"flex", alignItems:"center", gap:6 }}>
        ← Back to dashboard
      </button>
      <h2 style={{ color:C.text, fontSize:22, fontWeight:700, marginBottom:6 }}>Ready to begin?</h2>
      <p style={{ color:C.muted, fontSize:13, marginBottom:28 }}>Review the details below then start your interview when you're ready.</p>

      <Card style={{ marginBottom:20 }}>
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:16 }}>
          <AgentAvatar agent={agent} size={52} style={{ borderRadius:12 }} />
          <div>
            <div style={{ fontWeight:700, color:C.text, fontSize:16, marginBottom:4 }}>{survey?.name}</div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <Tag label={survey?.category||"Survey"} color={agent.color} />
              <span style={{ color:C.muted, fontSize:12 }}>~{survey?.estimatedMins||10} min · with {agentName}</span>
              {agent.interviewStyle && <span style={{ color:agent.color, fontSize:11, fontWeight:600, background:`${agent.color}18`, padding:"2px 8px", borderRadius:999 }}>{agent.interviewStyle}</span>}
            </div>
          </div>
        </div>
        <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:14 }}>
          {[
            ["Format",      formatLabel],
            ["Questions",   `${survey?.questions?.length||7} main questions with follow-ups`],
            ["Recording",   recordingLabel],
            ["Interviewer", `${agentName} will guide you through`],
            ...(survey?.incentiveType && survey.incentiveType !== "none" && survey?.incentiveValue
              ? [["Reward", `${survey.incentiveValue} · paid within 5–7 days via UPI`]]
              : []),
          ].map(([k,v])=>(
            <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${C.border}` }}>
              <span style={{ color:C.muted, fontSize:13 }}>{k}</span>
              <span style={{ color:C.text, fontSize:13, fontWeight:500 }}>{v}</span>
            </div>
          ))}
        </div>
      </Card>

      {modality === "video" && (
        <div style={{ background:`${C.amber}15`, border:`1px solid ${C.amber}40`, borderRadius:10, padding:"12px 16px", marginBottom:24 }}>
          <p style={{ color:C.amber, fontSize:12, margin:0, lineHeight:1.65 }}>
            ⚠️ Your browser will ask for <strong>microphone</strong> and <strong>camera</strong> access. Please click <strong>Allow</strong> on both prompts — the interview requires them.
          </p>
        </div>
      )}
      {modality === "voice" && (
        <div style={{ background:`${C.amber}15`, border:`1px solid ${C.amber}40`, borderRadius:10, padding:"12px 16px", marginBottom:24 }}>
          <p style={{ color:C.amber, fontSize:12, margin:0, lineHeight:1.65 }}>
            ⚠️ Your browser will ask for <strong>microphone</strong> access. Please click <strong>Allow</strong> — the interview requires it. No camera is needed.
          </p>
        </div>
      )}
      {modality === "chat" && (
        <div style={{ background:`${C.teal}10`, border:`1px solid ${C.teal}30`, borderRadius:10, padding:"12px 16px", marginBottom:24 }}>
          <p style={{ color:C.teal, fontSize:12, margin:0, lineHeight:1.65 }}>
            💬 This is a text-based interview. No camera or microphone required — just type your responses.
          </p>
        </div>
      )}

      {showLangPicker && (
        <div style={{ marginBottom:24 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
            <div style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", fontWeight:700 }}>
              Interview Language
            </div>
            {langLocked && <span style={{ fontSize:11, color:C.amber, background:`${C.amber}18`, border:`1px solid ${C.amber}30`, borderRadius:999, padding:"1px 8px", fontWeight:600 }}>🔒 locked</span>}
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {(supportedLanguages || []).map(lang => (
              <button key={lang}
                onClick={langLocked ? undefined : () => setSelectedLanguage(lang)}
                style={{ padding:"7px 16px", borderRadius:20, border:`1px solid ${activeLanguage===lang ? C.teal : C.border}`, background: activeLanguage===lang ? `${C.teal}18` : "transparent", color: activeLanguage===lang ? C.teal : C.muted, fontSize:13, fontWeight: activeLanguage===lang ? 600 : 400, cursor: langLocked ? "default" : "pointer", fontFamily:"inherit", transition:"all 0.15s", opacity: langLocked && activeLanguage!==lang ? 0.4 : 1 }}>
                {lang}
              </button>
            ))}
          </div>
          <p style={{ color:C.muted, fontSize:11, marginTop:8, lineHeight:1.55 }}>
            {langLocked
              ? "Language set from your previous session and cannot be changed."
              : "The interviewer will speak and listen in your chosen language."}
          </p>
        </div>
      )}

      <Btn size="lg" onClick={modality==="chat" ? ()=>onStart(showLangPicker?{language:activeLanguage}:{}) : ()=>{ unlockAudio(); setShowEnvModal(true); }} style={{ width:"100%" }}>{startIcon} Start My Interview</Btn>
      {showEnvModal && <EnvCheckModal modality={modality} survey={survey} participantId={participant?.id} interviewToken={interviewToken} onConfirm={(meta={})=>{ setShowEnvModal(false); onStart({...meta,...(showLangPicker?{language:activeLanguage}:{})}); }} />}
    </div>
  );
}

// ─── STIMULUS OVERLAY ─────────────────────────────────────────────────────────
// Full-screen overlay shown before a question when stimulusUrl is set.
// Blocks mic input until the participant clicks Continue.
function StimulusOverlay({ url, type, alt, onDismiss }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div style={{ position:"fixed", inset:0, zIndex:1000, background:"rgba(5,6,15,0.97)", backdropFilter:"blur(10px)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:24, padding:32 }}>

      {/* Header label */}
      <div style={{ color:C.teal, fontSize:11, textTransform:"uppercase", letterSpacing:"0.15em", fontWeight:700 }}>
        {loaded ? "Please review before answering" : `Loading ${type === "video" ? "video" : "image"} stimulus…`}
      </div>

      {/* Loading skeleton — visible until media is ready */}
      {!loaded && (
        <div style={{ width:"min(90vw,720px)", height:"min(62vh,420px)", borderRadius:14, border:`1px solid ${C.border}`, background:C.surface, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16 }}>
          <div style={{ width:40, height:40, border:`3px solid ${C.border}`, borderTopColor:C.teal, borderRadius:"50%", animation:"spin 0.9s linear infinite" }} />
          <p style={{ color:C.muted, fontSize:13, margin:0 }}>
            {type === "video" ? "Video is buffering — this may take a moment on slow connections." : "Image is loading…"}
          </p>
        </div>
      )}

      {/* Actual media — rendered in DOM immediately so it loads, revealed once ready */}
      <div style={{ display: loaded ? "block" : "none", animation: loaded ? "fadeIn 0.4s ease" : "none" }}>
        {type === "video"
          ? <video
              src={url}
              controls
              autoPlay
              onCanPlay={() => setLoaded(true)}
              onError={() => setLoaded(true)}   // show even on error so interview isn't stuck
              style={{ maxWidth:"90vw", maxHeight:"62vh", borderRadius:14, border:`1px solid ${C.border}`, outline:"none", display:"block" }}
            />
          : <img
              src={url}
              alt={alt || "Stimulus"}
              onLoad={() => setLoaded(true)}
              onError={() => setLoaded(true)}
              style={{ maxWidth:"90vw", maxHeight:"62vh", borderRadius:14, border:`1px solid ${C.border}`, objectFit:"contain", display:"block" }}
            />
        }
      </div>

      {/* Caption */}
      {alt && loaded && (
        <p style={{ color:C.muted, fontSize:13, textAlign:"center", maxWidth:620, margin:0, lineHeight:1.65 }}>
          {alt}
        </p>
      )}

      {/* Instructions + CTA */}
      {loaded
        ? (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
            <p style={{ color:C.text, fontSize:14, textAlign:"center", margin:0 }}>
              Take a moment to review the {type === "video" ? "video" : "image"} above, then continue when you are ready.
            </p>
            <Btn variant="primary" onClick={onDismiss} style={{ minWidth:240 }}>
              I've reviewed this — back to questions →
            </Btn>
          </div>
        ) : (
          <p style={{ color:C.muted, fontSize:13, textAlign:"center", margin:0, maxWidth:480 }}>
            The interviewer has shared a {type === "video" ? "video clip" : "image"} for you to review before answering the next question. It will appear here once it has loaded.
          </p>
        )
      }

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity:0; transform:scale(0.98); } to { opacity:1; transform:scale(1); } }
      `}</style>
    </div>
  );
}

// ─── INTERVIEW PAGE ────────────────────────────────────────────────────────────
// TTS: ElevenLabs via /api/tts  — no browser speechSynthesis
// STT: MediaRecorder + Deepgram via /api/sessions/:id/stt — no browser SpeechRecognition
// Works identically on Chrome, Firefox, Safari, Edge, Android WebView, iOS WKWebView
// Maps participant profile language display names → BCP-47 codes for TTS/STT
const LANGUAGE_BCP47 = {
  "English":   "en-US",
  "Hindi":     "hi-IN",
  "Tamil":     "ta-IN",
  "Telugu":    "te-IN",
  "Kannada":   "kn-IN",
  "Malayalam": "ml-IN",
  "Marathi":   "mr-IN",
  "Bengali":   "bn-IN",
  "Gujarati":  "gu-IN",
  "Punjabi":   "pa-IN",
};

/**
 * Resolve the BCP-47 code to use for TTS/STT during the interview.
 * Rules:
 *   1. If the platform multilingual toggle is off, always return en-US.
 *   2. If the survey has multilingualEnabled=false, always return en-US.
 *   3. Otherwise map participant's preferred language to BCP-47.
 *      If the language is unsupported (legacy value not in LANGUAGE_BCP47),
 *      fall back to en-US.
 */
function resolveInterviewLanguage(participantLang, multilingualEnabled, platformMultilingual) {
  if (!platformMultilingual || !multilingualEnabled) return "en-US";
  if (!participantLang) return "en-US";
  if (participantLang === "English") return "en-US";
  return LANGUAGE_BCP47[participantLang] || "en-US";
}

function InterviewPage({ survey, participant, onEnd, onDisqualified, resumeSessionId, multilingualSupport, apiFetch, interviewToken, languageOverride }) {
  const agentName      = resolveAgentName(survey?.agent);
  const agentProfile   = AGENT_PROFILES[agentName];
  const questions      = survey?.questions||[];
  const surveyLanguage = resolveInterviewLanguage(languageOverride || participant?.language, survey?.multilingualEnabled, multilingualSupport);
  const modality       = (survey?.modality||"video").toLowerCase();
  const isVideo        = modality === "video";
  const isVoice        = modality === "voice";
  const isChat         = modality === "chat";

  const interviewFormat = isVideo ? "video interview" : isVoice ? "voice interview" : "text-based chat interview";

  const systemPrompt=`You are ${agentName}, a professional market research interviewer conducting a ${interviewFormat}. ${agentProfile.personality||""}
${AGENT_GUARDRAIL}

Rules:
- Ask survey questions naturally and conversationally — never robotic
- After each answer, ask ONE short, relevant follow-up question that digs deeper into what they just said
- After the follow-up is answered, smoothly move to the next main question
- Keep your responses to 1–3 sentences max
- Be empathetic, curious, and human
- Never list multiple questions at once
- Always respond in English only — a translation layer will convert your responses to the participant's language

Survey questions to cover in order:
${questions.map((q,i)=>{
  const text = q.questionText || q;
  const stimNote = q.stimulusUrl ? `[A ${q.stimulusType||"image"} stimulus is shown to the participant before this question${q.stimulusAlt?` — it depicts: "${q.stimulusAlt}"`:""}.] ` : "";
  let screenerNote = "";
  if (q.isScreener && q.screenerCondition) {
    const cond = typeof q.screenerCondition === "string" ? (() => { try { return JSON.parse(q.screenerCondition); } catch { return null; } })() : q.screenerCondition;
    if (cond) {
      const criteriaNote = q.helpText ? ` Qualification criteria: "${q.helpText}".` : "";
      screenerNote = ` [SCREENER — after your follow-up probe, evaluate the SEMANTIC INTENT of the participant's combined answers to determine whether they genuinely satisfy the screener.${criteriaNote} The pass condition is: answer ${cond.operator} "${cond.value}" — but use your judgment about meaning, not just literal word matching. For example: if the screener asks whether they use a platform and they name specific platforms, that IS a "yes" even without the word "yes". If the screener expects "no" and they clearly describe using the thing, that IS a "no" answer failing the screener. Evaluate intent and context, not surface-level string matching. If the participant does NOT satisfy the screener intent, set isDisqualified: true and say a polite closing like "Thanks so much for your time — this study is looking for a slightly different profile, but we appreciate you sharing." Do NOT disqualify on the direct answer turn — only after probing.]`;
    }
  }
  return `${i+1}. ${stimNote}${text}${screenerNote}`;
}).join("\n")}

IMPORTANT: Respond ONLY as raw JSON, no markdown, no backticks:
{"text":"what you say aloud","questionIndex":0,"isFollowUp":false,"isComplete":false,"isDisqualified":false}

questionIndex = the current main question index (0-based)
isFollowUp = true if this turn is a follow-up to a main question
isComplete = true only after ALL ${questions.length} questions and their follow-ups are fully done
isDisqualified = true ONLY when: this is a follow-up turn for a screener question AND the participant's combined answers clearly reveal they do NOT genuinely satisfy the screener intent. Evaluate SEMANTICALLY — a participant who names specific platforms IS saying "yes" to using them; a participant who describes a behavior IS confirming it. Do not penalise for word choice. Never set this on the direct answer turn. When true, text must be a polite closing statement.

Start by warmly greeting the participant, briefly explaining the interview, then asking question 1. Do NOT ask for or use their name — greet them generically (e.g. "Hi there", "Welcome").`;

  const [transcript,       setTranscript]       = useState([]);
  const [agentSpeaking,    setAgentSpeaking]    = useState(false);
  const [userSpeaking,     setUserSpeaking]     = useState(false);
  const [currentQ,         setCurrentQ]         = useState(0);
  const [isListening,      setIsListening]      = useState(false);
  const [statusMsg,        setStatusMsg]        = useState("Starting up...");
  const [agentCurrentText, setAgentCurrentText] = useState(""); // eslint-disable-line no-unused-vars
  const [progress,         setProgress]         = useState(0);
  const [timer,            setTimer]            = useState(0);
  const [cameraError,      setCameraError]      = useState(false);
  const [micError,         setMicError]         = useState("");
  const [sessionStats,     setSessionStats]     = useState({exchanges:0,words:0});
  const [stimulus,         setStimulus]         = useState(null); // { url, type, alt, onDismiss }
  const [chatInput,        setChatInput]        = useState("");   // chat mode only

  const conversationRef       = useRef([]);
  const videoRef              = useRef(null);
  const streamRef             = useRef(null);      // camera + mic stream
  const chatCallbackRef       = useRef(null);      // chat mode: pending onResult callback
  const chatInputRef          = useRef(null);      // chat mode: textarea focus target
  const ttsSourceRef          = useRef(null);      // currently playing TTS AudioBufferSourceNode
  const ttsAbortRef           = useRef(null);      // AbortController for in-flight TTS fetch
  const ttsAnalyserRef        = useRef(null);      // AnalyserNode fed by currently playing TTS
  const interviewStartedRef   = useRef(false);     // guard against StrictMode double-effect
  const interviewMountedRef   = useRef(true);      // flipped to false on unmount to abort async chains
  const mediaRecorderRef      = useRef(null);
  const audioChunksRef        = useRef([]);
  const micSourceRef          = useRef(null);      // this turn's MediaStreamSource on the shared AudioContext
  const monitorRafRef         = useRef(null);
  const silenceTimerRef       = useRef(null);
  const noSpeechTimerRef      = useRef(null);
  const maxRecordingTimerRef  = useRef(null);
  const sessionIdRef          = useRef(null);
  const timerRef              = useRef(null);
  const transcriptEndRef      = useRef(null);
  const isListeningRef        = useRef(false);
  const currentQRef           = useRef(0);
  const inFollowUpRef         = useRef(false); // true when last agent reply was a follow-up probe
  const handleUserResponseRef = useRef(null);
  const handleNoResponseRef   = useRef(null);  // set after handleNoResponse is defined
  const noResponseCountRef    = useRef(0);     // consecutive no-audio attempts; reset on success
  const capturedFramesRef     = useRef([]);    // video frames captured during active speech
  const agentSpeakRef         = useRef(null);  // kept in sync with agentSpeak for use in intervals
  const sileroVadRef          = useRef(null);  // Silero VAD (MicVAD) instance — null until model loads
  const sileroReadyRef        = useRef(false); // true once Silero is usable; false → energy fallback
  const vadHandlersRef        = useRef(null);  // per-turn {onSpeechStart,onSpeechEnd} driven by Silero callbacks
  const vadDebugRef           = useRef({});    // TEMP DEBUG — live VAD state read by VadDebugBadge; remove with it

  // Audio blocked by autoplay policy / OS interruption (iOS) — shows the
  // "tap to enable audio" banner; any tap resumes via audioEngine.
  const [audioBlocked, setAudioBlocked] = useState(false);

  // ── Face-check (video only) ───────────────────────────────────────────────
  const [faceWarning,        setFaceWarning]       = useState(false); // overlay visible
  const faceCheckIntervalRef = useRef(null);   // setInterval id
  const faceMissCountRef     = useRef(0);      // consecutive checks with no face / not facing
  const faceCheckInFlightRef = useRef(false);  // prevent overlapping check requests

  // Auto-focus chat input whenever it's the user's turn to type
  useEffect(()=>{
    if(isChat && isListening && !agentSpeaking && chatInputRef.current){
      chatInputRef.current.focus();
    }
  },[isChat, isListening, agentSpeaking]);

  // Keep currentQRef in sync so startListening closure always reads latest value
  useEffect(()=>{ currentQRef.current=currentQ; },[currentQ]);
  useEffect(()=>{ timerRef.current=setInterval(()=>setTimer(t=>t+1),1000); return()=>clearInterval(timerRef.current); },[]);
  useEffect(()=>{ transcriptEndRef.current?.scrollIntoView({behavior:"smooth"}); },[transcript]);

  const fmtTime=s=>`${Math.floor(s/60)}:${String(s%60).padStart(2,"0")}`;

  // ── Real-time face visibility check (video modality only, every 8 s) ──────
  // Primary:  POST /api/vision/face-check (Google Vision).
  // Fallback: browser FaceDetector API (Chrome/Edge) → pixel-variance heuristic.
  // After 2 consecutive misses: show overlay + agent speaks a prompt (max 2×).
  useEffect(()=>{
    if(!isVideo) return;

    // Returns { frameData (base64), variance (pixel brightness variance) }
    const captureFrameForCheck=()=>{
      const video=videoRef.current;
      if(!video||!streamRef.current||video.readyState<2) return null;
      const canvas=document.createElement("canvas");
      canvas.width=320; canvas.height=240;
      const ctx=canvas.getContext("2d");
      ctx.drawImage(video,0,0,320,240);
      // Compute pixel variance on a small thumbnail for the client-side fallback
      const thumb=document.createElement("canvas");
      thumb.width=32; thumb.height=24;
      thumb.getContext("2d").drawImage(video,0,0,32,24);
      const px=thumb.getContext("2d").getImageData(0,0,32,24).data;
      let sum=0,sumSq=0,n=0;
      for(let i=0;i<px.length;i+=4){
        const g=px[i]*0.299+px[i+1]*0.587+px[i+2]*0.114;
        sum+=g; sumSq+=g*g; n++;
      }
      const mean=sum/n;
      const variance=(sumSq/n)-(mean*mean);
      return{ frameData:canvas.toDataURL("image/jpeg",0.7).split(",")[1], variance };
    };

    // Client-side fallback when Vision API is unavailable.
    // Returns { faceDetected, facingCamera } or null if no determination possible.
    const clientSideCheck=async()=>{
      const video=videoRef.current;
      if(!video) return null;
      // Try browser native FaceDetector first (Chrome/Edge)
      if("FaceDetector" in window){
        try{
          const detector=new window.FaceDetector({fastMode:true,maxDetectedFaces:1});
          const faces=await detector.detect(video);
          return{ faceDetected:faces.length>0, facingCamera:faces.length>0 };
        }catch{ /* not supported on this frame */ }
      }
      return null; // variance fallback handled by caller
    };

    const applyResult=(faceDetected,facingCamera)=>{
      if(!faceDetected||!facingCamera){
        faceMissCountRef.current+=1;
        if(faceMissCountRef.current>=2){
          setFaceWarning(true);
        }
      } else {
        faceMissCountRef.current=0;
        setFaceWarning(false);
      }
    };

    faceCheckIntervalRef.current=setInterval(async()=>{
      if(!interviewMountedRef.current) return;
      if(faceCheckInFlightRef.current) return;
      const captured=captureFrameForCheck();
      if(!captured) return;
      const{frameData,variance}=captured;

      faceCheckInFlightRef.current=true;
      try{
        const res=await apiFetch(`${API_BASE}/api/vision/face-check`,{
          method:"POST", headers:{"Content-Type":"application/json"},
          body:JSON.stringify({frameData}),
        });
        if(!res.ok) throw new Error("non-ok");
        const data=await res.json();

        if(!data.skipped){
          // Vision API gave us a real answer
          applyResult(data.faceDetected, data.facingCamera);
          return;
        }
        // Vision API unavailable — fall through to client-side checks
        const nativeResult=await clientSideCheck();
        if(nativeResult){
          applyResult(nativeResult.faceDetected, nativeResult.facingCamera);
          return;
        }
        // Last resort: pixel-variance heuristic.
        // A very uniform frame (variance < 300) typically means the camera is
        // pointed at a wall/ceiling, covered, or in complete darkness.
        const hasContent=variance>300;
        applyResult(hasContent, hasContent);
      }catch{
        // Network/parse error — try client-side before giving up
        try{
          const nativeResult=await clientSideCheck();
          if(nativeResult){ applyResult(nativeResult.faceDetected, nativeResult.facingCamera); return; }
          const hasContent=variance>300;
          applyResult(hasContent, hasContent);
        }catch{ /* silent — don't disrupt interview */ }
      }finally{
        faceCheckInFlightRef.current=false;
      }
    }, 8000); // 8 s interval

    return()=>{ clearInterval(faceCheckIntervalRef.current); };
  // eslint-disable-next-line
  },[isVideo]);

  // ── Media access — scoped to modality ────────────────────────────────────
  // video  → camera + mic
  // voice  → mic only (never requests camera)
  // chat   → no media at all
  // Audio constraints: browser-level echo cancellation (stops the agent's TTS
  // bleeding into the mic), noise suppression, and auto gain — critical for
  // reliable speech detection in noisy environments.
  useEffect(()=>{
    if(isChat) return; // no media needed for chat
    const AUDIO_CONSTRAINTS={ echoCancellation:true, noiseSuppression:true, autoGainControl:true };
    (async()=>{
      try{
        const constraints = isVideo ? {video:true,audio:AUDIO_CONSTRAINTS} : {audio:AUDIO_CONSTRAINTS};
        const stream=await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current=stream;
        if(isVideo && videoRef.current){ videoRef.current.srcObject=stream; videoRef.current.muted=true; }
      }catch{
        if(isVideo) setCameraError(true);
        // For video: camera blocked — fall back to audio-only. For voice: the getUserMedia already failed.
        try{ const s=await navigator.mediaDevices.getUserMedia({audio:AUDIO_CONSTRAINTS}); streamRef.current=s; }
        catch{ setMicError("Microphone access denied. Allow it in browser settings and reload."); }
      }

      // ── Silero VAD init (neural voice detection) ─────────────────────────
      // Loads in the background while the agent greets the participant. Until
      // ready — or on any failure (old browser, asset load error, WASM blocked)
      // — startListening transparently uses the adaptive energy fallback, so
      // no device is ever blocked from interviewing.
      if(!streamRef.current) return;
      try{
        const { MicVAD } = await import("@ricky0123/vad-web");
        if(!interviewMountedRef.current || !streamRef.current) return;
        const vad=await MicVAD.new({
          model: "v5",
          baseAssetPath: "/vad/",        // self-hosted (copy-vad-assets.js) — no CDN
          onnxWASMBasePath: "/vad/",
          startOnLoad: false,
          // Share the existing mic stream; never let the VAD stop our tracks
          // (its defaults call getUserMedia/track.stop() — that would kill the
          // MediaRecorder pipeline).
          getStream:    async()=>new MediaStream(streamRef.current.getAudioTracks()),
          pauseStream:  async()=>{},
          resumeStream: async()=>new MediaStream(streamRef.current.getAudioTracks()),
          positiveSpeechThreshold: 0.5,  // prob to enter "speaking"
          negativeSpeechThreshold: 0.35, // prob to leave "speaking" (hysteresis)
          redemptionMs: 400,             // brief dips (breaths, inter-word gaps) don't end speech
          minSpeechMs: 150,              // clicks/bumps shorter than this are misfires
          preSpeechPadMs: 0,             // segment audio unused — MediaRecorder owns the recording
          submitUserSpeechOnPause: false,
          onSpeechStart: ()=>vadHandlersRef.current?.onSpeechStart?.(),
          onSpeechEnd:   ()=>vadHandlersRef.current?.onSpeechEnd?.(),
          onVADMisfire:  ()=>vadHandlersRef.current?.onSpeechEnd?.(),
        });
        if(!interviewMountedRef.current){ vad.destroy().catch(()=>{}); return; }
        sileroVadRef.current=vad;
        sileroReadyRef.current=true;
        console.log("[VAD] Silero v5 ready — neural speech detection active");
      }catch(e){
        console.warn("[VAD] Silero unavailable — using adaptive energy fallback:",e?.message);
      }
    })();
    return()=>{
      sileroReadyRef.current=false;
      vadHandlersRef.current=null;
      sileroVadRef.current?.destroy().catch?.(()=>{});
      sileroVadRef.current=null;
      streamRef.current?.getTracks().forEach(t=>t.stop());
    };
  // eslint-disable-next-line
  },[]);

  // ── Display translation: English agent text → participant's native language ──
  // Called in parallel with agentSpeak so TTS audio is never delayed.
  // Returns original English text unchanged for English surveys or on any failure.
  const translateForDisplay=useCallback(async(text)=>{
    if(!text?.trim()) return text;
    if(!surveyLanguage||surveyLanguage.startsWith("en")) return text;
    try{
      const r=await apiFetch(`${API_BASE}/api/translate/from-english`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({text,language:surveyLanguage}),
      });
      if(!r.ok) return text;
      const d=await r.json();
      return d.translated||text;
    }catch{ return text; }
  },[surveyLanguage]);

  // ── TTS: sentence-level pipelining — fetches next sentence while current plays ─
  // This eliminates the long silence between LLM response and first audio playback.
  // Chat mode: skips TTS entirely — agent text is shown only, onDone fires immediately.
  const agentSpeak=useCallback((text,onDone)=>{
    if(isChat){ setAgentCurrentText(text); if(onDone) setTimeout(onDone,0); return; }
    if(ttsAbortRef.current){ ttsAbortRef.current.abort(); ttsAbortRef.current=null; }
    if(ttsSourceRef.current){ try{ ttsSourceRef.current.stop(); }catch{} ttsSourceRef.current=null; }
    setAgentSpeaking(true); setAgentCurrentText(text);

    const controller=new AbortController();
    ttsAbortRef.current=controller;
    const {signal}=controller;

    // Split into sentences; keep very short texts as one chunk to avoid overhead.
    // The splitter (further below) matches on ".", "!", "?" as terminators, and
    // String.match(/g) silently DROPS any trailing text that never reaches one —
    // not "included without punctuation", just absent from sentences[] entirely.
    // Claude's reply ending without terminal punctuation, or a stream cut short
    // by max_tokens/network before its last sentence closes, both violate that
    // assumption. Rather than teach the splitter regex to tolerate every way
    // the assumption can be violated, guarantee it up front: every string handed
    // to the splitter is normalized to end with real terminal punctuation.
    const trimmedText=text.trimEnd();
    const normalizedText=/[.!?]$/.test(trimmedText)?trimmedText:trimmedText+".";

    // Decimal points (e.g. "4.5") must NOT be treated as sentence terminators —
    // the splitter below would otherwise tear "4.5" into "...4." and "5...",
    // which TTS then reads as two separate utterances ("Four. ... Five.")
    // instead of "four point five". Covers Latin digits plus the Indic numeral
    // blocks for every supported language (Devanagari, Bengali, Gujarati,
    // Gurmukhi, Telugu, Kannada, Malayalam, Tamil) since surveys can be in any
    // of them. Decimal points are swapped for a placeholder before splitting,
    // then restored in each resulting chunk.
    const DIGIT_CLASS="0-9०-९০-৯૦-૯੦-੯౦-౯೦-೯൦-൯௦-௯";
    const DECIMAL_RE=new RegExp(`([${DIGIT_CLASS}])\\.([${DIGIT_CLASS}])`,"g");
    const DECIMAL_PLACEHOLDER=" DECPT ";
    const protectedText=normalizedText.replace(DECIMAL_RE,`$1${DECIMAL_PLACEHOLDER}$2`);
    const restoreDecimals=(s)=>s.split(DECIMAL_PLACEHOLDER).join(".");
    const sentences=(protectedText.length<120
      ?[protectedText]
      :(protectedText.match(/[^.!?]+[.!?]+\s*/g)||[protectedText])
    ).map(s=>restoreDecimals(s.trim())).filter(Boolean);

    const fetchSentence=(s)=>apiFetch(`${API_BASE}/api/tts`,{
      method:"POST", headers:{"Content-Type":"application/json"},
      body:JSON.stringify({text:s,voice:agentName.toLowerCase(),language:surveyLanguage}),
      signal,
    }).then(r=>r.blob());

    // Returns a Promise that resolves when the blob finishes playing (or is skipped).
    // Playback goes through the shared AudioContext (decodeAudioData →
    // AudioBufferSourceNode → analyser → destination) instead of an
    // HTMLAudioElement. On iOS WebKit (Safari AND Chrome/Edge — all WebKit),
    // element.play() deep in an async chain is rejected by autoplay policy, and
    // createMediaElementSource routes audio into a context the OS silently
    // suspends ("interrupted") whenever the mic session activates. Buffer
    // sources on the gesture-unlocked shared context are exempt from both.
    const playBlob=(blob)=>new Promise(resolve=>{
      if(signal.aborted||!blob?.size){ resolve(); return; }
      // Last-resort bare-element playback (no analyser hookup — SoundWave uses
      // its synthetic fallback): used when Web Audio is unavailable or the blob
      // can't be decoded. A bare element is reliable where MediaElementSource isn't.
      const playViaElement=()=>new Promise(res=>{
        const url=URL.createObjectURL(blob);
        const audio=new Audio(url);
        let settled=false;
        const done=()=>{ if(settled)return; settled=true; URL.revokeObjectURL(url); res(); };
        audio.onended=done; audio.onerror=done;
        signal.addEventListener("abort",()=>{ try{audio.pause();}catch{} done(); },{once:true});
        audio.play().catch(done);
      });
      (async()=>{
        const ctx=getAudioCtx();
        if(!ctx){ await playViaElement(); return; }
        // Covers both "suspended" and iOS's "interrupted" (phone call, Siri,
        // app switch). If resume needs a gesture, show the banner and park
        // until any tap unlocks — the sentence then plays instead of being lost.
        if(!(await ensureAudioRunning())){
          setAudioBlocked(true);
          await waitForAudioUnlock(signal);
          setAudioBlocked(false);
          if(signal.aborted) return;
        }
        let buffer=null;
        try{ buffer=await ctx.decodeAudioData(await blob.arrayBuffer()); }catch{}
        if(signal.aborted) return;
        if(!buffer){ await playViaElement(); return; }
        await new Promise(res=>{
          const source=ctx.createBufferSource();
          source.buffer=buffer;
          const analyser=ctx.createAnalyser();
          analyser.fftSize=256;
          analyser.smoothingTimeConstant=0.15;
          source.connect(analyser);
          analyser.connect(ctx.destination);
          ttsAnalyserRef.current=analyser;
          ttsSourceRef.current=source;
          let settled=false;
          const done=()=>{
            if(settled)return; settled=true;
            ttsAnalyserRef.current=null; ttsSourceRef.current=null;
            try{ source.disconnect(); analyser.disconnect(); }catch{}
            res();
          };
          source.onended=done;
          signal.addEventListener("abort",()=>{ try{source.stop();}catch{} done(); },{once:true});
          source.start();
        });
      })().catch(()=>{}).then(resolve);
    });

    // Browser speechSynthesis fallback — used when server TTS returns an empty blob
    // (i.e. ELEVENLABS_API_KEY / GOOGLE_API_KEY not configured in api/.env)
    const browserSpeak=(s)=>new Promise(resolve=>{
      if(signal.aborted||!('speechSynthesis' in window)){ resolve(); return; }
      window.speechSynthesis.cancel();
      const utt=new SpeechSynthesisUtterance(s);
      utt.onend=resolve; utt.onerror=resolve;
      signal.addEventListener("abort",()=>{ window.speechSynthesis.cancel(); resolve(); },{once:true});
      window.speechSynthesis.speak(utt);
    });

    (async()=>{
      try{
        // 2-deep pre-fetch pipeline: kick off sentences[0] AND sentences[1] immediately
        // so that while sentence N plays, both N+1 and N+2 are already being fetched.
        // This eliminates audio gaps from ElevenLabs latency spikes — even if one fetch
        // is slow, the next sentence's blob is already in hand or well underway.
        // All fetches carry the shared AbortSignal so they cancel instantly on interrupt.
        const fetchQueue=[];
        fetchQueue[0]=fetchSentence(sentences[0]);
        fetchQueue[0].catch(()=>{}); // suppress unhandled rejection if aborted before awaited
        if(sentences.length>1){ fetchQueue[1]=fetchSentence(sentences[1]); fetchQueue[1].catch(()=>{}); }

        for(let i=0;i<sentences.length;i++){
          // Each fetch is wrapped individually so a failure on one sentence (EL quota,
          // timeout, empty blob) falls back to browser TTS without aborting the rest.
          let blob;
          try{ blob=await fetchQueue[i]; }
          catch(fetchErr){
            if(fetchErr.name==="AbortError"||signal.aborted) return;
            blob=null; // force browserSpeak fallback for this sentence
          }
          // While current sentence plays, queue the fetch 2 ahead (N+2).
          // N+1 is already in-flight from the previous iteration (or upfront kick).
          // Bounds-checked — never queues past the end of the sentences array.
          if(i+2<sentences.length){ fetchQueue[i+2]=fetchSentence(sentences[i+2]); fetchQueue[i+2].catch(()=>{}); }
          if(!blob?.size) await browserSpeak(sentences[i]);
          else await playBlob(blob);
          if(signal.aborted) return;
        }
      }catch(err){
        if(err.name==="AbortError") return;
        // Unexpected outer error — try browser TTS for the full text as last resort
        await browserSpeak(text);
      }
      if(!signal.aborted){ ttsAbortRef.current=null; setAgentSpeaking(false); onDone?.(); }
    })();
  },[agentName,surveyLanguage,isChat]);

  // Keep agentSpeakRef in sync so the face-check interval can call agentSpeak
  useEffect(()=>{ agentSpeakRef.current=agentSpeak; },[agentSpeak]);

  // ── STT: MediaRecorder + Web Audio silence detection ─────────────────────
  // Long answers (>60 s) are handled by automatically sealing and submitting a
  // new STT segment every 50 s while recording continues uninterrupted.
  // All segment transcripts are concatenated in order at the end.
  const startListening=useCallback((onResult)=>{
    if(isChat){
      chatCallbackRef.current=onResult;
      setIsListening(true);
      isListeningRef.current=true;
      setStatusMsg("Type your response below…");
      return;
    }
    const stream=streamRef.current;
    if(!stream){ setMicError("Microphone not available."); return; }
    const audioTracks=stream.getAudioTracks();
    if(!audioTracks.length){ setMicError("No microphone detected."); return; }

    const audioStream=new MediaStream(audioTracks);
    const mimeType=
      MediaRecorder.isTypeSupported("audio/webm;codecs=opus")?"audio/webm;codecs=opus": // Chrome, Edge, Android, Firefox 62+
      MediaRecorder.isTypeSupported("audio/webm")            ?"audio/webm":              // Chrome/Edge fallback
      MediaRecorder.isTypeSupported("audio/ogg;codecs=opus") ?"audio/ogg;codecs=opus":  // Firefox < 62
      "audio/mp4";                                                                        // Safari (all platforms)

    let recorder;
    try{ recorder=new MediaRecorder(audioStream,{mimeType}); }
    catch{ recorder=new MediaRecorder(audioStream); }

    mediaRecorderRef.current=recorder;
    audioChunksRef.current=[];
    isListeningRef.current=true;

    // Shared AudioContext (audioEngine) — one context for the whole interview.
    // Creating a fresh context per turn (and closing it on stop) forced iOS to
    // renegotiate the hardware audio session every turn, which pushed the TTS
    // playback context into the WebKit-only "interrupted" state → silent agent.
    // Per turn we only attach/detach a MediaStreamSource + analyser.
    const audioCtx=getAudioCtx();
    if(audioCtx.state!=="running") audioCtx.resume().catch(()=>{});
    const analyser=audioCtx.createAnalyser();
    analyser.fftSize=1024;
    const micSource=audioCtx.createMediaStreamSource(audioStream);
    micSource.connect(analyser);
    micSourceRef.current=micSource;
    const dataArray=new Uint8Array(analyser.frequencyBinCount);
    let speechDetected=false;
    clearTimeout(silenceTimerRef.current);

    // ── Turn detection — two engines, one state machine ──────────────────────
    // Engine A (preferred): Silero VAD v5 — neural voice detection, robust to
    //   fans/traffic/wind, initialised in the media effect. Drives the state
    //   machine via vadHandlersRef callbacks.
    // Engine B (fallback): adaptive energy detection with hysteresis + hangover.
    //   Used automatically when Silero isn't ready or failed to load.
    // The analyser loop below always runs for audio-quality metrics; it only
    // makes speech decisions when Engine B is active.
    const SILENCE_MS      = 3000;  // submit this long after speech ends
    const CALIB_FRAMES    = 25;    // ~0.4 s at 60 fps — used to measure ambient
    let ambientLevel      = 0;
    let calibFrameCount   = 0;
    let wasSpeaking       = false;
    let speechRunFrames   = 0;     // consecutive frames above enter-threshold (Engine B)
    let silenceRunFrames  = 0;     // consecutive frames below exit-threshold (Engine B)
    let countdownInterval = null;  // local — tracks the "submitting in Ns" display
    // Audio quality metrics for server-side quality check
    let peakSpeechRms     = 0;     // max speech-band RMS during active speech (0–255 byte range)
    let clippingFlag      = false; // true if any sample exceeded digital clipping threshold

    // Compute RMS only over the human speech band (300–3 400 Hz).
    // This filters out low-frequency rumble and high-frequency hiss that
    // share energy with background noise but not with voice.
    const binHz = (audioCtx.sampleRate / 2) / analyser.frequencyBinCount;
    const loBin = Math.max(0, Math.floor(300  / binHz));
    const hiBin = Math.min(analyser.frequencyBinCount - 1, Math.floor(3400 / binHz));

    const clearCountdown=()=>{
      if(countdownInterval){ clearInterval(countdownInterval); countdownInterval=null; }
    };

    // ── Shared state machine — both engines call these two handlers ─────────
    const onSpeechStartEvt=()=>{
      if(!isListeningRef.current) return;
      speechDetected=true;
      clearTimeout(silenceTimerRef.current); // user resumed — cancel pending submit
      clearCountdown();
      setStatusMsg("Recording your response…");
      captureFrame(); // capture frame at start of active speech
    };
    const onSpeechEndEvt=()=>{
      if(!isListeningRef.current||!speechDetected) return;
      // Arm the submit timer and show a countdown so the user can see the
      // response will be submitted soon — and keep talking to cancel it.
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current=setTimeout(()=>{
        if(isListeningRef.current&&recorder.state==="recording") recorder.stop();
      },SILENCE_MS);

      let remaining=Math.ceil(SILENCE_MS/1000);
      setStatusMsg(`Done? Submitting in ${remaining}s — keep talking to continue…`);
      clearCountdown();
      countdownInterval=setInterval(()=>{
        remaining-=1;
        if(remaining>0) setStatusMsg(`Done? Submitting in ${remaining}s — keep talking to continue…`);
        else clearCountdown();
      },1000);
    };

    // ── Engine A: Silero — route its callbacks into the state machine ───────
    const useSilero=sileroReadyRef.current&&sileroVadRef.current;
    if(useSilero){
      vadHandlersRef.current={ onSpeechStart:onSpeechStartEvt, onSpeechEnd:onSpeechEndEvt };
      sileroVadRef.current.start().catch(e=>{
        console.warn("[VAD] Silero start failed — energy fallback takes over:",e?.message);
        sileroReadyRef.current=false; // analyser loop below picks up decisions next frame
      });
    }

    const monitorAudio=()=>{
      if(!isListeningRef.current) return;
      analyser.getByteFrequencyData(dataArray);

      // Speech-band RMS
      let sum=0;
      for(let i=loBin;i<=hiBin;i++) sum+=dataArray[i]*dataArray[i];
      const rms=Math.sqrt(sum/(hiBin-loBin+1));

      // Phase 1 — calibrate ambient noise floor (don't evaluate speech yet)
      if(calibFrameCount<CALIB_FRAMES){
        calibFrameCount++;
        ambientLevel+=(rms-ambientLevel)*0.3; // fast EMA convergence
        // TEMP DEBUG (iPad/non-English VAD investigation) — surfaced by VadDebugBadge; remove with it
        vadDebugRef.current={
          t:Date.now(), engine:sileroReadyRef.current?"silero":"energy",
          rms, floor:ambientLevel, ratio:ambientLevel>0?rms/ambientLevel:0,
          enterRatio:3.0, calibDone:false, speaking:wasSpeaking, detected:speechDetected,
        };
        monitorRafRef.current=requestAnimationFrame(monitorAudio);
        return;
      }

      // Quality metrics (both engines): peak speech RMS + clipping
      const enterThreshold=Math.max(ambientLevel*3.0,10);
      if(rms>enterThreshold){
        if(rms>peakSpeechRms) peakSpeechRms=rms;
        if(rms>240) clippingFlag=true; // byte value 240+/255 = near-full-scale clipping
      }

      // TEMP DEBUG (iPad/non-English VAD investigation) — surfaced by VadDebugBadge; remove with it
      vadDebugRef.current={
        t:Date.now(), engine:sileroReadyRef.current?"silero":"energy",
        rms, floor:ambientLevel, ratio:ambientLevel>0?rms/ambientLevel:0,
        enterRatio:3.0, calibDone:true, speaking:wasSpeaking, detected:speechDetected,
      };

      // Engine A active → metrics only; Silero owns the speech decisions
      if(sileroReadyRef.current){
        monitorRafRef.current=requestAnimationFrame(monitorAudio);
        return;
      }

      // ── Engine B: adaptive energy VAD with hysteresis + hangover ──────────
      // Enter "speaking" above 3.0× ambient; leave below 1.8× ambient. The gap
      // (hysteresis) stops flicker around a single threshold. Hangover frames
      // require sustained evidence before switching state, so inter-word gaps
      // and breaths don't end the turn, and clicks don't start one.
      const exitThreshold=Math.max(ambientLevel*1.8,6);
      if(!wasSpeaking){
        // Continuously adapt the noise floor during silence (slow EMA) so a
        // fan/traffic that starts mid-interview raises the threshold instead
        // of being mistaken for endless speech.
        if(rms<enterThreshold) ambientLevel+=(rms-ambientLevel)*0.02;
        speechRunFrames = rms>enterThreshold ? speechRunFrames+1 : 0;
        if(speechRunFrames>=3){            // ~50 ms of sustained voice energy
          wasSpeaking=true; speechRunFrames=0; silenceRunFrames=0;
          onSpeechStartEvt();
        }
      }else{
        silenceRunFrames = rms<exitThreshold ? silenceRunFrames+1 : 0;
        if(silenceRunFrames>=10){          // ~170 ms of sustained quiet
          wasSpeaking=false; silenceRunFrames=0; speechRunFrames=0;
          onSpeechEndEvt();
        }
      }

      monitorRafRef.current=requestAnimationFrame(monitorAudio);
    };
    // ── Frame capture — grabs a JPEG from the video stream ───────────────────
    // Only runs for video modality. Called at speech start and speech end so
    // every captured frame shows the participant actively answering.
    const captureFrame=()=>{
      if(!isVideo||!videoRef.current||!streamRef.current) return;
      const video=videoRef.current;
      if(video.readyState<2||video.videoWidth===0) return;
      const canvas=document.createElement("canvas");
      canvas.width=320; canvas.height=240;
      canvas.getContext("2d").drawImage(video,0,0,320,240);
      const frameData=canvas.toDataURL("image/jpeg",0.7).split(",")[1];
      capturedFramesRef.current.push({ questionIndex:currentQRef.current, frameData, timestamp:Date.now() });
    };

    // Segment helpers — submit a chunk array as one STT call; returns Promise<string>
    const ext=mimeType.includes("mp4")?"mp4":mimeType.includes("ogg")?"ogg":"webm";
    const sid=sessionIdRef.current||"unknown";
    // One id per speaking turn — long answers split into multiple 50s segments share
    // this id so the server accumulates them into a single answer row.
    const turnId=(window.crypto?.randomUUID?.()||`${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const submitSegment=(chunks)=>{
      if(!chunks.length) return Promise.resolve({english:"",native:"",accepted:false});
      const fd=new FormData();
      fd.append("audio",new Blob(chunks,{type:mimeType}),`r.${ext}`);
      fd.append("questionIndex",String(currentQRef.current));
      fd.append("turnId",turnId);
      fd.append("language",surveyLanguage);
      // Audio quality metrics for server-side quality check (computed by VAD above)
      fd.append("vadAmbientLevel",String(ambientLevel));
      fd.append("vadPeakSpeechRms",String(peakSpeechRms));
      fd.append("vadClippingFlag",String(clippingFlag));
      return apiFetch(`${API_BASE}/api/sessions/${sid}/stt`,{method:"POST",body:fd})
        .then(r=>r.ok?r.json():Promise.reject(new Error(`STT ${r.status}`)))
        .then(d=>({english:d.transcript?.trim()||"",native:(d.nativeTranscript||d.transcript)?.trim()||"",accepted:!!d.accepted}))
        .catch(err=>{ console.error("[STT segment]",err.message); return {english:"",native:"",accepted:false}; });
    };

    // Every 50 s, seal the accumulated chunks and submit as a segment.
    // Recording continues without interruption — no more hard cutoff.
    const segmentPromises=[];
    const segmentTimer=setInterval(()=>{
      if(!isListeningRef.current) return;
      const chunks=audioChunksRef.current.splice(0); // drain current buffer
      if(chunks.length) segmentPromises.push(submitSegment(chunks));
    },50000);

    recorder.ondataavailable=e=>{ if(e.data.size>0) audioChunksRef.current.push(e.data); };

    // Browser SpeechRecognition — runs in parallel with MediaRecorder+server-STT.
    // Used as fallback when GOOGLE_API_KEY is not configured in api/.env.
    let browserTranscript="";
    const SpeechRec=window.SpeechRecognition||window.webkitSpeechRecognition;
    const recognition=SpeechRec ? (()=>{
      const r=new SpeechRec();
      r.lang=surveyLanguage; r.continuous=true; r.interimResults=false;
      r.onresult=e=>{ for(let i=e.resultIndex;i<e.results.length;i++){ if(e.results[i].isFinal) browserTranscript+=e.results[i][0].transcript+" "; } };
      r.onerror=()=>{}; // ignore — server STT takes priority
      return r;
    })() : null;

    recorder.onstop=async()=>{
      captureFrame(); // capture frame at end of active speech
      clearInterval(segmentTimer);
      clearCountdown(); // stop the "Submitting in Ns" display if still running
      vadHandlersRef.current=null;                       // detach Silero from this turn
      sileroVadRef.current?.pause().catch?.(()=>{});     // stop neural detection between turns
      isListeningRef.current=false;
      setIsListening(false); setUserSpeaking(false);
      clearTimeout(silenceTimerRef.current);
      clearTimeout(noSpeechTimerRef.current);
      clearTimeout(maxRecordingTimerRef.current);
      cancelAnimationFrame(monitorRafRef.current);
      // Detach this turn's nodes — the shared context stays alive (closing it
      // per turn is what broke TTS on iOS).
      try{ micSource.disconnect(); }catch{}
      if(micSourceRef.current===micSource) micSourceRef.current=null;
      if(recognition){ try{ recognition.stop(); }catch{} }

      const remainingChunks=audioChunksRef.current.splice(0);
      if(!speechDetected&&!segmentPromises.length&&!remainingChunks.length){
        const bt=browserTranscript.trim();
        if(bt) onResult({english:bt,native:bt});
        else handleNoResponseRef.current?.();
        return;
      }

      setStatusMsg("Processing your response…");
      // Submit any remaining audio after the last segment interval
      if(remainingChunks.length) segmentPromises.push(submitSegment(remainingChunks));

      try{
        const parts=await Promise.all(segmentPromises);
        const english=parts.map(p=>p.english).join(" ").trim();
        const native=parts.map(p=>p.native).join(" ").trim();
        // Advance only if the server accepted at least one segment as a real answer
        // (substance gate). A low-confidence/empty turn is treated as no-response so
        // the interview never progresses past a question the participant didn't answer.
        const anyAccepted=parts.some(p=>p.accepted);
        if(anyAccepted) onResult({english,native});
        else if(browserTranscript.trim()){ const bt=browserTranscript.trim(); onResult({english:bt,native:bt}); } // fallback — browser ASR captured speech the server gate couldn't score
        else handleNoResponseRef.current?.();
      }catch(err){
        console.error("[STT]",err.message);
        const bt=browserTranscript.trim();
        if(bt) onResult({english:bt,native:bt});
        else handleNoResponseRef.current?.();
      }
    };

    recorder.start(250);
    if(recognition){ try{ recognition.start(); }catch{} }
    setIsListening(true); setUserSpeaking(true); setMicError(""); setStatusMsg("Listening — speak now…");
    requestAnimationFrame(monitorAudio);

    // ── Safety timeouts — prevent recording from running indefinitely ─────────
    // No-speech timeout: if the user never speaks within 20 s, stop and treat
    // it the same as a no-response (VAD never armed the silence timer in this case).
    clearTimeout(noSpeechTimerRef.current);
    noSpeechTimerRef.current=setTimeout(()=>{
      if(isListeningRef.current&&!speechDetected&&recorder.state==="recording"){
        setStatusMsg("No speech detected — stopping…");
        recorder.stop();
      }
    },20000);

    // Max-recording timeout: hard cap at 90 s regardless of VAD state.
    // Handles the case where background noise keeps the VAD in "speaking" state.
    clearTimeout(maxRecordingTimerRef.current);
    maxRecordingTimerRef.current=setTimeout(()=>{
      if(isListeningRef.current&&recorder.state==="recording"){
        setStatusMsg("Maximum recording time reached — processing…");
        recorder.stop();
      }
    },90000);
  // eslint-disable-next-line
  },[surveyLanguage]); // surveyLanguage is a stable constant during an interview

  const stopListening=()=>{
    if(isChat){ chatCallbackRef.current=null; setIsListening(false); isListeningRef.current=false; return; }
    vadHandlersRef.current=null;
    sileroVadRef.current?.pause().catch?.(()=>{});
    isListeningRef.current=false;
    clearTimeout(silenceTimerRef.current);
    clearTimeout(noSpeechTimerRef.current);
    clearTimeout(maxRecordingTimerRef.current);
    cancelAnimationFrame(monitorRafRef.current);
    if(mediaRecorderRef.current?.state==="recording") mediaRecorderRef.current.stop();
    try{ micSourceRef.current?.disconnect(); }catch{}
    micSourceRef.current=null;
  };

  // ── Full teardown — stop TTS, STT, and media stream ─────────────────────
  // Call this before navigating away (End button, isComplete, disqualified).
  const stopAll=()=>{
    // Capture the source BEFORE abort() — abort fires synchronously and the done()
    // listener in playBlob nulls ttsSourceRef.current, so we'd miss it otherwise.
    const ttsSource=ttsSourceRef.current;
    ttsSourceRef.current=null;
    if(ttsAbortRef.current){ ttsAbortRef.current.abort(); ttsAbortRef.current=null; }
    if(ttsSource){ try{ ttsSource.stop(); }catch{} }
    window.speechSynthesis?.cancel(); // belt-and-suspenders for browserSpeak fallback
    setAgentSpeaking(false);
    // Stop STT / recording
    stopListening();
    // Stop face-check interval
    clearInterval(faceCheckIntervalRef.current);
    // Stop camera / mic tracks
    streamRef.current?.getTracks().forEach(t=>t.stop());
  };

  // ── Chat submit — called when user presses Send or Enter in chat mode ─────
  const handleChatSubmit=()=>{
    const text=chatInput.trim();
    if(!text||!chatCallbackRef.current) return;
    const cb=chatCallbackRef.current;
    chatCallbackRef.current=null;
    setChatInput("");
    setIsListening(false);
    isListeningRef.current=false;
    cb(text);
  };

  // Streams Claude's response from /chat/stream (SSE).
  // onTextReady(text) fires as soon as the JSON "text" field is fully parsed from the stream,
  // allowing TTS to start before the rest of the JSON (questionIndex, isComplete…) arrives.
  const askClaude=async(userMsg,onTextReady,{skipPersist=false}={})=>{
    const messages=[...conversationRef.current,{role:"user",content:userMsg}];
    conversationRef.current=messages;
    setStatusMsg("Thinking…");
    const sid=sessionIdRef.current||"unknown";
    try{
      const res=await apiFetch(`${API_BASE}/api/sessions/${sid}/chat/stream`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({messages,systemPrompt,questionIndex:skipPersist?null:currentQRef.current}),
      });
      if(!res.ok){ const t=await res.text(); setStatusMsg(`API error ${res.status}: ${t.slice(0,80)}`); return null; }

      const reader=res.body.getReader();
      const decoder=new TextDecoder();
      let fullText="";
      let earlyFired=false;

      while(true){
        const {done,value}=await reader.read();
        if(done) break;
        const chunk=decoder.decode(value,{stream:true});
        for(const line of chunk.split("\n")){
          if(!line.startsWith("data: ")) continue;
          const data=line.slice(6).trim();
          if(!data||data==="[DONE]") continue;
          try{
            const evt=JSON.parse(data);
            if(evt.type==="content_block_delta"&&evt.delta?.type==="text_delta"){
              fullText+=evt.delta.text;
              // Fire onTextReady the moment the JSON "text" field is complete in the stream
              if(!earlyFired&&onTextReady){
                const m=fullText.match(/"text"\s*:\s*"((?:[^"\\]|\\.)*)"/);
                if(m){
                  earlyFired=true;
                  onTextReady(m[1].replace(/\\"/g,'"').replace(/\\n/g,"\n").replace(/\\t/g,"\t"));
                }
              }
            }
          }catch{ /* partial SSE line — accumulate and retry next chunk */ }
        }
      }

      conversationRef.current=[...conversationRef.current,{role:"assistant",content:fullText}];
      let parsed;
      try{ const m=fullText.match(/\{[\s\S]*\}/); parsed=JSON.parse(m?m[0]:fullText); }
      catch{
        // JSON parse failed — extract text field via regex rather than stripping all JSON syntax,
        // which would leave raw key names (text:...,questionIndex:...) in the output.
        // The closing quote is OPTIONAL: a stream cut short (max_tokens, network hiccup) ends
        // mid-string with no closing quote yet, and requiring one made the whole match fail,
        // falling through to raw fullText — the agent showed/spoke the literal
        // {"text":"..." wrapper. An unterminated match still captures everything written
        // so far, which is the best available text for a truncated reply.
        const tm=fullText.match(/"text"\s*:\s*"((?:[^"\\]|\\.)*)"?/);
        const extractedText=tm?tm[1].replace(/\\"/g,'"').replace(/\\n/g,'\n').replace(/\\t/g,'\t'):fullText.replace(/[{}"[\]]/g,"");
        parsed={text:extractedText,questionIndex:currentQRef.current,isFollowUp:false,isComplete:false};
      }
      return parsed;
    }catch{ setStatusMsg("Network error — is the server running?"); return null; }
  };

  const handleUserResponse=useCallback(async(payload)=>{
    // payload is either a plain string (chat mode) or {english, native} (STT voice/video mode).
    // english → sent to Claude and stored in DB. native → shown in transcript UI.
    const english = typeof payload==="string" ? payload : payload.english;
    const native  = typeof payload==="string" ? payload : (payload.native||payload.english);

    // Screener evaluation is handled entirely by Claude (semantically, after the follow-up probe).
    // We do NOT do a literal string match here because participants often answer in a way that
    // clearly implies "yes" without using the word — e.g. naming a platform when asked if they
    // use one. Claude evaluates the combined direct + probe answers and sets isDisqualified:true
    // if the participant genuinely does not satisfy the screener condition.

    noResponseCountRef.current = 0; // successful response — reset strike counter
    setTranscript(prev=>[...prev,{role:"user",text:native}]);
    setSessionStats(s=>({exchanges:s.exchanges+1,words:s.words+english.split(" ").length}));

    // Skip logic: evaluate whether the answered question routes to a non-sequential question.
    // If so, append a directive so Claude asks the right question and sets the right questionIndex.
    const nextQIdx = resolveNextQuestion(currentQRef.current, english, questions);
    const isSkipping = nextQIdx !== currentQRef.current + 1 && nextQIdx < questions.length;
    let claudeMsg = english;
    if (isSkipping) {
      const nextQText = questions[nextQIdx]?.questionText || questions[nextQIdx]?.text || "";
      claudeMsg += `\n\n[INTERVIEWER_DIRECTIVE: Skip ahead to question ${nextQIdx + 1}${nextQText ? `: "${nextQText}"` : ""}. Do not ask any questions in between. Use "questionIndex": ${nextQIdx} in your JSON response.]`;
    }

    // Coordination: TTS may start early (from stream); completion fires only after BOTH
    // TTS finishes AND the full JSON reply (with isComplete/questionIndex) is received.
    let ttsFinished=false;
    let fullReplyMeta=null;
    let earlyStarted=false;

    const proceed=()=>{
      if(!ttsFinished||!fullReplyMeta) return; // wait for both gates
      if(!interviewMountedRef.current) return; // component unmounted — discard
      const{qi,isFollowUp,isComplete,isDisqualified}=fullReplyMeta;
      inFollowUpRef.current = isFollowUp; // track whether next user turn is a probe response
      setCurrentQ(qi);
      const _calcProgress = Math.min(100, Math.round(((qi + 1 + (isFollowUp ? 0.5 : 0)) / questions.length) * 100));
      setProgress(prev => {
        if (_calcProgress < prev) console.warn("[Progress] regression detected", { qi, isFollowUp, calculatedProgress: _calcProgress, previousProgress: prev });
        return Math.max(prev, _calcProgress);
      });
      if(isDisqualified){
        // Agent already said polite closing (TTS played above) — now navigate away
        streamRef.current?.getTracks().forEach(t => t.stop());
        clearInterval(timerRef.current);
        stopAll();
        onDisqualified?.(sessionIdRef.current);
        return;
      }
      if(isComplete){
        setStatusMsg("Interview complete!"); clearInterval(timerRef.current);
        stopAll();
        onEnd({transcript,stats:sessionStats,duration:timer,sessionId:sessionIdRef.current,modality,capturedFrames:capturedFramesRef.current,endReason:"complete"});
      }else if(!isFollowUp && questions[qi]?.stimulusUrl){
        // New main question has a stimulus — show it before activating mic
        setStatusMsg("Review the content below, then click Continue to answer…");
        const _sq = questions[qi];
        const _stype = _sq.stimulusType === "video" ? "video" : "image";
        setStimulus({
          url: _sq.stimulusUrl,
          type: _stype,
          alt: _sq.stimulusAlt || "",
          onDismiss: ()=>{
            setStimulus(null);
            const restate = `Welcome back! Now that you've reviewed the ${_stype}, ${_sq.questionText}`;
            setStatusMsg("…");
            agentSpeak(restate,()=>{ setStatusMsg("Your turn — tap 🎤 to respond"); startListening(handleUserResponseRef.current); });
            translateForDisplay(restate).then(displayText=>setTranscript(prev=>[...prev,{role:"agent",text:displayText}]));
          },
        });
      }else{
        setStatusMsg("Your turn — tap 🎤 to respond");
        startListening(handleUserResponseRef.current);
      }
    };

    const onTTSDone=()=>{ ttsFinished=true; proceed(); };

    const reply=await askClaude(claudeMsg,async(agentText)=>{
      // Fires as soon as "text" field is extracted from the LLM stream — start TTS immediately
      earlyStarted=true;
      agentSpeak(agentText,onTTSDone); // audio starts without waiting for translation
      const displayText=await translateForDisplay(agentText);
      setTranscript(prev=>[...prev,{role:"agent",text:displayText}]);
    });

    if(!reply) return;

    const qi=typeof reply.questionIndex==="number"?reply.questionIndex:currentQRef.current;
    fullReplyMeta={qi,isFollowUp:!!reply.isFollowUp,isComplete:!!reply.isComplete,isDisqualified:!!reply.isDisqualified};

    if(!earlyStarted){
      // Stream didn't extract text early (e.g. JSON format changed) — fall back to normal flow
      const displayText=await translateForDisplay(reply.text);
      setTranscript(prev=>[...prev,{role:"agent",text:displayText,isFollowUp:reply.isFollowUp}]);
      agentSpeak(reply.text,onTTSDone);
    }

    proceed(); // run now if TTS already finished while we were awaiting the full reply
  // eslint-disable-next-line
  },[agentSpeak,startListening,questions,translateForDisplay]);

  useEffect(()=>{ handleUserResponseRef.current=handleUserResponse; },[handleUserResponse]);

  // ── No-response handler: auto follow-up, 3-strike graceful end ───────────
  const handleNoResponse = useCallback(()=>{
    if(!interviewMountedRef.current) return;
    noResponseCountRef.current += 1;
    const strike = noResponseCountRef.current;

    if(strike >= 3){
      const msg = "I wasn't able to pick up your voice after several attempts. This is usually due to background noise or microphone issues. I'll need to close the interview here — thank you for your time, and please try again in a quieter environment.";
      setStatusMsg("Interview ended — no response recorded.");
      agentSpeak(msg,()=>{
        if(!interviewMountedRef.current) return;
        stopAll();
        onEnd({transcript,stats:sessionStats,duration:timer,sessionId:sessionIdRef.current,modality,capturedFrames:capturedFramesRef.current,endReason:"no_response",questionIndex:currentQRef.current,progress});
      });
      translateForDisplay(msg).then(displayText=>setTranscript(prev=>[...prev,{role:"agent",text:displayText}]));
      return;
    }

    const prompts = [
      "I didn't quite catch that — could you move a little closer to your microphone, speak clearly, and try again?",
      "I'm still having trouble hearing you. Please make sure you're in a quiet spot with no background noise, then try once more.",
    ];
    const msg = prompts[strike - 1];
    setStatusMsg("…");
    agentSpeak(msg,()=>{
      if(!interviewMountedRef.current) return;
      setStatusMsg("Your turn — tap 🎤 to respond");
      startListening(handleUserResponseRef.current);
    });
    translateForDisplay(msg).then(displayText=>setTranscript(prev=>[...prev,{role:"agent",text:displayText}]));
  // eslint-disable-next-line
  },[agentSpeak,startListening,stopAll,onEnd,transcript,sessionStats,timer,modality,translateForDisplay]);

  useEffect(()=>{ handleNoResponseRef.current=handleNoResponse; },[handleNoResponse]);

  // ── Session creation + interview kickoff (sequential) ────────────────────
  useEffect(()=>{
    // Guard against re-entrant calls (e.g. parent re-rendering InterviewPage).
    // NOT used to block StrictMode's second run — we reset the flag in cleanup
    // so the second (real) mount gets its own IIFE with mounted=true.
    if(interviewStartedRef.current) return;
    interviewStartedRef.current=true;
    interviewMountedRef.current=true; // restore after previous cleanup
    let mounted=true; // set to false on unmount so the async IIFE self-aborts
    (async()=>{
      // Create backend session first — STT results stored against this ID.
      // If resuming an abandoned session, reuse that session ID instead of creating a new one.
      // Determine starting question index (0 for new sessions, or last answered + 1 for resumes)
      let startQIdx = 0;
      let priorSttResults = [];

      if(resumeSessionId){
        if(mounted) sessionIdRef.current=resumeSessionId;
        console.log("[Session] resuming existing session",resumeSessionId);
        // Fetch prior STT results to determine where to resume
        try{
          const sttRes = await apiFetch(`${API_BASE}/api/sessions/${resumeSessionId}/stt-results`);
          if(sttRes.ok){
            const sttData = await sttRes.json();
            priorSttResults = sttData.sttResults || [];
            if(priorSttResults.length > 0){
              const maxIdx = Math.max(...priorSttResults.map(r=>r.questionIndex));
              startQIdx = Math.min(maxIdx + 1, questions.length - 1);
            }
          }
        }catch(e){ console.warn("[Resume STT fetch]",e.message); }
      }else{
        try{
          const uuidRe=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          const safeParticipantId=uuidRe.test(participant?.id||"")?participant.id:null;
          const safeSurveyId=survey?.id||null;
          const sessionBody = interviewToken
            ? { language: languageOverride || null }
            : { surveyId: safeSurveyId, participantId: safeParticipantId, language: languageOverride || participant?.language || null };
          const r=await apiFetch(`${API_BASE}/api/sessions`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(sessionBody)});
          const rawText=await r.text();
          let d;
          try{ d=JSON.parse(rawText); }catch(jsonErr){
            console.error("[Session create] non-JSON response (HTTP",r.status,"):", rawText.slice(0,300));
            throw new Error(`HTTP ${r.status} — server returned non-JSON`);
          }
          if(!r.ok){
            console.error("[Session create] HTTP",r.status,":",d?.error||rawText.slice(0,200));
            // Surface actionable server messages (already-completed, survey closed)
            // instead of the generic fallback below.
            if(mounted && (d?.code==="ALREADY_COMPLETED" || d?.code==="SURVEY_CLOSED")){
              setStatusMsg(d.error); return;
            }
          }
          if(mounted) sessionIdRef.current=d.sessionId;
        }catch(e){ console.error("[Session create]",e.message); if(mounted){ setStatusMsg("Could not start session — please check your connection and try again."); return; } }
      }

      if(!mounted) return;
      if(!sessionIdRef.current){ setStatusMsg("Could not start session — please go back and try again."); return; }

      // Set starting question index for resume
      if(startQIdx > 0){
        setCurrentQ(startQIdx);
        currentQRef.current = startQIdx;
        setProgress(Math.round((startQIdx / questions.length) * 100));
        // Rebuild alternating user/assistant conversation for Claude context
        // assistant = question asked, user = participant's answer.
        // Sort by questionIndex so resumed turns appear in their original order.
        const priorSorted = [...priorSttResults].sort((a,b)=>a.questionIndex-b.questionIndex);
        const qTextFor = r => questions[r.questionIndex]?.questionText || questions[r.questionIndex]?.text || `Question ${r.questionIndex+1}`;
        const priorConvo = priorSorted.flatMap(r=>[
          { role:"assistant", content: qTextFor(r) },
          { role:"user",      content: r.transcript },
        ]);
        conversationRef.current = priorConvo;
        // Build prior transcript display entries. Must include each agent question
        // (translated for multilingual surveys) before the participant's answer —
        // otherwise the resumed conversation shows a stack of "Response recorded"
        // chips with no questions above them.
        const priorQDisplay = await Promise.all(priorSorted.map(r=>translateForDisplay(qTextFor(r))));
        const priorTranscriptEntries = priorSorted.flatMap((r,i)=>[
          { role:"agent",       text: priorQDisplay[i] },
          { role:"participant", text: r.transcript },
        ]);
        setTranscript(priorTranscriptEntries);
      }

      const isResume = startQIdx > 0;
      const resumePrompt = isResume
        ? `(Resuming interview. Questions 0–${startQIdx-1} have already been answered. Briefly and warmly acknowledge the resume (e.g. "Welcome back! Let's continue where we left off."), then ask question ${startQIdx+1} of ${questions.length}.)`
        : "(Interview starting now — please greet the participant and ask the first question)";

      const intro=await askClaude(resumePrompt,undefined,{skipPersist:true});
      if(!intro||!mounted) return;
      translateForDisplay(intro.text).then(displayText=>setTranscript(prev=>[...prev,{role:"agent",text:displayText}]));
      const q0 = questions[startQIdx];
      agentSpeak(intro.text,()=>{
        if(!mounted) return;
        if(q0?.stimulusUrl){
          setStatusMsg("Review the content below, then click Continue to answer…");
          const _s0type = q0.stimulusType === "video" ? "video" : "image";
          setStimulus({
            url: q0.stimulusUrl,
            type: _s0type,
            alt: q0.stimulusAlt || "",
            onDismiss: ()=>{
              setStimulus(null);
              const restate = `Welcome back! Now that you've reviewed the ${_s0type}, ${q0.questionText}`;
              setStatusMsg("…");
              agentSpeak(restate,()=>{ setStatusMsg("Your turn — tap 🎤 to respond"); startListening(handleUserResponseRef.current); });
              translateForDisplay(restate).then(displayText=>setTranscript(prev=>[...prev,{role:"agent",text:displayText}]));
            },
          });
        }else{
          setStatusMsg("Your turn — tap 🎤 to respond");
          startListening(handleUserResponseRef.current);
        }
      });
    })();
    return()=>{ mounted=false; interviewMountedRef.current=false; interviewStartedRef.current=false; stopAll(); };
  // eslint-disable-next-line
  },[]);

  // ── Data-collection badges for video / voice ─────────────────────────────
  const dataBadges = isVideo
    ? [{label:"🎙 Transcript",on:true},{label:"💬 Sentiment",on:true},{label:"👁 Facial",on:!cameraError}]
    : [{label:"🎙 Transcript",on:true},{label:"💬 Sentiment",on:true},{label:"🔊 Audio Tone",on:true}];

  // ─── CHAT mode: full-screen messenger layout ───────────────────────────────
  if (isChat) {
    return (
      <>
      {stimulus && <StimulusOverlay url={stimulus.url} type={stimulus.type} alt={stimulus.alt} onDismiss={stimulus.onDismiss} />}
      <style>{`
        .vc-chat { display:flex; flex-direction:column; height:calc(100vh - 80px); max-width:800px; margin:0 auto; width:100%; }
        @media(max-width:640px){ .vc-chat{ height:calc(100dvh - 56px); } }
        .vc-chat-msgs::-webkit-scrollbar{ width:4px; } .vc-chat-msgs::-webkit-scrollbar-thumb{ background:rgba(255,255,255,0.08); border-radius:99px; }
        .vc-chat-textarea:focus{ border-color:${C.teal} !important; }
        .vc-chat-textarea:disabled{ opacity:0.45; cursor:not-allowed; }
        @keyframes vc-thinking{ 0%,80%,100%{opacity:.25;transform:scale(0.8)} 40%{opacity:1;transform:scale(1)} }
      `}</style>
      <div className="vc-chat">

        {/* ── Header bar ── */}
        <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 18px", background:C.card, borderRadius:"14px 14px 0 0", border:`1px solid ${C.border}`, borderBottom:`1px solid ${C.subtle}`, flexShrink:0, zIndex:2 }}>
          <AgentAvatar agent={agentProfile} size={42} speaking={agentSpeaking} />
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ color:C.text, fontSize:14, fontWeight:700 }}>{agentName}</span>
              <span style={{ fontSize:11, color:agentSpeaking?C.teal:isListening?C.teal:C.muted, display:"flex", alignItems:"center", gap:5 }}>
                {(agentSpeaking||isListening)&&<GlowDot color={agentSpeaking?C.teal:C.teal} size={5} pulse />}
                {agentSpeaking?"Thinking…":isListening?"Waiting for your reply…":"…"}
              </span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:5 }}>
              <div style={{ flex:1, height:4, background:C.subtle, borderRadius:99, overflow:"hidden" }}>
                <div style={{ width:`${progress}%`, height:"100%", background:`linear-gradient(90deg,${agentProfile.color},${C.teal})`, borderRadius:99, transition:"width 0.6s ease" }} />
              </div>
              <span style={{ color:C.muted, fontSize:11, flexShrink:0, whiteSpace:"nowrap" }}>{progress}%</span>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:5, background:`${C.teal}15`, border:`1px solid ${C.teal}30`, borderRadius:8, padding:"4px 10px" }}>
              <GlowDot color={C.teal} pulse size={5} />
              <span style={{ color:C.teal, fontSize:11, fontWeight:700 }}>{fmtTime(timer)}</span>
            </div>
            <Btn variant="danger" size="sm" onClick={()=>{ stopAll(); onEnd({transcript,stats:sessionStats,duration:timer,sessionId:sessionIdRef.current,modality,capturedFrames:capturedFramesRef.current,endReason:"manual",questionIndex:currentQRef.current,progress}); }}>⏹ End</Btn>
          </div>
        </div>

        {/* ── Message bubbles ── */}
        <div className="vc-chat-msgs" style={{ flex:1, overflowY:"auto", padding:"24px 20px", background:C.bg, display:"flex", flexDirection:"column", gap:20, minHeight:0, border:`1px solid ${C.border}`, borderTop:"none", borderBottom:"none" }}>
          {transcript.length===0&&(
            <div style={{ margin:"auto", textAlign:"center", color:C.muted, padding:"40px 20px" }}>
              <div style={{ marginBottom:14, display:"flex", justifyContent:"center" }}><AgentAvatar agent={agentProfile} size={64} /></div>
              <div style={{ fontSize:14, lineHeight:1.6 }}>{agentName} is starting the interview…</div>
            </div>
          )}
          {transcript.map((t,i)=>{
            const isAgent = t.role==="agent";
            return (
              <div key={i} style={{ display:"flex", justifyContent:isAgent?"flex-start":"flex-end", alignItems:"flex-end", gap:10 }}>
                {isAgent&&<AgentAvatar agent={agentProfile} size={32} />}
                <div style={{ maxWidth:"72%", minWidth:48 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:isAgent?agentProfile.color:C.teal, marginBottom:5, textAlign:isAgent?"left":"right", paddingLeft:isAgent?2:0, paddingRight:isAgent?0:2 }}>
                    {isAgent?`${agentName}${t.isFollowUp?" (follow-up)":""}` :"You"}
                  </div>
                  <div style={{ background:isAgent?C.card:`${C.teal}22`, border:`1px solid ${isAgent?C.border:C.teal+"45"}`, borderRadius:isAgent?"4px 16px 16px 16px":"16px 4px 16px 16px", padding:"13px 17px" }}>
                    <p style={{ margin:0, color:C.text, fontSize:14, lineHeight:1.75 }}>{t.text}</p>
                  </div>
                </div>
                {!isAgent&&<div style={{ width:32, height:32, borderRadius:"50%", background:`${C.teal}20`, border:`1.5px solid ${C.teal}40`, fontSize:14, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, color:C.teal, fontWeight:700 }}>
                  {(participant?.name||"?")[0].toUpperCase()}
                </div>}
              </div>
            );
          })}
          {/* Typing / thinking indicator */}
          {agentSpeaking&&(
            <div style={{ display:"flex", alignItems:"flex-end", gap:10 }}>
              <AgentAvatar agent={agentProfile} size={32} speaking={agentSpeaking} />
              <div style={{ background:C.card, border:`1px solid ${agentProfile.color}30`, borderRadius:"4px 16px 16px 16px", padding:"14px 18px", display:"flex", alignItems:"center", gap:5 }}>
                {[0,1,2].map(k=>(
                  <div key={k} style={{ width:7, height:7, borderRadius:"50%", background:agentProfile.color, animation:`vc-thinking 1.2s ease-in-out ${k*0.2}s infinite` }} />
                ))}
              </div>
            </div>
          )}
          <div ref={transcriptEndRef} />
        </div>

        {/* ── Reply input area ── */}
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderTop:`1px solid ${C.subtle}`, borderRadius:"0 0 14px 14px", padding:"14px 18px", flexShrink:0 }}>
          <div style={{ display:"flex", gap:10, alignItems:"flex-end" }}>
            <textarea
              ref={chatInputRef}
              className="vc-chat-textarea"
              value={chatInput}
              onChange={e=>setChatInput(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); handleChatSubmit(); } }}
              disabled={agentSpeaking||!isListening}
              placeholder={agentSpeaking?`${agentName} is thinking…`:isListening?"Type your response… (Enter to send)":"Please wait…"}
              rows={2}
              style={{ flex:1, background:C.surface, border:`1px solid ${isListening&&!agentSpeaking?C.teal:C.border}`, borderRadius:10, padding:"11px 14px", color:C.text, fontSize:14, fontFamily:"inherit", outline:"none", resize:"none", lineHeight:1.6, transition:"border-color 0.2s", maxHeight:130, overflowY:"auto" }}
            />
            <Btn variant="primary" onClick={handleChatSubmit} disabled={agentSpeaking||!isListening||!chatInput.trim()} style={{ height:46, paddingLeft:20, paddingRight:20, flexShrink:0, alignSelf:"flex-end" }}>Send ↵</Btn>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:8 }}>
            <span style={{ fontSize:11, color:C.muted }}>💬 Sentiment analysis · {sessionStats.exchanges} exchange{sessionStats.exchanges!==1?"s":""}</span>
            <span style={{ fontSize:11, color:C.muted }}>Shift+Enter for new line · {sessionStats.words} words</span>
          </div>
        </div>
      </div>
      </>
    );
  }

  // ─── VIDEO / VOICE mode (responsive two-column) ───────────────────────────
  return (
    <>
    {stimulus && <StimulusOverlay url={stimulus.url} type={stimulus.type} alt={stimulus.alt} onDismiss={stimulus.onDismiss} />}
    <style>{`
      .vc-iv-grid  { display:grid; grid-template-columns:1fr 288px; gap:20px; height:calc(100vh - 100px); }
      .vc-iv-right { display:flex; flex-direction:column; gap:14px; overflow-y:auto; padding-right:2px; }
      /* Tablet & mobile portrait: pin to the viewport and let the camera dominate.
         (Previously 641–900px used document flow with height:auto, which collapsed
          the video to its min-height on iPad — the camera now grows to fill instead.) */
      @media(max-width:900px){
        .voxa-main.live { position:fixed; inset:0; box-sizing:border-box; overflow:hidden; display:flex; flex-direction:column; }
        .vc-iv-grid  { display:flex; flex-direction:column; height:auto; flex:1; min-height:0; gap:8px; }
        .vc-iv-left  { flex:1 1 auto; min-height:0; gap:8px !important; }   /* camera region grows to fill; tighten inter-card gap */
        .vc-iv-right { flex:0 0 auto; height:22dvh; min-height:0; overflow:hidden; display:flex; flex-direction:column; padding-right:0; gap:0; }  /* transcript = capped strip — kept small so the camera dominates */
        .vc-transcript-card   { flex:1 !important; min-height:0 !important; overflow:hidden !important; }
        .vc-transcript-scroll { flex:1 !important; min-height:0 !important; overflow-y:auto !important; }
        .vc-transcript-timer  { display:flex !important; }
      }
      .vc-badges { display:flex; flex-direction:column; gap:5px; }
      @media(max-width:640px){
        .vc-iv-right { height:17dvh; }   /* phones: minimal transcript strip — maximise camera height */
        .vc-badges { flex-direction:row; flex-wrap:wrap; gap:4px; }
        .vc-badge  { padding:2px 6px !important; font-size:9px !important; }
        .vc-agent-card { padding:6px 10px !important; gap:7px !important; bottom:8px !important; left:8px !important; }
        .vc-agent-avatar { width:28px !important; height:28px !important; font-size:14px !important; }
        .vc-agent-name { font-size:11px !important; }
        .vc-agent-status { font-size:10px !important; }
        .vc-rec-hint { bottom:52px !important; left:8px !important; right:8px !important; padding:6px 10px !important; }
        .vc-rec-hint-sub { display:none !important; }
        /* Transcript card fills all remaining height; clip overflow so nothing bleeds */
        .vc-transcript-card { flex:1 !important; min-height:0 !important; overflow:hidden !important; }
        /* Scroll area fills the card and scrolls internally */
        .vc-transcript-scroll { flex:1 !important; min-height:0 !important; overflow-y:auto !important; }
        .vc-transcript-timer { display:flex !important; }
      }
      /* ── Landscape mobile: 2-column layout (video left, transcript right) ── */
      @media (orientation:landscape) and (max-height:500px){
        .voxa-main.live { position:fixed; inset:0; box-sizing:border-box; overflow:hidden; display:flex; flex-direction:column; padding:6px 10px; }
        .vc-iv-grid { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:10px; flex:1; min-height:0; height:auto; }
        .vc-iv-left { display:flex; flex-direction:column; gap:6px; min-height:0; overflow:hidden; flex:1 1 50%; }
        .vc-iv-right { flex:1; height:auto; min-height:0; overflow:hidden; display:flex; flex-direction:column; padding-right:0; gap:0; }
        .vc-transcript-card { flex:1 !important; min-height:0 !important; overflow:hidden !important; }
        .vc-transcript-scroll { flex:1 !important; min-height:0 !important; overflow-y:auto !important; }
        .vc-transcript-timer { display:flex !important; }
        .vc-badges { flex-direction:row; flex-wrap:wrap; gap:4px; }
        .vc-badge  { padding:2px 6px !important; font-size:9px !important; }
        .vc-agent-card { padding:5px 8px !important; gap:6px !important; bottom:6px !important; left:6px !important; }
        .vc-agent-avatar { width:24px !important; height:24px !important; font-size:12px !important; }
        .vc-agent-name { font-size:10px !important; }
        .vc-agent-status { font-size:9px !important; }
        .vc-rec-hint-sub { display:none !important; }
      }
      @keyframes vc-face-flash { 0%,100%{ background:rgba(10,11,20,0.78); border-color:rgba(255,190,0,0); } 50%{ background:rgba(255,190,0,0.18); border-color:rgba(255,190,0,0.6); } }
      @keyframes vc-mic-ring { 0%,100%{ box-shadow:0 0 0 0 rgba(0,210,180,0.55),0 0 16px rgba(0,210,180,0.5); } 60%{ box-shadow:0 0 0 10px rgba(0,210,180,0),0 0 20px rgba(0,210,180,0.3); } }
      @keyframes vc-stop-ring { 0%,100%{ box-shadow:0 0 0 0 rgba(255,71,87,0.6),0 0 18px rgba(255,71,87,0.55); } 60%{ box-shadow:0 0 0 12px rgba(255,71,87,0),0 0 22px rgba(255,71,87,0.25); } }
      @keyframes vc-retry-ring { 0%,100%{ box-shadow:0 0 0 0 rgba(255,171,0,0.55),0 0 14px rgba(255,171,0,0.45); } 60%{ box-shadow:0 0 0 10px rgba(255,171,0,0),0 0 18px rgba(255,171,0,0.2); } }
      @keyframes vc-halo-ring { 0%,100%{ box-shadow:0 0 18px 2px var(--halo,rgba(0,210,180,0.45)),0 0 40px 8px var(--halo,rgba(0,210,180,0.15)); transform:scale(1); } 50%{ box-shadow:0 0 36px 8px var(--halo,rgba(0,210,180,0.75)),0 0 70px 18px var(--halo,rgba(0,210,180,0.3)); transform:scale(1.04); } }
    `}</style>
    {/* Audio blocked by autoplay policy / OS interruption (iOS) — one tap resumes.
        audioEngine also arms a document-level pointerdown handler while blocked,
        so ANY tap recovers; this banner exists for discoverability. */}
    {audioBlocked && (
      <div onClick={()=>{ unlockAudio(); setAudioBlocked(false); }}
        style={{ position:"fixed", top:14, left:"50%", transform:"translateX(-50%)", zIndex:10000, background:C.amber, color:"#0c0d16", fontWeight:700, fontSize:13, padding:"11px 20px", borderRadius:99, cursor:"pointer", boxShadow:"0 8px 28px rgba(0,0,0,0.5)", whiteSpace:"nowrap" }}>
        🔊 Tap to enable audio
      </div>
    )}
    {/* TEMP DEBUG — remove after iPad VAD investigation */}
    <VadDebugBadge vadRef={vadDebugRef} />
    <div className="vc-iv-grid">
      <div className="vc-iv-left" style={{ display:"flex", flexDirection:"column", gap:16, minHeight:0 }}>

        {/* ── Main panel: camera (video) or avatar (voice) ── */}
        <div style={{ flex:1, position:"relative", background:"#05060f", borderRadius:14, border:`1px solid ${userSpeaking?C.teal:C.border}`, overflow:"hidden", minHeight:240, transition:"border-color 0.3s", boxShadow:userSpeaking?`0 0 20px ${C.teal}30`:"none", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
          {isVideo&&<>
            <video ref={videoRef} autoPlay muted playsInline style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", display:"block", transform:"scaleX(-1)" }} />
            <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 50% 40%,#1a1d35 0%,#05060f 100%)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", zIndex:1, opacity:streamRef.current?0:1, pointerEvents:"none" }}>
              <div style={{ fontSize:56, marginBottom:12, opacity:0.35 }}>🎥</div>
              {cameraError?<div style={{ color:C.amber, fontSize:12, textAlign:"center", maxWidth:220 }}>Camera not available. Interview continues without video.</div>:<div style={{ color:C.muted, fontSize:12 }}>Starting camera…</div>}
            </div>
            {userSpeaking&&<div style={{ position:"absolute", inset:0, border:`2px solid ${C.teal}`, borderRadius:14, zIndex:5, boxShadow:`inset 0 0 30px ${C.teal}20`, pointerEvents:"none" }} />}
            {isListening&&<div className="vc-rec-hint" style={{ position:"absolute", bottom:80, left:16, right:16, zIndex:10, background:"rgba(10,11,20,0.92)", border:`1px solid ${C.teal}40`, borderRadius:10, padding:"10px 14px", backdropFilter:"blur(8px)" }}>
              <div style={{ fontSize:11, color:C.teal, fontWeight:600, marginBottom:4 }}>🎙 Recording your response…</div>
              <div className="vc-rec-hint-sub" style={{ fontSize:12, color:C.muted, lineHeight:1.5 }}>Speak clearly — auto-submits after a pause, or tap ⏹ to submit</div>
            </div>}
            <div className="vc-agent-card" style={{ position:"absolute", bottom:16, left:16, zIndex:10, display:"flex", alignItems:"center", gap:10, background:"rgba(10,11,20,0.9)", border:`1px solid ${agentProfile.color}40`, borderRadius:10, padding:"10px 14px", backdropFilter:"blur(8px)" }}>
              <AgentAvatar agent={agentProfile} size={36} speaking={agentSpeaking} />
              <div>
                <div className="vc-agent-name" style={{ color:C.text, fontSize:13, fontWeight:600 }}>{agentName} — AI Interviewer</div>
                <div className="vc-agent-status" style={{ fontSize:11, color:agentSpeaking?C.teal:C.muted, display:"flex", alignItems:"center", gap:5 }}>
                  {agentSpeaking&&<GlowDot color={C.teal} size={5} pulse />}{agentSpeaking?"Speaking…":"Listening"}
                </div>
              </div>
            </div>
          </>}
          {isVoice&&<div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:16, padding:32 }}>
            <div style={{ position:"relative", display:"flex", alignItems:"center", justifyContent:"center", "--halo":`${agentProfile.color}90` }}>
              {/* Pulsing outer ring — only shown while agent is speaking */}
              {agentSpeaking&&<div style={{ position:"absolute", inset:-10, borderRadius:"50%", border:`2px solid ${agentProfile.color}`, opacity:0.6, animation:"vc-halo-ring 1.4s ease-in-out infinite", pointerEvents:"none" }} />}
              <AgentAvatar agent={agentProfile} size={96} speaking={agentSpeaking} />
            </div>
            <div style={{ textAlign:"center" }}>
              <div style={{ color:C.text, fontSize:15, fontWeight:700 }}>{agentName} — AI Interviewer</div>
              <div style={{ fontSize:12, color:agentSpeaking?agentProfile.color:isListening?C.teal:C.muted, display:"flex", alignItems:"center", gap:6, justifyContent:"center", marginTop:5 }}>
                {agentSpeaking&&<GlowDot color={agentProfile.color} size={5} pulse />}{agentSpeaking?"Speaking…":isListening?"Listening…":"Thinking…"}
              </div>
            </div>
            <SoundWave active={agentSpeaking||userSpeaking} color={agentSpeaking?agentProfile.color:C.teal} bars={16} analyserRef={agentSpeaking?ttsAnalyserRef:undefined} />
          </div>}
          {/* Face warning overlay */}
          {isVideo&&faceWarning&&(
            <div style={{ position:"absolute", inset:0, zIndex:20, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", backdropFilter:"blur(4px)", borderRadius:14, pointerEvents:"none", border:"2px solid rgba(255,190,0,0.6)", animation:"vc-face-flash 1.2s ease-in-out infinite" }}>
              <div style={{ fontSize:36, marginBottom:10 }}>📷</div>
              <div style={{ color:"#ffbe00", fontSize:15, fontWeight:700, marginBottom:6, textAlign:"center" }}>Please face the camera</div>
              <div style={{ color:"rgba(255,255,255,0.7)", fontSize:12, textAlign:"center", maxWidth:220, lineHeight:1.6 }}>Make sure your face is fully visible and well-lit</div>
            </div>
          )}
          {/* REC badge */}
          <div style={{ position:"absolute", top:14, right:14, zIndex:10, display:"flex", alignItems:"center", gap:6, background:"rgba(255,71,87,0.2)", border:`1px solid ${C.red}50`, borderRadius:8, padding:"5px 10px" }}>
            <GlowDot color={C.red} pulse size={6} /><span style={{ color:C.red, fontSize:11, fontWeight:700 }}>REC {fmtTime(timer)}</span>
          </div>
          {/* Data badges */}
          <div className="vc-badges" style={{ position:"absolute", top:14, left:14, zIndex:10 }}>
            {dataBadges.map(b=>(
              <div key={b.label} className="vc-badge" style={{ background:"rgba(10,11,20,0.85)", border:`1px solid ${b.on?C.teal+"50":C.border}`, borderRadius:6, padding:"3px 9px", fontSize:10, color:b.on?C.teal:C.muted, backdropFilter:"blur(4px)" }}>{b.label}</div>
            ))}
          </div>
        </div>

        {/* ── Mic controls ── */}
        {(()=>{
          const isRetry = statusMsg.includes("try again");
          const cardBorder = isListening ? `1px solid ${C.teal}70` : isRetry ? `1px solid ${C.amber}70` : `1px solid ${C.border}`;
          const cardGlow   = isListening ? `0 0 18px ${C.teal}18` : isRetry ? `0 0 18px ${C.amber}12` : "none";
          return (
            <Card style={{ padding:"16px 20px", border:cardBorder, boxShadow:cardGlow, transition:"all 0.3s" }}>
              <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                {/* Mic / Stop / Retry button */}
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:5, flexShrink:0 }}>
                  <button
                    onClick={isListening ? stopListening : ()=>!agentSpeaking&&startListening(handleUserResponseRef.current)}
                    disabled={agentSpeaking}
                    style={{
                      width: isListening ? 56 : 48,
                      height: isListening ? 56 : 48,
                      borderRadius:"50%",
                      border: isListening ? `2px solid ${C.teal}` : isRetry ? `2px solid ${C.amber}` : "none",
                      background: isListening ? C.red : agentSpeaking ? C.subtle : isRetry ? `${C.amber}25` : C.blue,
                      cursor: agentSpeaking ? "not-allowed" : "pointer",
                      fontSize: isListening ? 22 : 20,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      animation: isListening ? "vc-stop-ring 1.6s ease-in-out infinite" : isRetry ? "vc-retry-ring 2s ease-in-out infinite" : !agentSpeaking ? "vc-mic-ring 2.4s ease-in-out infinite" : "none",
                      flexShrink:0, transition:"all 0.25s",
                    }}>
                    {isListening ? "⏹" : isRetry ? "🔄" : "🎤"}
                  </button>
                  <span style={{ fontSize:10, fontWeight:700, color: isListening ? C.red : isRetry ? C.amber : agentSpeaking ? C.muted : C.blue, whiteSpace:"nowrap", letterSpacing:"0.03em" }}>
                    {isListening ? "TAP TO SUBMIT" : isRetry ? "TAP TO RETRY" : agentSpeaking ? "WAIT…" : "TAP TO SPEAK"}
                  </span>
                </div>

                {/* Status + waveform */}
                <div style={{ flex:1, minWidth:0 }}>
                  <SoundWave active={agentSpeaking||userSpeaking} color={agentSpeaking?agentProfile.color:C.teal} bars={20} analyserRef={agentSpeaking?ttsAnalyserRef:undefined} />
                  <div style={{ marginTop:6, fontSize:12, fontWeight: isRetry||isListening ? 600 : 400, color: agentSpeaking ? agentProfile.color : isListening ? C.teal : isRetry ? C.amber : C.muted, display:"flex", alignItems:"center", gap:5 }}>
                    {isListening && <GlowDot color={C.teal} size={5} pulse />}
                    {isRetry && <GlowDot color={C.amber} size={5} pulse />}
                    {agentSpeaking ? `🔊 ${agentName} is speaking…` : isListening ? "Recording your response…" : isRetry ? statusMsg.replace("Didn't catch that — tap 🎤 to try again","Didn't catch that").replace("Error processing audio — tap 🎤 to try again","Error processing — please retry") : statusMsg}
                  </div>
                  {isListening && <div style={{ marginTop:3, fontSize:12, fontWeight:600, color:C.teal }}>Auto-submits after silence — or tap ⏹ to submit now</div>}
                  {isRetry && <div style={{ marginTop:3, fontSize:11, color:C.muted }}>Speak clearly and tap 🔄, or move closer to your mic</div>}
                  {micError && <div style={{ fontSize:11, color:C.amber, marginTop:4 }}>{micError}</div>}
                </div>

                <Btn variant="danger" size="sm" onClick={()=>{ stopAll(); onEnd({transcript,stats:sessionStats,duration:timer,sessionId:sessionIdRef.current,modality,capturedFrames:capturedFramesRef.current,endReason:"manual",questionIndex:currentQRef.current,progress}); }}>⏹ End</Btn>
              </div>
            </Card>
          );
        })()}

        {/* ── Progress ── */}
        <Card style={{ padding:"14px 20px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <span style={{ color:C.muted, fontSize:12 }}>Progress</span>
            <span style={{ color:C.muted, fontSize:12 }}>{progress}% complete</span>
          </div>
          <ProgressBar value={progress} color={agentProfile.color} height={5} />
        </Card>
      </div>

      {/* ── Right column: transcript + stats ── */}
      <div className="vc-iv-right">
        <Card className="vc-transcript-card" style={{ flex:"1 1 0", minHeight:0, display:"flex", flexDirection:"column" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, flexShrink:0 }}>
            <p style={{ margin:0, color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", fontWeight:700 }}>Conversation</p>
            <span className="vc-transcript-timer" style={{ display:"none", alignItems:"center", gap:5, background:`${C.teal}15`, border:`1px solid ${C.teal}30`, borderRadius:7, padding:"3px 9px" }}>
              <GlowDot color={C.teal} pulse size={5} />
              <span style={{ color:C.teal, fontSize:11, fontWeight:700 }}>{fmtTime(timer)}</span>
            </span>
          </div>
          <div className="vc-transcript-scroll" style={{ flex:1, overflowY:"auto", minHeight:0 }}>
            {transcript.length===0&&<div style={{ color:C.muted, fontSize:12, textAlign:"center", paddingTop:20 }}>The conversation will appear here…</div>}
            {transcript.map((t,i)=>(
              <div key={i} style={{ marginBottom:14, paddingBottom:14, borderBottom:i<transcript.length-1?`1px solid ${C.border}`:"none" }}>
                <div style={{ marginBottom:4 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:t.role==="agent"?agentProfile.color:C.teal }}>
                    {t.role==="agent"?`${agentName}${t.isFollowUp?" (follow-up)":""}` :"You"}
                  </span>
                </div>
                {t.role==="agent"
                  ? <p style={{ margin:0, fontSize:12, color:C.text, lineHeight:1.55 }}>{t.text}</p>
                  : <span style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:12, color:C.teal, background:`${C.teal}12`, border:`1px solid ${C.teal}30`, borderRadius:8, padding:"4px 10px" }}>✓ Response recorded</span>
                }
              </div>
            ))}
            <div ref={transcriptEndRef} />
          </div>
        </Card>
      </div>
    </div>
    </>
  );
}

// ─── COMPLETION PAGE ──────────────────────────────────────────────────────────
const COMPLETION_VARIANTS = {
  complete: {
    icon:    "✓",
    iconBg:  `${C.blue}20`,
    iconBorder: `2px solid ${C.teal}40`,
    iconColor:  C.teal,
    heading: "Interview Complete",
    body:    (name) => `Thank you${name ? `, ${name}` : ""}! Your responses have been recorded successfully.`,
    showSurveyTitle: true,
    subtext: "Your contribution helps shape real research outcomes.",
  },
  no_response: {
    icon:    "⚠",
    iconBg:  "rgba(255,190,0,0.12)",
    iconBorder: "2px solid rgba(255,190,0,0.35)",
    iconColor:  "#ffbe00",
    heading: "Session Ended Early",
    body:    (name) => `We weren't able to record a response${name ? `, ${name}` : ""}. This is usually caused by background noise or a microphone issue.`,
    showSurveyTitle: true,
    subtext: "Your partial session has been saved.",
  },
  manual: {
    icon:    "◼",
    iconBg:  "rgba(255,255,255,0.06)",
    iconBorder: "2px solid rgba(255,255,255,0.15)",
    iconColor:  C.muted,
    heading: "Session Ended",
    body:    (name) => `Your partial responses have been saved${name ? `, ${name}` : ""}. You can retake this survey from the dashboard.`,
    showSurveyTitle: true,
    subtext: null,
  },
};

const SUPPLIER_REDIRECT_COUNTDOWN_SECS = 10;

function CompletionPage({ survey, sessionData, participant, onReturnHome, onGoToPayoutDetails, apiFetch, supplierExitUrls, withdrawalToken }) {
  const [finalising,    setFinalising]    = useState(true);
  const [hasUpi,        setHasUpi]        = useState(null);  // null=loading, true/false after check
  const [qualityFlag,   setQualityFlag]   = useState(null);  // null | "pass" | "review" | "fail"
  // Resolved supplier exit URL, once known — drives the countdown banner below.
  // Not navigated to immediately: the S2S postback to the supplier already fired
  // server-side inside POST /report, independent of this browser redirect, so
  // there is no data-loss risk in giving the participant time to see/copy the
  // withdrawal link first.
  const [pendingExit,   setPendingExit]   = useState(null);
  const [countdown,     setCountdown]     = useState(SUPPLIER_REDIRECT_COUNTDOWN_SECS);
  const [linkCopied,    setLinkCopied]    = useState(false);
  const finalisedRef = useRef(false);      // guard: finalisation must run exactly once

  useEffect(() => {
    if (finalisedRef.current) return;      // StrictMode double-invoke / remount guard
    finalisedRef.current = true;
    const finalise = async () => {
      const sid    = sessionData?.sessionId;
      const frames = sessionData?.capturedFrames || [];
      if (!sid) { setFinalising(false); return; }
      try {
        if (frames.length > 0) {
          await apiFetch(`${API_BASE}/api/sessions/${sid}/frames`, {
            method:  "POST",
            headers: { "Content-Type": "application/json" },
            body:    JSON.stringify({ frames }),
          });
        }
        const reportRes  = await apiFetch(`${API_BASE}/api/sessions/${sid}/report`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ endReason: sessionData?.endReason || "complete", questionIndex: sessionData?.questionIndex, progress: sessionData?.progress }),
        });
        const reportData = await reportRes.json();
        if (reportData.qualityFlag && reportData.qualityFlag !== "pass") {
          setQualityFlag(reportData.qualityFlag);
        }
        // Supplier exit redirect — route based on how the session ended
        if (supplierExitUrls) {
          const endReason = sessionData?.endReason || "complete";
          let exitUrl = null;
          if (endReason === "screened_out") {
            exitUrl = supplierExitUrls.screenOut || supplierExitUrls.securityFail || null;
          } else if (endReason === "manual" || endReason === "no_response") {
            exitUrl = supplierExitUrls.abandon || null;
          } else if (reportData.qualityFlag === "fail") {
            // Hard QC fail → security/quality-fail disposition.
            exitUrl = supplierExitUrls.securityFail || null;
          } else if (reportData.qualityFlag === "review") {
            // Under review — a genuine completion pending an admin decision, NOT a
            // security failure. Route to the dedicated under-review URL. If the
            // supplier didn't configure one, fall back to the complete URL (the
            // participant did finish; the authoritative disposition is carried by
            // the S2S postback, which sends "pending_review" then complete/rejected).
            exitUrl = supplierExitUrls.underReview || supplierExitUrls.complete || null;
          } else {
            exitUrl = supplierExitUrls.complete || null;
          }
          if (exitUrl) {
            setPendingExit(exitUrl);
            return;
          }
        }
      } catch (e) {
        console.error("[Completion finalise]", e.message);
        // Supplier screen-out or security-fail redirect on error
        if (supplierExitUrls?.securityFail) {
          setPendingExit(supplierExitUrls.securityFail);
          return;
        }
      } finally {
        setFinalising(false);
      }
    };
    finalise();
  }, []); // eslint-disable-line

  // Countdown → auto-redirect once a supplier exit URL is resolved. A "Continue
  // now" button (rendered below) lets the participant skip the wait. Matches
  // the standard panel-redirect pattern (Qualtrics/Dynata-style "you have N
  // seconds before this page redirects") — this ticks independently of, and
  // does not gate, the withdrawal-link card shown alongside it.
  useEffect(() => {
    if (!pendingExit) return;
    if (countdown <= 0) { window.location.href = pendingExit; return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [pendingExit, countdown]);

  const copyWithdrawalLink = () => {
    const link = `${window.location.origin}/withdraw?token=${withdrawalToken}`;
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  // Block accidental navigation away while the session is still saving.
  // A close/refresh/back mid-save would lose frames + finalisation, so we
  // arm the browser's native "Leave site?" guard until finalising completes.
  useEffect(() => {
    if (!finalising) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [finalising]);

  // Check if participant has a UPI ID on file (for payout message)
  useEffect(() => {
    if (!participant?.id) return;
    apiFetch(`${API_BASE}/api/participants/${participant.id}/payment-details`)
      .then(r => r.json())
      .then(d => setHasUpi(!!(d.paymentDetail?.maskedUpiId)))
      .catch(() => setHasUpi(false));
  }, [participant?.id]); // eslint-disable-line

  const firstName = participant?.name?.split(" ")[0] || "";
  const variant   = COMPLETION_VARIANTS[sessionData?.endReason] || COMPLETION_VARIANTS.complete;
  const isComplete = !sessionData?.endReason || sessionData.endReason === "complete";

  // Payout message — only for completed sessions on cash-incentive surveys
  const hasCashIncentive = isComplete && survey?.incentiveType === "cash" && survey?.incentiveValue;
  const incentiveValue   = survey?.incentiveValue || "";

  return (
    <div className="voxa-completion">
      <div style={{ width:72, height:72, borderRadius:"50%", background:variant.iconBg, border:variant.iconBorder, display:"flex", alignItems:"center", justifyContent:"center", fontSize:32, margin:"0 auto 28px", color:variant.iconColor, fontWeight:800 }}>{variant.icon}</div>
      <h1 style={{ fontSize:28, fontWeight:800, color:C.text, margin:"0 0 20px", letterSpacing:"-0.02em" }}>{variant.heading}</h1>
      <p style={{ color:C.muted, fontSize:16, lineHeight:1.7, margin:"0 0 16px" }}>
        {variant.body(firstName)}
      </p>
      {variant.showSurveyTitle && survey?.title && (
        <p style={{ color:C.muted, fontSize:14, fontWeight:600, margin:"0 0 16px" }}>{survey.title}</p>
      )}
      {variant.subtext && (
        <p style={{ color:C.muted, fontSize:14, lineHeight:1.6, margin:"0 0 16px" }}>
          {variant.subtext}
        </p>
      )}

      {/* Payout context message */}
      {hasCashIncentive && hasUpi !== null && (
        <div style={{ background:`${C.teal}12`, border:`1px solid ${C.teal}40`, borderRadius:10, padding:"14px 18px", margin:"0 0 20px", textAlign:"left" }}>
          {hasUpi ? (
            <p style={{ color:C.teal, fontSize:13, margin:0, lineHeight:1.6 }}>
              💰 Your <strong>{incentiveValue}</strong> reward is now under review and will be paid to your UPI account within 5–7 business days.
            </p>
          ) : (
            <p style={{ color:C.text, fontSize:13, margin:0, lineHeight:1.6 }}>
              💰 To receive your <strong>{incentiveValue}</strong> reward, <span style={{ color:C.teal, textDecoration:"underline", cursor:"pointer" }} onClick={onGoToPayoutDetails||onReturnHome}>add your UPI ID in your Profile → Payout Details</span>.
            </p>
          )}
        </div>
      )}

      {/* Data quality warning — only shown when quality check is active and session failed/needs review */}
      {qualityFlag === "fail" && (
        <div data-testid="quality-warning-fail" style={{ background:"#7F1D1D22", border:"1px solid #EF444480", borderRadius:10, padding:"14px 18px", margin:"0 0 20px", textAlign:"left" }}>
          <p style={{ color:"#FCA5A5", fontSize:13, margin:0, lineHeight:1.6 }}>
            ⚠️ We had trouble capturing your responses clearly. Your session may not meet the minimum quality threshold and could be excluded from the survey results. This won't affect your ability to participate in future surveys.
          </p>
        </div>
      )}
      {qualityFlag === "review" && (
        <div data-testid="quality-warning-review" style={{ background:"#78350F22", border:"1px solid #F59E0B80", borderRadius:10, padding:"14px 18px", margin:"0 0 20px", textAlign:"left" }}>
          <p style={{ color:"#FCD34D", fontSize:13, margin:0, lineHeight:1.6 }}>
            ⏳ Some parts of your session are flagged for manual review. Your reward will be processed after a quick quality check, typically within 1–2 business days.
          </p>
        </div>
      )}

      {/* Save-state banner — prominent + animated. While saving, warns the
          user not to leave; once done, confirms it's safe to go. */}
      {finalising ? (
        <div
          role="alert"
          style={{
            display:"flex", alignItems:"flex-start", gap:14, textAlign:"left",
            background:`${C.amber}14`, border:`1.5px solid ${C.amber}66`, borderRadius:14,
            padding:"16px 18px", margin:"8px 0 28px",
            animation:"savePulse 1.8s ease-in-out infinite, savePop 0.3s ease-out",
          }}>
          <div style={{
            width:22, height:22, flexShrink:0, marginTop:1, borderRadius:"50%",
            border:`3px solid ${C.amber}44`, borderTopColor:C.amber,
            animation:"saveSpin 0.8s linear infinite",
          }} />
          <div>
            <div style={{ color:C.amber, fontWeight:700, fontSize:15, marginBottom:4 }}>
              Saving your responses — please wait
            </div>
            <div style={{ color:C.text, fontSize:13, lineHeight:1.6 }}>
              <strong>Do not close this tab, refresh, press back, or lock your device.</strong> This
              only takes a few seconds — leaving now could lose your interview.
            </div>
          </div>
        </div>
      ) : pendingExit ? (
        <div
          role="alert"
          style={{
            display:"flex", alignItems:"center", gap:12, textAlign:"left",
            background:`${C.teal}12`, border:`1.5px solid ${C.teal}55`, borderRadius:14,
            padding:"14px 18px", margin:"8px 0 28px", animation:"savePop 0.3s ease-out",
          }}>
          <div style={{ fontSize:20, flexShrink:0 }}>✓</div>
          <div style={{ color:C.teal, fontWeight:600, fontSize:14, lineHeight:1.5 }}>
            Your session is safely saved. Returning you to the survey platform in {countdown}s — don't close or refresh this page.
          </div>
        </div>
      ) : (
        <div
          style={{
            display:"flex", alignItems:"center", gap:12, textAlign:"left",
            background:`${C.teal}12`, border:`1.5px solid ${C.teal}55`, borderRadius:14,
            padding:"14px 18px", margin:"8px 0 28px", animation:"savePop 0.3s ease-out",
          }}>
          <div style={{ fontSize:20, flexShrink:0 }}>✓</div>
          <div style={{ color:C.teal, fontWeight:600, fontSize:14, lineHeight:1.5 }}>
            Your session is safely saved — you can leave this page now.
          </div>
        </div>
      )}
      {/* Supplier withdrawal link — shown for supplier participants who consented to
          facial analysis. Deliberately not tied to the redirect countdown above:
          withdrawing consent is a privacy action and shouldn't carry time pressure —
          it just needs to stay legible and easy to copy for as long as this page is up. */}
      {withdrawalToken && !finalising && (
        <div style={{ margin:"20px 0", padding:"16px 18px", background:`${C.amber}12`, border:`1.5px solid ${C.amber}70`, borderRadius:14, textAlign:"left" }}>
          <div style={{ color:C.amber, fontSize:14, fontWeight:700, marginBottom:6 }}>🔒 Save this link now</div>
          <div style={{ color:C.text, fontSize:12, lineHeight:1.7, marginBottom:10 }}>
            You consented to facial expression analysis for this interview. This link lets you withdraw that consent within 90 days — it won't be shown to you again, and no one else has access to it, so save or copy it before you leave.
            Withdrawing does not affect data already processed and shared with the research company.
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"stretch" }}>
            <div style={{ flex:1, minWidth:0, wordBreak:"break-all", fontSize:11, color:C.purple, background:`${C.purple}10`, border:`1px solid ${C.purple}30`, borderRadius:8, padding:"8px 12px", fontFamily:"monospace" }}>
              {`${window.location.origin}/withdraw?token=${withdrawalToken}`}
            </div>
            <Btn variant={linkCopied ? "teal" : "outline"} size="sm" onClick={copyWithdrawalLink} style={{ flexShrink:0 }}>
              {linkCopied ? "✓ Copied" : "📋 Copy"}
            </Btn>
          </div>
        </div>
      )}

      {pendingExit ? (
        <Btn variant="primary" onClick={() => { window.location.href = pendingExit; }}>Continue to survey platform now →</Btn>
      ) : supplierExitUrls && !isComplete ? (
        // Supplier participants have no dashboard. complete/screened_out/abandon
        // sessions redirect via exit URL (pendingExit above) — this fallback is
        // only shown when no abandon URL was configured by the supplier.
        <div style={{ color:C.muted, fontSize:14, lineHeight:1.7, marginTop:8, padding:"14px 18px", background:C.surface, border:`1px solid ${C.border}`, borderRadius:12 }}>
          Your progress has been saved. You can resume this interview — please return to the survey platform.
        </div>
      ) : (
        <Btn variant="primary" onClick={onReturnHome} disabled={finalising}>← Return to Dashboard</Btn>
      )}
    </div>
  );
}

export { CompletionPage, SetupPage };


// ─── PROFILE PAGE ─────────────────────────────────────────────────────────────
const PROFILE_OPTIONS = {
  education:      ["High School or below","Diploma / Vocational","Graduate (Bachelor's)","Post Graduate (Master's)","Doctorate / PhD","Other"],
  profession:     ["Student","Salaried — Private Sector","Salaried — Government / PSU","Business Owner / Self Employed","Freelancer / Consultant","Homemaker","Retired","Healthcare Professional","IT / Tech Professional","Finance / Banking","Other"],
  yearsWorking:   ["Less than 1 year","1–3 years","3–7 years","7–15 years","15+ years","Not applicable"],
  incomeBand:     ["Prefer not to say","Below ₹3 LPA","₹3–6 LPA","₹6–10 LPA","₹10–20 LPA","₹20–40 LPA","Above ₹40 LPA"],
  householdRole:  ["Primary decision maker","Joint decision maker","Influencer (input but not final call)","Not involved in household decisions"],
  dependents:     ["0","1","2","3","4","5+"],
  onlineFreq:     ["Daily","Multiple times a week","Weekly","Monthly","Rarely","Never"],
  avgSpend:       ["Below ₹1,000","₹1,000–5,000","₹5,000–15,000","₹15,000–30,000","Above ₹30,000","Prefer not to say"],
  primaryChannel: ["Amazon","Flipkart","Meesho","Myntra","D2C brand websites","Physical retail stores","Social commerce","Other"],
  deviceUsage:    ["Smartphone only","Laptop / Desktop only","Both smartphone and laptop/desktop","Tablet primarily"],
  internetHours:  ["Less than 1 hour","1–3 hours","3–6 hours","6–10 hours","10+ hours"],
  socialMedia:    ["Instagram","YouTube","WhatsApp","Facebook","Twitter/X","LinkedIn","Snapchat","ShareChat","None"],
  contentType:    ["News","Entertainment / OTT","Sports","Finance / Business","Gaming","Education / How-to","Travel / Lifestyle","Other"],
  industries:     ["FMCG / Consumer Goods","Automobile","Financial Services","Healthcare / Pharma","Real Estate","EdTech / Education","Retail / E-commerce","Travel & Hospitality","Technology / SaaS","Media & Entertainment","Agriculture","Manufacturing","None in particular"],
  brandAwareness: ["Very closely — I research before every purchase","Somewhat — I notice major launches","Occasionally","Rarely / Not at all"],
  gender:         ["Male","Female","Non-binary","Prefer not to say"],
  language:       ["English"], // overridden at runtime from /api/config — do not hardcode here
};

const INDIA_CITIES_PROFILE = [
  { name:"Mumbai",tier:1 },{ name:"Delhi",tier:1 },{ name:"Bengaluru",tier:1 },{ name:"Chennai",tier:1 },
  { name:"Kolkata",tier:1 },{ name:"Hyderabad",tier:1 },{ name:"Pune",tier:1 },{ name:"Ahmedabad",tier:1 },
  { name:"Jaipur",tier:2 },{ name:"Lucknow",tier:2 },{ name:"Kochi",tier:2 },{ name:"Chandigarh",tier:2 },
  { name:"Indore",tier:2 },{ name:"Nagpur",tier:2 },{ name:"Bhopal",tier:2 },{ name:"Patna",tier:2 },
  { name:"Vadodara",tier:2 },{ name:"Coimbatore",tier:2 },{ name:"Visakhapatnam",tier:2 },
  { name:"Agra",tier:3 },{ name:"Varanasi",tier:3 },{ name:"Rajkot",tier:3 },{ name:"Madurai",tier:3 },
  { name:"Mysuru",tier:3 },{ name:"Surat",tier:3 },{ name:"Other",tier:3 },
];

function ProfileSelect({ label, value, options, onChange, help }) {
  const st = { width:"100%", background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 12px", color:value?C.text:C.muted, fontSize:13, fontFamily:"inherit", cursor:"pointer" };
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:"block", color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", fontWeight:700, marginBottom:6 }}>{label}</label>
      <select value={value||""} onChange={e=>onChange(e.target.value)} style={st}>
        <option value="">—</option>
        {options.map(o=><option key={o} value={o}>{o}</option>)}
      </select>
      {help && <div style={{ color:C.muted, fontSize:11, lineHeight:1.5, marginTop:6 }}>{help}</div>}
    </div>
  );
}

function ProfileMulti({ label, value=[], options, onChange }) {
  const toggle = (opt) => onChange(value.includes(opt) ? value.filter(x=>x!==opt) : [...value,opt]);
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:"block", color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", fontWeight:700, marginBottom:8 }}>{label}</label>
      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
        {options.map(opt=>{
          const sel=value.includes(opt);
          return <button key={opt} onClick={()=>toggle(opt)} style={{ padding:"5px 12px", borderRadius:20, border:`1px solid ${sel?C.blue:C.border}`, background:sel?`${C.blue}20`:"transparent", color:sel?C.blue:C.muted, fontSize:12, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s" }}>{opt}</button>;
        })}
      </div>
    </div>
  );
}

function ProfileSection({ icon, title, children, onEdit, editing, onSave, onCancel, saving }) {
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:24, marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:editing?20:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:18 }}>{icon}</span>
          <span style={{ fontWeight:700, fontSize:15, color:C.text }}>{title}</span>
        </div>
        {!editing && <Btn variant="outline" size="sm" onClick={onEdit}>Edit</Btn>}
        {editing && (
          <div style={{ display:"flex", gap:8 }}>
            <Btn variant="ghost" size="sm" onClick={onCancel}>Cancel</Btn>
            <Btn size="sm" onClick={onSave} disabled={saving}>{saving?"Saving…":"Save"}</Btn>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function ProfileField({ label, value }) {
  const display = Array.isArray(value) ? (value.length ? value.join(", ") : "—") : (value || "—");
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:2, marginBottom:12 }}>
      <span style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.09em", fontWeight:700 }}>{label}</span>
      <span style={{ color:C.text, fontSize:13 }}>{display}</span>
    </div>
  );
}

function ProfilePage({ participant, onBack, onNameChange, onSignOut, apiFetch, supportedLanguages=["English"], scrollToSection=null, onManageConsents }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [editing,  setEditing]  = useState(null);  // which section is open
  const [draft,    setDraft]    = useState({});
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState("");
  const [consents, setConsents] = useState([]);
  const [revoking, setRevoking] = useState(null);   // consentRecord.id being revoked
  const [revokeConfirm, setRevokeConfirm] = useState(null); // record pending confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteInput,   setDeleteInput]   = useState("");
  const [deleting,      setDeleting]      = useState(false);
  const [exporting,     setExporting]     = useState(false);

  const payoutSectionRef = useRef(null);
  useEffect(()=>{
    if(scrollToSection==="payout" && payoutSectionRef.current){
      setTimeout(()=>payoutSectionRef.current?.scrollIntoView({behavior:"smooth",block:"start"}), 300);
    }
  },[scrollToSection]);

  // Payout details state
  const [paymentDetail,     setPaymentDetail]     = useState(null);
  const [payouts,           setPayouts]           = useState([]);
  const [payoutLoading,     setPayoutLoading]     = useState(true);
  const [upiDraft,          setUpiDraft]          = useState("");
  const [upiConsentDraft,   setUpiConsentDraft]   = useState(false);
  const [savingUpi,         setSavingUpi]         = useState(false);
  const [editingUpi,        setEditingUpi]        = useState(false);
  const [verifyOpen,        setVerifyOpen]        = useState(false);
  const [verifyAmount,      setVerifyAmount]      = useState("");
  const [verifyTxnId,       setVerifyTxnId]       = useState("");
  const [verifying,         setVerifying]         = useState(false);
  const [verifyError,       setVerifyError]       = useState("");

  // Referral state
  const [referralInfo,    setReferralInfo]    = useState(null);
  const [referralList,    setReferralList]    = useState([]);
  const [referralLoading, setReferralLoading] = useState(false);
  const [copyDone,        setCopyDone]        = useState(false);

  useEffect(()=>{
    if(!participant?.id){ setLoading(false); return; }
    Promise.all([
      apiFetch(`${API_BASE}/api/participants/${participant.id}`).then(r=>r.json()),
      apiFetch(`${API_BASE}/api/participants/${participant.id}/consent`).then(r=>r.json()),
    ])
      .then(([pData, cData])=>{
        if(pData.participant) setData(pData.participant); else setError("Could not load profile.");
        if(cData.consents)    setConsents(cData.consents);
      })
      .catch(()=>setError("Could not reach server."))
      .finally(()=>setLoading(false));
  },[participant?.id]); // eslint-disable-line

  // Fetch referral info if participant is eligible (completed first survey)
  useEffect(()=>{
    if(!participant?.id) return;
    // Use referralEligible from either fetched data or participant prop
    const eligible = data?.referralEligible || participant?.referralEligible;
    if(!eligible) return;
    setReferralLoading(true);
    Promise.all([
      apiFetch(`${API_BASE}/api/participants/${participant.id}/referral`).then(r=>r.json()),
      apiFetch(`${API_BASE}/api/participants/${participant.id}/referrals`).then(r=>r.json()),
    ])
      .then(([rInfo, rList])=>{
        setReferralInfo(rInfo);
        setReferralList(rList.referrals || []);
      })
      .catch(()=>{})
      .finally(()=>setReferralLoading(false));
  },[participant?.id, data?.referralEligible, participant?.referralEligible]); // eslint-disable-line

  useEffect(()=>{
    if(!participant?.id) return;
    setPayoutLoading(true);
    Promise.all([
      apiFetch(`${API_BASE}/api/participants/${participant.id}/payment-details`).then(r=>r.json()),
      apiFetch(`${API_BASE}/api/participants/${participant.id}/payouts`).then(r=>r.json()),
    ])
      .then(([pdData, pData])=>{
        setPaymentDetail(pdData.paymentDetail || null);
        setPayouts(pData.payouts || []);
      })
      .catch(()=>{})
      .finally(()=>setPayoutLoading(false));
  },[participant?.id]); // eslint-disable-line

  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(""),2500); };

  const startEdit = (section, initial) => { setEditing(section); setDraft(initial); };
  const cancelEdit = () => { setEditing(null); setDraft({}); };

  const saveSection = async (body) => {
    setSaving(true);
    try {
      const r = await apiFetch(`${API_BASE}/api/participants/${participant.id}`, {
        method:"PATCH", headers:{"Content-Type":"application/json"}, body:JSON.stringify(body),
      });
      if (!r.ok) throw new Error("Save failed");
      // Optimistically merge into local state
      setData(prev => {
        const next = { ...prev };
        if (body.name !== undefined)          { next.name = body.name; onNameChange?.(body.name); }
        if (body.mobile !== undefined)        next.mobile = body.mobile;
        if (body.marketingOptIn !== undefined) next.marketingOptIn = body.marketingOptIn;
        if (body.demographics) next.demographics = { ...next.demographics, ...body.demographics };
        if (body.profile)      next.profile      = { ...next.profile,      ...body.profile      };
        return next;
      });
      setEditing(null); setDraft({});
      showToast("Saved!");
    } catch { showToast("Save failed — please try again."); }
    setSaving(false);
  };

  const UPI_RE = /^[\w.\-]{2,256}@[a-zA-Z]{2,64}$/;

  const saveUpi = async () => {
    if (!UPI_RE.test(upiDraft)) { showToast("Invalid UPI ID format (e.g. name@okicici)"); return; }
    setSavingUpi(true);
    try {
      const method = paymentDetail ? "PATCH" : "POST";
      const r = await apiFetch(`${API_BASE}/api/participants/${participant.id}/payment-details`, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ upiId: upiDraft, consentGiven: true }),
      });
      const d = await r.json();
      if (!r.ok) { showToast(d.error || "Failed to save"); setSavingUpi(false); return; }
      setPaymentDetail(d.paymentDetail);
      setEditingUpi(false); setUpiDraft(""); setUpiConsentDraft(false);
      showToast("UPI ID saved!");
    } catch { showToast("Failed to save — please try again."); }
    setSavingUpi(false);
  };

  const submitVerify = async () => {
    if (!verifyAmount || !verifyTxnId) { setVerifyError("Please enter both amount and transaction ID."); return; }
    setVerifying(true); setVerifyError("");
    try {
      const r = await apiFetch(`${API_BASE}/api/participants/${participant.id}/payment-details/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enteredAmount: parseFloat(verifyAmount), enteredTxnId: verifyTxnId }),
      });
      const d = await r.json();
      if (d.ok) {
        setPaymentDetail(prev => ({ ...prev, upiVerificationStatus: "verified", upiVerified: true }));
        setVerifyOpen(false); setVerifyAmount(""); setVerifyTxnId("");
        showToast("UPI verified successfully!");
      } else {
        setPaymentDetail(prev => ({ ...prev, upiVerificationStatus: d.upiVerificationStatus, verificationAttempts: (prev?.verificationAttempts || 0) + 1 }));
        setVerifyError(d.error || "Incorrect details.");
      }
    } catch { setVerifyError("Verification failed — please try again."); }
    setVerifying(false);
  };

  const inp = (extra={}) => ({ width:"100%", background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 12px", color:C.text, fontSize:13, fontFamily:"inherit", boxSizing:"border-box", ...extra });
  const lbl = { display:"block", color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", fontWeight:700, marginBottom:6 };

  const revokeConsent = async (record) => {
    setRevoking(record.id);
    try {
      const body = { consentType: record.consentType };
      if (record.surveyId) body.surveyId = record.surveyId;
      const r = await apiFetch(`${API_BASE}/api/participants/${participant.id}/consent/revoke`, {
        method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(body),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || "Failed"); }
      setConsents(prev => prev.map(c => c.id === record.id ? { ...c, revokedAt: new Date().toISOString() } : c));
      showToast("Consent withdrawn.");
    } catch (e) { showToast(e.message || "Could not withdraw consent. Please try again."); }
    setRevoking(null);
    setRevokeConfirm(null);
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const r = await apiFetch(`${API_BASE}/api/participants/${participant.id}/export`);
      if (!r.ok) throw new Error("Export failed");
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `rikai-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { showToast("Export failed — please try again."); }
    setExporting(false);
  };

  const deleteAccount = async () => {
    if (deleteInput.trim().toUpperCase() !== "DELETE") return;
    setDeleting(true);
    try {
      const r = await apiFetch(`${API_BASE}/api/participants/${participant.id}`, { method:"DELETE" });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || "Deletion failed"); }
      onSignOut?.();
    } catch (e) { showToast(e.message || "Deletion failed — please try again."); setDeleting(false); }
  };

  if (loading) return <div style={{ textAlign:"center", color:C.muted, paddingTop:80 }}>Loading profile…</div>;
  if (error)   return <div style={{ textAlign:"center", color:C.red,  paddingTop:80 }}>{error}</div>;

  const dem = data?.demographics || {};
  const pro = data?.profile      || {};

  // Compute age display
  const ageDisplay = dem.age ? `${dem.age} yrs (${dem.ageBand||""})` : "—";

  return (
    <div style={{ maxWidth:700, margin:"0 auto", animation:"fadeUp 0.3s ease" }}>
      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)", background:C.teal, color:C.bg, padding:"10px 22px", borderRadius:10, fontWeight:700, fontSize:13, zIndex:999, boxShadow:`0 4px 20px ${C.tealGlow}` }}>
          {toast}
        </div>
      )}

      {/* Page header */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:28 }}>
        <Btn variant="ghost" size="sm" onClick={onBack}>← Back</Btn>
        <div style={{ width:46, height:46, borderRadius:"50%", background:`${C.blue}20`, border:`1px solid ${C.blue}50`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, fontWeight:700, color:C.blue }}>
          {data?.name?.[0]||"?"}
        </div>
        <div>
          <div style={{ fontWeight:700, fontSize:18, color:C.text }}>{data?.name}</div>
          <div style={{ color:C.muted, fontSize:12 }}>{data?.email}</div>
        </div>
      </div>

      {/* ── ACCOUNT ── */}
      <ProfileSection icon="👤" title="Account" editing={editing==="account"}
        onEdit={()=>startEdit("account",{ name:data?.name||"", mobile:data?.mobile||"" })}
        onSave={()=>saveSection({ name:draft.name, mobile:draft.mobile })}
        onCancel={cancelEdit} saving={saving}>
        {editing==="account" ? (
          <>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Full name</label>
              <input value={draft.name||""} onChange={e=>setDraft(d=>({...d,name:e.target.value}))} style={inp()} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Mobile</label>
              <input value={draft.mobile||""} onChange={e=>setDraft(d=>({...d,mobile:e.target.value.replace(/\D/g,"").slice(0,10)}))} placeholder="10-digit mobile" style={inp()} />
            </div>
            <div style={{ marginBottom:4 }}>
              <label style={lbl}>Email (cannot be changed)</label>
              <input value={data?.email||""} disabled style={inp({ opacity:0.45, cursor:"not-allowed" })} />
            </div>
          </>
        ) : (
          <div className="profile-2col">
            <ProfileField label="Full name" value={data?.name} />
            <ProfileField label="Mobile"    value={data?.mobile} />
            <ProfileField label="Email"     value={data?.email} />
          </div>
        )}
      </ProfileSection>

      {/* ── DEMOGRAPHICS ── */}
      <ProfileSection icon="📍" title="Demographics" editing={editing==="demographics"}
        onEdit={()=>startEdit("demographics",{
          firstName: dem.firstName||"", lastName:  dem.lastName||"",
          dob: dem.dob||"", gender: dem.gender||"", city: dem.city||"", language: dem.language||"",
        })}
        onSave={()=>{
          const found = INDIA_CITIES_PROFILE.find(c=>c.name===draft.city);
          const cityTier = found ? `Tier ${found.tier}` : "";
          const dob = draft.dob;
          let age=null, ageBand=null;
          if(dob){ const y=Math.floor((Date.now()-new Date(dob))/(365.25*24*3600*1000)); age=y; ageBand=y<18?"Under 18":y<25?"18–24":y<35?"25–34":y<45?"35–44":y<55?"45–54":"55+"; }
          saveSection({ demographics:{ ...draft, cityTier, age, ageBand } });
        }}
        onCancel={cancelEdit} saving={saving}>
        {editing==="demographics" ? (
          <>
            <div className="profile-2col-narrow">
              <div style={{ marginBottom:14 }}>
                <label style={lbl}>First name</label>
                <input value={draft.firstName||""} onChange={e=>setDraft(d=>({...d,firstName:e.target.value}))} style={inp()} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={lbl}>Last name</label>
                <input value={draft.lastName||""} onChange={e=>setDraft(d=>({...d,lastName:e.target.value}))} style={inp()} />
              </div>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Date of birth</label>
              <input type="date" value={draft.dob||""} onChange={e=>setDraft(d=>({...d,dob:e.target.value}))} style={inp({ colorScheme:"dark" })} />
            </div>
            <ProfileSelect label="Gender"   value={draft.gender}   options={PROFILE_OPTIONS.gender}   onChange={v=>setDraft(d=>({...d,gender:v}))} />
            <ProfileSelect label="City"     value={draft.city}     options={INDIA_CITIES_PROFILE.map(c=>c.name)} onChange={v=>setDraft(d=>({...d,city:v}))} />
            <ProfileSelect label="Preferred Language" value={draft.language} options={supportedLanguages} onChange={v=>setDraft(d=>({...d,language:v}))}
              help="For surveys that offer more than one language, your interview — the questions you hear and how you answer — will be in this language. Surveys that are English-only are always conducted in English." />
          </>
        ) : (
          <div className="profile-2col">
            <ProfileField label="First name" value={dem.firstName} />
            <ProfileField label="Last name"  value={dem.lastName} />
            <ProfileField label="Date of birth" value={dem.dob} />
            <ProfileField label="Age"        value={ageDisplay} />
            <ProfileField label="Gender"     value={dem.gender} />
            <ProfileField label="City"       value={dem.city} />
            <ProfileField label="Preferred Language"   value={dem.language} />
          </div>
        )}
      </ProfileSection>

      {/* ── EDUCATION & PROFESSION ── */}
      <ProfileSection icon="🎓" title="Education & Profession" editing={editing==="edu"}
        onEdit={()=>startEdit("edu",{ education:pro.education||"", profession:pro.profession||"", yearsWorking:pro.yearsWorking||"" })}
        onSave={()=>saveSection({ profile:{ education:draft.education, profession:draft.profession, yearsWorking:draft.yearsWorking } })}
        onCancel={cancelEdit} saving={saving}>
        {editing==="edu" ? (
          <>
            <ProfileSelect label="Highest education level"  value={draft.education}    options={PROFILE_OPTIONS.education}    onChange={v=>setDraft(d=>({...d,education:v}))} />
            <ProfileSelect label="What best describes you?" value={draft.profession}   options={PROFILE_OPTIONS.profession}   onChange={v=>setDraft(d=>({...d,profession:v}))} />
            <ProfileSelect label="Years of work experience" value={draft.yearsWorking} options={PROFILE_OPTIONS.yearsWorking} onChange={v=>setDraft(d=>({...d,yearsWorking:v}))} />
          </>
        ) : (
          <div className="profile-2col">
            <ProfileField label="Education"       value={pro.education} />
            <ProfileField label="Profession"      value={pro.profession} />
            <ProfileField label="Work experience" value={pro.yearsWorking} />
          </div>
        )}
      </ProfileSection>

      {/* ── FINANCIAL PROFILE ── */}
      <ProfileSection icon="💰" title="Financial Profile" editing={editing==="finance"}
        onEdit={()=>startEdit("finance",{ incomeBand:pro.incomeBand||"", householdRole:pro.householdRole||"", dependents:pro.dependents||"" })}
        onSave={()=>saveSection({ profile:{ incomeBand:draft.incomeBand, householdRole:draft.householdRole, dependents:draft.dependents } })}
        onCancel={cancelEdit} saving={saving}>
        {editing==="finance" ? (
          <>
            <ProfileSelect label="Annual household income"                  value={draft.incomeBand}    options={PROFILE_OPTIONS.incomeBand}    onChange={v=>setDraft(d=>({...d,incomeBand:v}))} />
            <ProfileSelect label="Role in household purchase decisions"     value={draft.householdRole} options={PROFILE_OPTIONS.householdRole} onChange={v=>setDraft(d=>({...d,householdRole:v}))} />
            <ProfileSelect label="Number of dependents"                     value={draft.dependents}    options={PROFILE_OPTIONS.dependents}    onChange={v=>setDraft(d=>({...d,dependents:v}))} />
          </>
        ) : (
          <div className="profile-2col">
            <ProfileField label="Income band"     value={pro.incomeBand} />
            <ProfileField label="Household role"  value={pro.householdRole} />
            <ProfileField label="Dependents"      value={pro.dependents} />
          </div>
        )}
      </ProfileSection>

      {/* ── SHOPPING BEHAVIOUR ── */}
      <ProfileSection icon="🛒" title="Shopping Behaviour" editing={editing==="shopping"}
        onEdit={()=>startEdit("shopping",{ onlineFreq:pro.onlineFreq||"", avgSpend:pro.avgSpend||"", primaryChannel:pro.primaryChannel||[] })}
        onSave={()=>saveSection({ profile:{ onlineFreq:draft.onlineFreq, avgSpend:draft.avgSpend, primaryChannel:draft.primaryChannel } })}
        onCancel={cancelEdit} saving={saving}>
        {editing==="shopping" ? (
          <>
            <ProfileSelect label="How often do you shop online?" value={draft.onlineFreq}  options={PROFILE_OPTIONS.onlineFreq}  onChange={v=>setDraft(d=>({...d,onlineFreq:v}))} />
            <ProfileSelect label="Avg. monthly online spend"     value={draft.avgSpend}    options={PROFILE_OPTIONS.avgSpend}    onChange={v=>setDraft(d=>({...d,avgSpend:v}))} />
            <ProfileMulti  label="Where do you mostly shop?"     value={draft.primaryChannel} options={PROFILE_OPTIONS.primaryChannel} onChange={v=>setDraft(d=>({...d,primaryChannel:v}))} />
          </>
        ) : (
          <div className="profile-2col">
            <ProfileField label="Online shopping freq." value={pro.onlineFreq} />
            <ProfileField label="Avg. monthly spend"    value={pro.avgSpend} />
            <ProfileField label="Channels"              value={pro.primaryChannel} />
          </div>
        )}
      </ProfileSection>

      {/* ── DIGITAL FOOTPRINT ── */}
      <ProfileSection icon="📱" title="Digital Footprint" editing={editing==="digital"}
        onEdit={()=>startEdit("digital",{ deviceUsage:pro.deviceUsage||"", internetHours:pro.internetHours||"", socialMedia:pro.socialMedia||[], contentType:pro.contentType||[] })}
        onSave={()=>saveSection({ profile:{ deviceUsage:draft.deviceUsage, internetHours:draft.internetHours, socialMedia:draft.socialMedia, contentType:draft.contentType } })}
        onCancel={cancelEdit} saving={saving}>
        {editing==="digital" ? (
          <>
            <ProfileSelect label="Primary device(s)"         value={draft.deviceUsage}   options={PROFILE_OPTIONS.deviceUsage}   onChange={v=>setDraft(d=>({...d,deviceUsage:v}))} />
            <ProfileSelect label="Daily internet usage"      value={draft.internetHours} options={PROFILE_OPTIONS.internetHours} onChange={v=>setDraft(d=>({...d,internetHours:v}))} />
            <ProfileMulti  label="Social platforms"          value={draft.socialMedia}   options={PROFILE_OPTIONS.socialMedia}   onChange={v=>setDraft(d=>({...d,socialMedia:v}))} />
            <ProfileMulti  label="Content you consume most"  value={draft.contentType}   options={PROFILE_OPTIONS.contentType}   onChange={v=>setDraft(d=>({...d,contentType:v}))} />
          </>
        ) : (
          <div className="profile-2col">
            <ProfileField label="Device usage"      value={pro.deviceUsage} />
            <ProfileField label="Internet hours"    value={pro.internetHours} />
            <ProfileField label="Social platforms"  value={pro.socialMedia} />
            <ProfileField label="Content consumed"  value={pro.contentType} />
          </div>
        )}
      </ProfileSection>

      {/* ── INDUSTRY AWARENESS ── */}
      <ProfileSection icon="🏭" title="Industry Awareness" editing={editing==="industry"}
        onEdit={()=>startEdit("industry",{ industries:pro.industries||[], brandAwareness:pro.brandAwareness||"" })}
        onSave={()=>saveSection({ profile:{ industries:draft.industries, brandAwareness:draft.brandAwareness } })}
        onCancel={cancelEdit} saving={saving}>
        {editing==="industry" ? (
          <>
            <ProfileMulti  label="Industries you follow or work in"   value={draft.industries}     options={PROFILE_OPTIONS.industries}     onChange={v=>setDraft(d=>({...d,industries:v}))} />
            <ProfileSelect label="How closely do you follow brand news?" value={draft.brandAwareness} options={PROFILE_OPTIONS.brandAwareness} onChange={v=>setDraft(d=>({...d,brandAwareness:v}))} />
          </>
        ) : (
          <div className="profile-2col">
            <ProfileField label="Industries"       value={pro.industries} />
            <ProfileField label="Brand awareness"  value={pro.brandAwareness} />
          </div>
        )}
      </ProfileSection>

      {/* ── PAYOUT DETAILS ── */}
      {(()=>{
        const vStatus = paymentDetail?.upiVerificationStatus || null;
        // payoutSectionRef attached below to the outer wrapper div
        const statusBadge = (s) => {
          const map = {
            unverified:           { label:"Pending Verification", bg:`${C.amber}20`,   color:C.amber },
            pending_verification: { label:"Verify Now",           bg:`${C.amber}20`,          color:C.amber },
            verified:             { label:"Verified ✓",           bg:`${C.teal}20`,    color:C.teal },
            verification_failed:  { label:"Verification Failed",  bg:`${C.danger||"#FF4757"}20`, color:C.danger||"#FF4757" },
          };
          const m = map[s] || map.unverified;
          return <span style={{ background:m.bg, color:m.color, fontSize:11, fontWeight:700, padding:"3px 8px", borderRadius:6 }}>{m.label}</span>;
        };

        return (
          <div ref={payoutSectionRef} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:24, marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, gap:8, flexWrap:"wrap" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:20 }}>💸</span>
                <div style={{ fontWeight:700, color:C.text, fontSize:15 }}>Payout Details</div>
              </div>
              {!payoutLoading && !editingUpi && paymentDetail && (
                <button onClick={()=>{ setUpiDraft(""); setEditingUpi(true); }}
                  style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:8, padding:"6px 14px", fontSize:12, color:C.muted, cursor:"pointer", fontFamily:"inherit" }}>
                  Update
                </button>
              )}
            </div>

            {payoutLoading ? (
              <div style={{ color:C.muted, fontSize:13 }}>Loading…</div>
            ) : !paymentDetail ? (
              // CREATE: no UPI saved yet — form always visible (no toggle needed)
              <div style={{ maxWidth:400 }}>
                <label style={lbl}>UPI ID</label>
                <input value={upiDraft} onChange={e=>setUpiDraft(e.target.value)}
                  placeholder="name@okicici" style={inp({ marginBottom:12 })} />
                <label style={{ display:"flex", alignItems:"flex-start", gap:8, marginBottom:14, cursor:"pointer" }}>
                  <input type="checkbox" checked={upiConsentDraft} onChange={e=>setUpiConsentDraft(e.target.checked)}
                    style={{ marginTop:2, accentColor:C.teal, flexShrink:0 }} />
                  <span style={{ color:C.muted, fontSize:12, lineHeight:1.5 }}>
                    I consent to Rik.ai storing my UPI ID for payout processing. This information is encrypted and never shared with third parties.
                  </span>
                </label>
                {upiConsentDraft && (
                  <button onClick={saveUpi} disabled={savingUpi || !upiDraft}
                    style={{ background:C.teal, color:"#fff", border:"none", borderRadius:8, padding:"9px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", opacity:savingUpi||!upiDraft?0.6:1 }}>
                    {savingUpi ? "Saving…" : "Save"}
                  </button>
                )}
              </div>
            ) : editingUpi ? (
              // UPDATE: editing existing UPI — controlled by editingUpi toggle
              <div style={{ maxWidth:400 }}>
                <label style={lbl}>UPI ID</label>
                <input value={upiDraft} onChange={e=>setUpiDraft(e.target.value)}
                  placeholder="name@okicici" style={inp({ marginBottom:12 })} />
                <div style={{ display:"flex", gap:10 }}>
                  <button onClick={saveUpi} disabled={savingUpi || !upiDraft}
                    style={{ background:C.teal, color:"#fff", border:"none", borderRadius:8, padding:"9px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", opacity:savingUpi||!upiDraft?0.6:1 }}>
                    {savingUpi ? "Saving…" : "Save"}
                  </button>
                  <button onClick={()=>{ setEditingUpi(false); setUpiDraft(""); }}
                    style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:8, padding:"9px 16px", fontSize:13, color:C.muted, cursor:"pointer", fontFamily:"inherit" }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, flexWrap:"wrap" }}>
                  <span style={{ color:C.muted, fontSize:12 }}>UPI ID:</span>
                  <span style={{ color:C.text, fontWeight:600, fontSize:13 }}>{paymentDetail.maskedUpiId}</span>
                  {statusBadge(vStatus)}
                </div>

                {vStatus === "unverified" && (
                  <div style={{ color:C.muted, fontSize:12, padding:"8px 12px", background:`${C.amber}10`, border:`1px solid ${C.amber}30`, borderRadius:8, marginBottom:12 }}>
                    Admin will initiate verification by sending a small token amount to your UPI ID.
                  </div>
                )}

                {vStatus === "pending_verification" && !verifyOpen && (
                  <div style={{ marginBottom:12 }}>
                    <div style={{ color:C.muted, fontSize:12, marginBottom:10 }}>
                      A small token amount has been sent to your UPI ID. Enter the amount and transaction ID you received to verify.
                    </div>
                    <button onClick={()=>{ setVerifyOpen(true); setVerifyError(""); }}
                      style={{ background:C.blue, color:"#fff", border:"none", borderRadius:8, padding:"9px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                      Verify UPI
                    </button>
                  </div>
                )}

                {vStatus === "pending_verification" && verifyOpen && (
                  <div style={{ maxWidth:400, marginBottom:12 }}>
                    <div style={{ marginBottom:10 }}>
                      <label style={lbl}>Amount received (₹)</label>
                      <input type="number" step="0.01" min="0.01" value={verifyAmount} onChange={e=>setVerifyAmount(e.target.value)}
                        placeholder="e.g. 0.75" style={inp({ marginBottom:10 })} />
                      <label style={lbl}>Transaction ID</label>
                      <input value={verifyTxnId} onChange={e=>setVerifyTxnId(e.target.value)}
                        placeholder="UPI transaction ID" style={inp({ marginBottom:12 })} />
                    </div>
                    {verifyError && (
                      <div style={{ color:C.danger||"#FF4757", fontSize:12, marginBottom:10, padding:"6px 10px", background:`${C.danger||"#FF4757"}15`, borderRadius:6 }}>
                        {verifyError}
                      </div>
                    )}
                    <div style={{ display:"flex", gap:10 }}>
                      <button onClick={submitVerify} disabled={verifying}
                        style={{ background:C.teal, color:"#fff", border:"none", borderRadius:8, padding:"9px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", opacity:verifying?0.6:1 }}>
                        {verifying ? "Verifying…" : "Submit"}
                      </button>
                      <button onClick={()=>{ setVerifyOpen(false); setVerifyError(""); }}
                        style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:8, padding:"9px 16px", fontSize:13, color:C.muted, cursor:"pointer", fontFamily:"inherit" }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {vStatus === "verification_failed" && (
                  <div style={{ color:C.danger||"#FF4757", fontSize:12, padding:"8px 12px", background:`${C.danger||"#FF4757"}10`, border:`1px solid ${C.danger||"#FF4757"}30`, borderRadius:8, marginBottom:12 }}>
                    Verification failed after 3 attempts. Please update your UPI ID or contact support.
                  </div>
                )}
              </div>
            )}

            {/* Payout history */}
            {payouts.length > 0 && (
              <div style={{ marginTop:20 }}>
                <div style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:700, marginBottom:10 }}>Payout History</div>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {payouts.map(p => {
                    const statusMap = {
                      under_review:    { label:"Under Review",   bg:`${C.amber}20`,  color:C.amber },
                      pending_payout:  { label:"Payout Pending", bg:`${C.blue}20`,  color:C.blue },
                      paid:            { label:"Paid",           bg:`${C.teal}20`,   color:C.teal },
                      cancelled:       { label:"Cancelled",      bg:`${C.subtle||C.border}40`, color:C.muted },
                      on_hold:         { label:"On Hold",        bg:`${C.amber}15`,  color:C.amber },
                    };
                    const sm = statusMap[p.status] || statusMap.under_review;
                    return (
                      <div key={p.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, gap:8, flexWrap:"wrap" }}>
                        <div style={{ minWidth:0 }}>
                          <div style={{ color:C.text, fontSize:13, fontWeight:600, marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.surveyTitle}</div>
                          <div style={{ color:C.muted, fontSize:11 }}>{new Date(p.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}</div>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
                          <span style={{ color:C.text, fontWeight:700, fontSize:14 }}>₹{p.amount}</span>
                          <span style={{ background:sm.bg, color:sm.color, fontSize:11, fontWeight:700, padding:"3px 8px", borderRadius:6 }}>{sm.label}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── REFER & EARN ── only shown once participant is referralEligible */}
      {(data?.referralEligible || participant?.referralEligible) && (
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:24, marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
            <span style={{ fontSize:20 }}>🎁</span>
            <div>
              <div style={{ fontWeight:700, color:C.text, fontSize:15 }}>Refer &amp; Earn</div>
              <div style={{ color:C.muted, fontSize:12, marginTop:2 }}>Earn ₹30 when your friend completes their first survey, and ₹20 more when they complete their third.</div>
            </div>
          </div>

          {referralLoading ? (
            <div style={{ color:C.muted, fontSize:13 }}>Loading referral info…</div>
          ) : referralInfo ? (
            <>
              {/* Referral code + share link */}
              <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px", marginBottom:16 }}>
                <div style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:700, marginBottom:8 }}>Your Referral Code</div>
                <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                  <span style={{ fontFamily:"monospace", fontSize:22, fontWeight:800, letterSpacing:"0.12em", color:C.teal }}>{referralInfo.referralCode || "—"}</span>
                  {referralInfo.shareLink && (
                    <button
                      onClick={()=>{
                        navigator.clipboard?.writeText(referralInfo.shareLink).then(()=>{ setCopyDone(true); setTimeout(()=>setCopyDone(false), 2000); });
                      }}
                      style={{ background: copyDone ? `${C.teal}20` : `${C.blue}20`, border:`1px solid ${copyDone ? C.teal : C.blue}50`, borderRadius:8, padding:"6px 14px", color: copyDone ? C.teal : C.blue, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", flexShrink:0 }}>
                      {copyDone ? "Copied!" : "Copy Link"}
                    </button>
                  )}
                </div>
                {referralInfo.shareLink && (
                  <div style={{ color:C.muted, fontSize:11, marginTop:6, wordBreak:"break-all" }}>{referralInfo.shareLink}</div>
                )}
              </div>

              {/* Stats row */}
              {referralInfo.stats && (
                <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:16 }}>
                  {[
                    { label:"Total Referred", value: referralInfo.stats.total },
                    { label:"Qualified",      value: referralInfo.stats.qualified, color: C.teal },
                    { label:"Pending",        value: referralInfo.stats.pending,   color: C.amber },
                    { label:"Total Earned",   value: `₹${referralInfo.stats.totalEarned}`, color: C.teal },
                  ].map(s=>(
                    <div key={s.label} style={{ flex:"1 1 80px", minWidth:80, background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                      <div style={{ fontWeight:800, fontSize:18, color: s.color || C.text }}>{s.value}</div>
                      <div style={{ color:C.muted, fontSize:11, marginTop:2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Referral list */}
              {referralList.length > 0 ? (
                <div>
                  <div style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:700, marginBottom:10 }}>Your Referrals</div>
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {referralList.map(r => {
                      const statusMap = {
                        pending:              { label:"Survey Pending",   bg:`${C.amber}20`,  color:C.amber },
                        milestone_1:          { label:"Reward #1 Earned", bg:`${C.teal}20`,   color:C.teal },
                        milestone_2_rewarded: { label:"Both Rewards Earned", bg:`${C.teal}20`,color:C.teal },
                        expired:              { label:"Expired",          bg:`${C.subtle||C.border}40`, color:C.muted },
                        flagged:              { label:"Under Review",     bg:`${C.amber}15`,  color:C.amber },
                        rejected:             { label:"Rejected",         bg:`${C.red}15`,    color:C.red },
                      };
                      const sm = statusMap[r.status] || statusMap.pending;
                      return (
                        <div key={r.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, gap:8, flexWrap:"wrap" }}>
                          <div style={{ minWidth:0 }}>
                            <div style={{ color:C.text, fontSize:13, fontWeight:600 }}>{r.refereeName}</div>
                            <div style={{ color:C.muted, fontSize:11, marginTop:2 }}>
                              Joined {new Date(r.registeredAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}
                              {r.status === "pending" && r.hasTimer && r.daysLeft != null && (
                                <span style={{ color: r.daysLeft <= 7 ? C.red : C.amber, marginLeft:8 }}>· {r.daysLeft}d left</span>
                              )}
                              {r.status === "pending" && !r.hasTimer && (
                                <span style={{ color:C.muted, marginLeft:8 }}>· Awaiting survey match</span>
                              )}
                            </div>
                          </div>
                          <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
                            {r.rewardEarned > 0 && <span style={{ color:C.teal, fontWeight:700, fontSize:13 }}>+₹{r.rewardEarned}</span>}
                            <span style={{ background:sm.bg, color:sm.color, fontSize:11, fontWeight:700, padding:"3px 8px", borderRadius:6 }}>{sm.label}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div style={{ color:C.muted, fontSize:13, padding:"14px 0", textAlign:"center" }}>
                  No referrals yet. Share your link to start earning!
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* ── PRIVACY & CONSENT ── */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:24, marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
          <span style={{ fontSize:20 }}>🔐</span>
          <div style={{ fontWeight:700, color:C.text, fontSize:15 }}>Privacy &amp; Consent</div>
        </div>

        {/* Platform data consent — always present, non-revocable here */}
        {(()=>{
          const pc = consents.find(c => c.consentType === "platform_data");
          return (
            <div style={{ padding:"12px 14px", borderRadius:10, background:C.surface, border:`1px solid ${C.border}`, marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:10 }}>
                <div>
                  <div style={{ color:C.text, fontSize:13, fontWeight:600, marginBottom:3 }}>Platform data processing</div>
                  <div style={{ color:C.muted, fontSize:12, lineHeight:1.55 }}>
                    Profile, demographics &amp; interview transcripts used for survey matching and market research.
                  </div>
                  {pc && <div style={{ color:C.muted, fontSize:11, marginTop:5 }}>Consented {new Date(pc.grantedAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}</div>}
                </div>
                <span style={{ background:`${C.teal}20`, color:C.teal, fontSize:11, fontWeight:700, padding:"3px 8px", borderRadius:6, flexShrink:0 }}>Active</span>
              </div>
              <div style={{ marginTop:10, padding:"8px 12px", background:`${C.amber}10`, border:`1px solid ${C.amber}30`, borderRadius:8 }}>
                <span style={{ color:C.amber, fontSize:11, lineHeight:1.5 }}>
                  To withdraw this consent you must delete your account. Contact support or use the Delete Account option when available.
                </span>
              </div>
            </div>
          );
        })()}

        {/* Marketing / promotional notifications toggle */}
        {(()=>{
          const optIn = data?.marketingOptIn !== false;
          const handleToggle = async () => {
            const next = !optIn;
            // Optimistic update
            setData(prev => ({ ...prev, marketingOptIn: next }));
            try {
              const r = await apiFetch(`${API_BASE}/api/participants/${participant.id}`, {
                method:"PATCH", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ marketingOptIn: next }),
              });
              if (!r.ok) throw new Error();
              showToast(next ? "Notifications turned on." : "Notifications turned off.");
            } catch {
              // Revert on failure
              setData(prev => ({ ...prev, marketingOptIn: optIn }));
              showToast("Could not update preference. Please try again.");
            }
          };
          return (
            <div style={{ padding:"12px 14px", borderRadius:10, background:C.surface, border:`1px solid ${C.border}`, marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12 }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ color:C.text, fontSize:13, fontWeight:600, marginBottom:3 }}>Survey &amp; earning notifications</div>
                  <div style={{ color:C.muted, fontSize:12, lineHeight:1.55 }}>
                    New surveys, referral rewards and earning opportunities. Does not affect account or payout alerts.
                  </div>
                  {!optIn && (
                    <div style={{ color:C.amber, fontSize:11, marginTop:5, lineHeight:1.5 }}>
                      Notifications are off — you may miss new surveys that match your profile.
                    </div>
                  )}
                </div>
                {/* Toggle switch */}
                <button
                  onClick={handleToggle}
                  aria-label={optIn ? "Turn off notifications" : "Turn on notifications"}
                  style={{
                    flexShrink:0, width:44, height:24, borderRadius:12, border:"none", cursor:"pointer",
                    background: optIn ? C.teal : C.subtle, position:"relative", transition:"background 0.2s", padding:0,
                  }}
                >
                  <span style={{
                    display:"block", width:18, height:18, borderRadius:"50%", background:"#fff",
                    position:"absolute", top:3, left: optIn ? 23 : 3, transition:"left 0.2s",
                    boxShadow:"0 1px 3px rgba(0,0,0,0.3)",
                  }} />
                </button>
              </div>
            </div>
          );
        })()}

        {/* Policy links */}
        <div style={{ display:"flex", gap:20, marginBottom:16, paddingBottom:16, borderBottom:`1px solid ${C.border}` }}>
          <a href={LEGAL_PP} target="_blank" rel="noopener"
            style={{ color:C.muted, fontSize:12, textDecoration:"none" }}>
            Privacy Policy ↗
          </a>
          <a href={LEGAL_TOS} target="_blank" rel="noopener"
            style={{ color:C.muted, fontSize:12, textDecoration:"none" }}>
            Terms of Service ↗
          </a>
        </div>

        {/* Facial analysis consents — summary + link to dedicated Privacy page */}
        {(()=>{
          const facial  = consents.filter(c => c.consentType === "facial_analysis");
          const active  = facial.filter(c => !c.revokedAt).length;
          const total   = facial.length;
          return (
            <div style={{ padding:"12px 14px", borderRadius:10, background:C.surface, border:`1px solid ${C.border}` }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
                <div>
                  <div style={{ color:C.text, fontSize:13, fontWeight:600, marginBottom:2 }}>Facial analysis consents</div>
                  <div style={{ color:C.muted, fontSize:12 }}>
                    {total === 0
                      ? "No consents yet — granted when you start a video interview."
                      : `Active for ${active} survey${active !== 1 ? "s" : ""}${total > active ? ` · ${total - active} withdrawn` : ""}`}
                  </div>
                </div>
                {total > 0 && onManageConsents && (
                  <button onClick={onManageConsents}
                    style={{ background:"none", border:`1px solid ${C.purple}50`, borderRadius:6, color:C.purple, fontSize:11, fontWeight:600, padding:"5px 12px", cursor:"pointer", flexShrink:0, fontFamily:"inherit", whiteSpace:"nowrap" }}>
                    Manage →
                  </button>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── DANGER ZONE ── */}
      <div style={{ border:`1px solid ${C.red}40`, borderRadius:14, padding:24, marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
          <span style={{ fontSize:20 }}>⚠️</span>
          <div style={{ fontWeight:700, color:C.red, fontSize:15 }}>Danger Zone</div>
        </div>

        {/* Download My Data */}
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, marginBottom:16, flexWrap:"wrap" }}>
          <div style={{ minWidth:0 }}>
            <div style={{ color:C.text, fontSize:13, fontWeight:600, marginBottom:3 }}>Download my data</div>
            <div style={{ color:C.muted, fontSize:12, lineHeight:1.55 }}>
              Export all personal data we hold about you as a JSON file (DPDP Act § 11).
            </div>
          </div>
          <button onClick={exportData} disabled={exporting}
            style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:8, color:C.text, fontSize:13, fontWeight:600, padding:"8px 16px", cursor:"pointer", fontFamily:"inherit", flexShrink:0, opacity:exporting?0.6:1, whiteSpace:"nowrap" }}>
            {exporting ? "Exporting…" : "Download"}
          </button>
        </div>

        <div style={{ height:1, background:C.border, marginBottom:16 }} />

        {/* Delete Account */}
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
          <div style={{ minWidth:0 }}>
            <div style={{ color:C.red, fontSize:13, fontWeight:600, marginBottom:3 }}>Delete my account</div>
            <div style={{ color:C.muted, fontSize:12, lineHeight:1.55 }}>
              Permanently deletes your account, profile, all interview transcripts and consent records.
              This cannot be undone.
            </div>
          </div>
          {!deleteConfirm && (
            <button onClick={()=>{ setDeleteConfirm(true); setDeleteInput(""); }}
              style={{ background:C.red, border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:600, padding:"8px 16px", cursor:"pointer", fontFamily:"inherit", flexShrink:0, whiteSpace:"nowrap" }}>
              Delete account
            </button>
          )}
        </div>

        {/* Inline confirmation */}
        {deleteConfirm && (
          <div style={{ marginTop:16, padding:"16px", background:`${C.red}0d`, border:`1px solid ${C.red}40`, borderRadius:10 }}>
            <div style={{ color:C.text, fontSize:13, marginBottom:12, lineHeight:1.6 }}>
              This will permanently delete everything — your profile, demographics, interview transcripts, facial analysis scores and all consent records.
              <br/><strong>Aggregate survey reports are not affected</strong> (they contain no personal data).
              <br/><br/>Type <strong>DELETE</strong> below to confirm.
            </div>
            <input
              value={deleteInput}
              onChange={e=>setDeleteInput(e.target.value)}
              placeholder="Type DELETE to confirm"
              autoFocus
              style={{ width:"100%", background:C.surface, border:`1px solid ${C.red}60`, borderRadius:8, padding:"10px 12px", color:C.text, fontSize:13, fontFamily:"inherit", boxSizing:"border-box", marginBottom:12, outline:"none" }}
            />
            <div style={{ display:"flex", gap:8 }}>
              <button
                onClick={deleteAccount}
                disabled={deleting || deleteInput.trim().toUpperCase() !== "DELETE"}
                style={{ background:C.red, border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:600, padding:"8px 18px", cursor:"pointer", fontFamily:"inherit", opacity:(deleting||deleteInput.trim().toUpperCase()!=="DELETE")?0.45:1 }}>
                {deleting ? "Deleting…" : "Permanently delete my account"}
              </button>
              <button onClick={()=>{ setDeleteConfirm(false); setDeleteInput(""); }} disabled={deleting}
                style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:8, color:C.muted, fontSize:13, padding:"8px 16px", cursor:"pointer", fontFamily:"inherit" }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SUPPORT PAGE ─────────────────────────────────────────────────────────────

const FAQ_CATEGORIES = [
  {
    id: "payments",
    title: "Payments & Incentives",
    icon: "💳",
    questions: [
      { q: "When will I receive my incentive?", a: "Incentives are processed within 7–10 business days after your survey session is marked as completed and verified. You'll receive it via the method specified in the survey briefing (Amazon voucher, UPI transfer, etc.)." },
      { q: "I completed a survey but didn't receive payment. What do I do?", a: "First, check that your session status shows 'Completed' on your dashboard. If it does and it has been more than 10 business days, please file a support ticket under the 'Payment Issue' category with the survey name. Our team will investigate within 2 business days." },
      { q: "What payment methods are supported?", a: "Currently supported: Amazon Pay vouchers, UPI transfer, and reward points (method depends on the individual survey). The incentive type is shown on the survey briefing page before you start." },
      { q: "How do I update my payment / UPI details?", a: "Go to your Profile (click your name in the header) and update your contact details there. If a specific payment method requires separate details, please contact us via the Contact tab." },
    ],
  },
  {
    id: "technical",
    title: "Technical Issues",
    icon: "🔧",
    questions: [
      { q: "My camera or microphone isn't working.", a: "Make sure you've granted browser permissions for camera and microphone. In Chrome: click the padlock icon in the address bar → Site settings → allow Camera and Microphone. Then refresh the page. If the issue persists, try a different browser (Chrome or Edge recommended)." },
      { q: "My interview got cut off. Will my progress be saved?", a: "Sessions are saved progressively. If your session was interrupted, return to your dashboard — if the survey shows as 'Pending', you may be able to resume. If it shows as stuck, please file a support ticket with the survey name and approximate time of the interruption." },
      { q: "Which browsers are supported?", a: "Rik.ai works best on Google Chrome (v90+) and Microsoft Edge. Firefox and Safari have partial support. Mobile browsers (Chrome for Android, Safari for iOS) are supported but a quiet environment and stable connection are strongly recommended." },
      { q: "The audio is choppy or I can't hear the AI interviewer.", a: "This is usually a network issue. Try: (1) Move closer to your router or switch to a wired connection. (2) Close other tabs and applications. (3) Refresh the page and rejoin. If the problem continues, try Google TTS mode by lowering your browser's audio quality settings." },
    ],
  },
  {
    id: "privacy",
    title: "Privacy & Data",
    icon: "🔒",
    questions: [
      { q: "What data is collected during my interview?", a: "For video surveys: your spoken responses (transcribed), sentiment analysis, and facial expression data (engagement and positivity scores — not raw video). For voice surveys: transcript and sentiment only. For chat surveys: text transcript only. Raw video is never stored permanently." },
      { q: "Are my camera recordings stored permanently?", a: "No. Video frames captured during your session are used only for facial expression analysis immediately after the interview ends and are not retained beyond that processing window. Your responses are stored as anonymised scores and transcripts only." },
      { q: "How do I delete my account?", a: "Go to your Profile page and use the 'Delete Account' option. Account deletion is scheduled with a 30-day cooling period (in case of accidental deletion). After 30 days, all your personal data is permanently removed from our systems." },
      { q: "Who sees my responses?", a: "Your responses contribute to an anonymised aggregate report seen only by the survey's commissioning company. Individual responses are never shared with third parties and are not linked to your identity in any report." },
    ],
  },
  {
    id: "surveys",
    title: "About Surveys",
    icon: "📋",
    questions: [
      { q: "Why was I invited to this survey?", a: "You were matched based on your profile — demographic details and interests you provided during registration. The more complete your profile, the better the match quality and the more relevant surveys you'll receive." },
      { q: "Can I redo a survey I've already completed?", a: "No. Each survey invitation can only be completed once to maintain data integrity. If you experienced a technical issue during your session, please file a support ticket." },
      { q: "How long do surveys typically take?", a: "Each survey shows an estimated time on the briefing page before you start. Most range from 10–25 minutes. The AI interviewer may ask follow-up questions, so allow a few extra minutes." },
      { q: "What is an AI interviewer? Is it a real person?", a: "No — Rik.ai uses an AI agent (powered by Claude) to conduct the interview. It asks questions, listens to your responses, and asks intelligent follow-ups just like a human researcher would. Your responses are just as valuable as in a traditional interview." },
    ],
  },
];

const STATUS_STYLES = {
  open:      { label:"Open",      bg:"#F5A62320", color:"#F5A623", border:"#F5A62340" },
  in_review: { label:"In Review", bg:`${C.blue}20`, color:C.blue, border:`${C.blue}40` },
  resolved:  { label:"Resolved",  bg:`${C.teal}20`,   color:C.teal,   border:`${C.teal}40` },
};

const TICKET_CATEGORIES = [
  { value:"payment",         label:"Payment / Incentive Issue" },
  { value:"technical",       label:"Technical Problem" },
  { value:"survey_content",  label:"Survey Content Issue" },
  { value:"other",           label:"Other" },
];

const TICKET_PRIORITIES = [
  { value:"low",    label:"Low — general question" },
  { value:"normal", label:"Normal — something isn't working" },
  { value:"high",   label:"High — blocking me from completing a survey" },
  { value:"urgent", label:"Urgent — payment overdue / data concern" },
];

// ── Supplier consent withdrawal page — no login required ─────────────────────
// Rendered at phase="withdraw" when URL contains ?withdrawToken=<token>
function WithdrawPage() {
  const urlParams   = new URLSearchParams(window.location.search);
  const token       = urlParams.get("token") || urlParams.get("withdrawToken");
  const [status,    setStatus]    = useState("loading"); // loading | ready | withdrawn | expired | notfound | error
  const [surveyTitle, setSurveyTitle] = useState("");
  const [grantedAt,   setGrantedAt]   = useState(null);
  const [revokedAt,   setRevokedAt]   = useState(null);
  const [submitting,  setSubmitting]  = useState(false);

  useEffect(() => {
    if (!token) { setStatus("notfound"); return; }
    fetch(`${API_BASE}/api/supplier-consent/status?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) {
          setStatus(d.error.includes("expired") ? "expired" : "notfound");
          return;
        }
        setSurveyTitle(d.surveyTitle || "Video survey");
        setGrantedAt(d.grantedAt);
        setRevokedAt(d.revokedAt || null);
        setStatus(d.revokedAt ? "withdrawn" : "ready");
      })
      .catch(() => setStatus("error"));
  }, [token]);

  const handleWithdraw = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API_BASE}/api/supplier-consent/withdraw?token=${encodeURIComponent(token)}`, { method: "POST" });
      const d = await r.json();
      if (d.ok) { setRevokedAt(d.revokedAt); setStatus("withdrawn"); }
      else setStatus("error");
    } catch { setStatus("error"); }
    setSubmitting(false);
  };

  const containerStyle = { minHeight:"100vh", background:C.bg, color:C.text, fontFamily:"Poppins,sans-serif", display:"flex", alignItems:"center", justifyContent:"center", padding:24 };
  const boxStyle = { background:C.card, border:`1px solid ${C.border}`, borderRadius:16, maxWidth:480, width:"100%", padding:"32px 28px", boxShadow:"0 12px 48px rgba(0,0,0,0.4)" };

  if (status === "loading") return (
    <div style={containerStyle}><div style={boxStyle}><div style={{ color:C.muted, textAlign:"center" }}>Loading…</div></div></div>
  );
  if (status === "notfound") return (
    <div style={containerStyle}><div style={boxStyle}>
      <div style={{ fontSize:32, marginBottom:12 }}>🔍</div>
      <div style={{ fontWeight:700, fontSize:17, marginBottom:8 }}>Link not found</div>
      <div style={{ color:C.muted, fontSize:13, lineHeight:1.6 }}>This withdrawal link is invalid or doesn't exist. If you believe this is an error, contact <a href="mailto:support@rikai.tech" style={{ color:C.teal }}>support@rikai.tech</a>.</div>
    </div></div>
  );
  if (status === "expired") return (
    <div style={containerStyle}><div style={boxStyle}>
      <div style={{ fontSize:32, marginBottom:12 }}>⏰</div>
      <div style={{ fontWeight:700, fontSize:17, marginBottom:8 }}>Withdrawal link expired</div>
      <div style={{ color:C.muted, fontSize:13, lineHeight:1.6 }}>This link is more than 90 days old. To withdraw your consent after the expiry window, contact <a href="mailto:support@rikai.tech" style={{ color:C.teal }}>support@rikai.tech</a> with the survey name.</div>
    </div></div>
  );
  if (status === "error") return (
    <div style={containerStyle}><div style={boxStyle}>
      <div style={{ fontSize:32, marginBottom:12 }}>⚠️</div>
      <div style={{ fontWeight:700, fontSize:17, marginBottom:8 }}>Something went wrong</div>
      <div style={{ color:C.muted, fontSize:13, lineHeight:1.6 }}>Please try again or contact <a href="mailto:support@rikai.tech" style={{ color:C.teal }}>support@rikai.tech</a>.</div>
    </div></div>
  );
  if (status === "withdrawn") return (
    <div style={containerStyle}><div style={boxStyle}>
      <div style={{ fontSize:32, marginBottom:12 }}>✅</div>
      <div style={{ fontWeight:700, fontSize:17, marginBottom:8 }}>Consent withdrawn</div>
      <div style={{ color:C.muted, fontSize:13, lineHeight:1.6 }}>
        Your facial analysis consent for <strong style={{ color:C.text }}>{surveyTitle}</strong> was withdrawn on{" "}
        {revokedAt ? new Date(revokedAt).toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"}) : "now"}.
      </div>
      <div style={{ marginTop:16, padding:"12px 14px", background:`${C.surface}`, border:`1px solid ${C.border}`, borderRadius:10, color:C.muted, fontSize:12, lineHeight:1.6 }}>
        Data already processed and shared in anonymised form with the research company before your withdrawal cannot be recalled. Rik.ai will not use your data for any new analysis.
      </div>
    </div></div>
  );

  // status === "ready"
  return (
    <div style={containerStyle}><div style={boxStyle}>
      <div style={{ fontSize:32, marginBottom:12 }}>🔐</div>
      <div style={{ fontWeight:700, fontSize:18, marginBottom:6 }}>Withdraw facial analysis consent</div>
      <div style={{ color:C.muted, fontSize:13, lineHeight:1.6, marginBottom:20 }}>
        You are withdrawing consent for facial expression analysis performed during your interview for:
      </div>
      <div style={{ padding:"12px 16px", background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, marginBottom:20 }}>
        <div style={{ fontWeight:600, fontSize:14 }}>{surveyTitle}</div>
        {grantedAt && <div style={{ color:C.muted, fontSize:12, marginTop:4 }}>Consent given on {new Date(grantedAt).toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"})}</div>}
      </div>
      <div style={{ padding:"12px 14px", background:`${C.amber}10`, border:`1px solid ${C.amber}30`, borderRadius:10, fontSize:12, color:C.muted, lineHeight:1.6, marginBottom:24 }}>
        <strong style={{ color:C.amber }}>Please note:</strong> Withdrawing consent does not delete data already processed and shared in anonymised form before this withdrawal.
        Under GDPR Article 7(3) and India's DPDP Act 2023 Section 6(3), you have the right to withdraw, but withdrawal is not retroactive.
      </div>
      <Btn variant="danger" onClick={handleWithdraw} disabled={submitting} style={{ width:"100%" }}>
        {submitting ? "Withdrawing…" : "Yes, withdraw my consent"}
      </Btn>
    </div></div>
  );
}

// ── Privacy & Consents page ───────────────────────────────────────────────────
function PrivacyPage({ participant, apiFetch, onBack }) {
  const [consents,     setConsents]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [filter,       setFilter]       = useState("all");   // all | active | revoked
  const [search,       setSearch]       = useState("");
  const [revoking,     setRevoking]     = useState(null);
  const [revokeConfirm, setRevokeConfirm] = useState(null);
  const [toast,        setToast]        = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  useEffect(() => {
    apiFetch(`${API_BASE}/api/participants/${participant.id}/consent?type=facial_analysis`)
      .then(r => r.json())
      .then(d => { if (d.consents) setConsents(d.consents); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [participant.id]);

  const revokeConsent = async (record) => {
    setRevoking(record.id);
    try {
      const r = await apiFetch(`${API_BASE}/api/participants/${participant.id}/consent/revoke`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ consentType: record.consentType, surveyId: record.surveyId }),
      });
      if (!r.ok) throw new Error("Failed");
      setConsents(prev => prev.map(c =>
        c.id === record.id ? { ...c, revokedAt: new Date().toISOString() } : c
      ));
      setRevokeConfirm(null);
      showToast("Consent withdrawn. Past session data is not affected.");
    } catch { showToast("Could not withdraw consent. Please try again."); }
    setRevoking(null);
  };

  const filtered = consents.filter(c => {
    if (filter === "active"  && c.revokedAt)  return false;
    if (filter === "revoked" && !c.revokedAt) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (c.surveyTitle || "").toLowerCase().includes(q) ||
             (c.surveyCategory || "").toLowerCase().includes(q);
    }
    return true;
  });

  const activeCount = consents.filter(c => !c.revokedAt).length;

  return (
    <div style={{ minHeight:"100vh", background:C.bg, color:C.text, fontFamily:"Poppins,sans-serif" }}>
      {toast && (
        <div style={{ position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)", background:C.teal, color:"#000", padding:"10px 22px", borderRadius:30, fontWeight:600, fontSize:13, zIndex:9999, boxShadow:"0 4px 20px rgba(0,0,0,0.4)" }}>
          {toast}
        </div>
      )}
      <div style={{ maxWidth:680, margin:"0 auto", padding:"24px 16px 60px" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:28 }}>
          <button onClick={onBack} style={{ background:"none", border:"none", color:C.muted, cursor:"pointer", fontSize:20, padding:"4px 8px", fontFamily:"inherit" }}>←</button>
          <div>
            <div style={{ fontWeight:700, fontSize:18, color:C.text }}>Privacy &amp; Consents</div>
            <div style={{ color:C.muted, fontSize:12, marginTop:2 }}>
              Manage your facial analysis consent per survey. Withdrawing a consent does not affect past completed sessions.
            </div>
          </div>
        </div>

        {/* Summary strip */}
        <div style={{ display:"flex", gap:10, marginBottom:20, flexWrap:"wrap" }}>
          {[
            { key:"all",     label:`All (${consents.length})` },
            { key:"active",  label:`Active (${activeCount})` },
            { key:"revoked", label:`Withdrawn (${consents.length - activeCount})` },
          ].map(tab => (
            <button key={tab.key} onClick={() => setFilter(tab.key)}
              style={{ background: filter===tab.key ? C.purple : C.surface, color: filter===tab.key ? "#fff" : C.muted,
                border: `1px solid ${filter===tab.key ? C.purple : C.border}`, borderRadius:20, fontSize:12, fontWeight:600,
                padding:"5px 14px", cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s" }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <input
          placeholder="Search by survey name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width:"100%", background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:"9px 14px",
            color:C.text, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box", marginBottom:16 }}
        />

        {/* List */}
        {loading ? (
          <div style={{ color:C.muted, textAlign:"center", padding:40 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ color:C.muted, textAlign:"center", padding:40, fontSize:13 }}>
            {search ? "No matching consents." : filter === "active" ? "No active consents." : filter === "revoked" ? "No withdrawn consents." : "No facial analysis consents yet."}
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {filtered.map(c => {
              const isActive   = !c.revokedAt;
              const isRevoking = revoking === c.id;
              const isPending  = revokeConfirm?.id === c.id;
              return (
                <div key={c.id} style={{ padding:"14px 16px", borderRadius:12, background:C.card, border:`1px solid ${isActive ? C.border : C.subtle}`, opacity:isActive?1:0.65 }}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:10 }}>
                    <div style={{ minWidth:0 }}>
                      <div style={{ color:isActive?C.text:C.muted, fontSize:14, fontWeight:600, marginBottom:3 }}>
                        {c.surveyTitle || "Video survey"}
                        {c.surveyCategory && <span style={{ color:C.muted, fontWeight:400 }}> · {c.surveyCategory}</span>}
                      </div>
                      <div style={{ color:C.muted, fontSize:12 }}>
                        Consented {new Date(c.grantedAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}
                        {c.revokedAt && ` · Withdrawn ${new Date(c.revokedAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}`}
                      </div>
                    </div>
                    <div style={{ flexShrink:0, display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
                      <span style={{ fontSize:11, fontWeight:700, padding:"3px 8px", borderRadius:6,
                        background: isActive ? `${C.teal}20` : `${C.muted}15`,
                        color: isActive ? C.teal : C.muted }}>
                        {isActive ? "Active" : "Withdrawn"}
                      </span>
                      {isActive && !isPending && (
                        <button onClick={() => setRevokeConfirm(c)} disabled={!!revoking}
                          style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:6, color:C.muted, fontSize:11, padding:"4px 10px", cursor:"pointer", fontFamily:"inherit" }}>
                          Withdraw
                        </button>
                      )}
                    </div>
                  </div>
                  {isPending && (
                    <div style={{ marginTop:12, padding:"10px 12px", background:`${C.red}10`, border:`1px solid ${C.red}30`, borderRadius:8 }}>
                      <div style={{ color:C.text, fontSize:12, marginBottom:8, lineHeight:1.5 }}>
                        Withdraw facial analysis consent for this survey?
                        Future sessions will not include facial expression analysis.
                        <br/><span style={{ color:C.muted }}>Past completed sessions are not affected.</span>
                      </div>
                      <div style={{ display:"flex", gap:8 }}>
                        <button onClick={() => revokeConsent(c)} disabled={isRevoking}
                          style={{ background:C.red, border:"none", borderRadius:6, color:"#fff", fontSize:12, fontWeight:600, padding:"6px 14px", cursor:"pointer", fontFamily:"inherit", opacity:isRevoking?0.6:1 }}>
                          {isRevoking ? "Withdrawing…" : "Yes, withdraw"}
                        </button>
                        <button onClick={() => setRevokeConfirm(null)} disabled={isRevoking}
                          style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:6, color:C.muted, fontSize:12, padding:"6px 14px", cursor:"pointer", fontFamily:"inherit" }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Regulatory note */}
        <div style={{ marginTop:28, padding:"12px 16px", background:C.surface, border:`1px solid ${C.border}`, borderRadius:10 }}>
          <div style={{ color:C.muted, fontSize:11, lineHeight:1.7 }}>
            Under GDPR Article 7 and India's DPDP Act 2023, you have the right to withdraw consent at any time.
            Withdrawal does not affect the lawfulness of processing that took place before withdrawal.
            Consent records are retained for compliance and audit purposes.
          </div>
        </div>
      </div>
    </div>
  );
}

function SupportPage({ participant, apiFetch, onBack }) {
  const [tab, setTab]           = useState("faq");         // faq | report | contact
  const [openFaq, setOpenFaq]   = useState(new Set());     // Set of open question ids

  // Report tab state
  const [tickets, setTickets]   = useState(null);          // null = loading
  const [tForm, setTForm]       = useState({ category:"", priority:"normal", subject:"", description:"", surveyName:"" });
  const [tSubmitting, setTSubmitting] = useState(false);
  const [tError, setTError]     = useState("");
  const [tToast, setTToast]     = useState("");

  // Contact tab state
  const [cForm, setCForm]       = useState({ name: participant?.name||"", email: participant?.email||"", message:"" });
  const [cSubmitting, setCSubmitting] = useState(false);
  const [cError, setCError]     = useState("");
  const [cToast, setCToast]     = useState("");

  // Load tickets when Report tab is opened
  useEffect(() => {
    if (tab === "report" && tickets === null && participant?.id) {
      apiFetch(`${API_BASE}/api/participants/${participant.id}/tickets`)
        .then(r => r.json())
        .then(d => setTickets(d.tickets || []))
        .catch(() => setTickets([]));
    }
  }, [tab, tickets, participant, apiFetch]);

  function toggleFaq(id) {
    setOpenFaq(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function submitTicket(e) {
    e.preventDefault();
    setTError("");
    if (!tForm.category) { setTError("Please select a category."); return; }
    setTSubmitting(true);
    try {
      const r = await apiFetch(`${API_BASE}/api/participants/${participant.id}/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(tForm),
      });
      const d = await r.json();
      if (!r.ok) { setTError(d.error || "Failed to submit ticket."); return; }
      setTToast("Ticket submitted. We'll be in touch soon.");
      setTimeout(() => setTToast(""), 4000);
      setTForm({ category:"", priority:"normal", subject:"", description:"", surveyName:"" });
      setTickets(null); // force reload
    } catch { setTError("Network error. Please try again."); }
    finally { setTSubmitting(false); }
  }

  async function submitContact(e) {
    e.preventDefault();
    setCError("");
    setCSubmitting(true);
    try {
      const r = await apiFetch(`${API_BASE}/api/support/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cForm),
      });
      const d = await r.json();
      if (!r.ok) { setCError(d.error || "Failed to send message."); return; }
      setCToast("Message sent! We'll get back to you soon.");
      setTimeout(() => setCToast(""), 4000);
      setCForm(f => ({ ...f, message:"" }));
    } catch { setCError("Network error. Please try again."); }
    finally { setCSubmitting(false); }
  }

  const inputStyle = { width:"100%", background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 14px", color:C.text, fontSize:14, outline:"none", boxSizing:"border-box" };
  const labelStyle = { fontSize:12, color:C.muted, fontWeight:600, letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:6, display:"block" };

  return (
    <div style={{ maxWidth:740, margin:"0 auto", padding:"28px 16px 60px" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:28 }}>
        <button onClick={onBack} style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:8, width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:C.muted, flexShrink:0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div>
          <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:C.text }}>Help &amp; Support</h1>
          <p style={{ margin:"2px 0 0", fontSize:13, color:C.muted }}>FAQs, issue reporting, and contact</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:24 }}>
        {[
          { id:"faq",     label:"FAQ" },
          { id:"report",  label:"Report an Issue" },
          { id:"contact", label:"Contact Us" },
        ].map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{ padding:"10px 4px", borderRadius:8, border:`1px solid ${tab===t.id?C.blue:C.border}`, background:tab===t.id?`${C.blue}18`:"transparent", color:tab===t.id?C.blue:C.muted, fontWeight:700, fontSize:13, cursor:"pointer", transition:"all 0.15s" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── FAQ tab ─────────────────────────────────────────────────────── */}
      {tab === "faq" && (
        <div>
          {FAQ_CATEGORIES.map(cat => (
            <div key={cat.id} style={{ marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, padding:"12px 0 10px", borderBottom:`1px solid ${C.border}`, marginBottom:4 }}>
                <span style={{ fontSize:18 }}>{cat.icon}</span>
                <span style={{ fontWeight:700, fontSize:15, color:C.text }}>{cat.title}</span>
              </div>
              {cat.questions.map((item, idx) => {
                const id = `${cat.id}-${idx}`;
                const isOpen = openFaq.has(id);
                return (
                  <div key={id} style={{ borderBottom:`1px solid ${C.border}20` }}>
                    <button onClick={()=>toggleFaq(id)} style={{ width:"100%", textAlign:"left", background:"transparent", border:"none", padding:"13px 4px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
                      <span style={{ fontSize:14, color:isOpen?C.teal:C.text, fontWeight:isOpen?600:400 }}>{item.q}</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isOpen?C.teal:C.muted} strokeWidth="2.5" style={{ flexShrink:0, transform:isOpen?"rotate(180deg)":"none", transition:"transform 0.2s" }}><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    {isOpen && (
                      <div style={{ padding:"0 4px 14px", fontSize:13, color:C.muted, lineHeight:1.65 }}>{item.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          <p style={{ marginTop:20, fontSize:12, color:C.muted, textAlign:"center" }}>
            Can't find an answer?{" "}
            <button onClick={()=>setTab("report")} style={{ background:"none", border:"none", color:C.teal, cursor:"pointer", fontSize:12, fontWeight:600, padding:0 }}>Report an issue</button>
            {" "}or{" "}
            <button onClick={()=>setTab("contact")} style={{ background:"none", border:"none", color:C.teal, cursor:"pointer", fontSize:12, fontWeight:600, padding:0 }}>contact us</button>.
          </p>
        </div>
      )}

      {/* ── Report an Issue tab ─────────────────────────────────────────── */}
      {tab === "report" && (
        <div>
          {tToast && <div style={{ background:C.tealGlow, border:`1px solid ${C.teal}`, borderRadius:8, padding:"10px 16px", marginBottom:20, fontSize:13, color:C.teal }}>{tToast}</div>}

          <form onSubmit={submitTicket} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20, marginBottom:24 }}>
            <h3 style={{ margin:"0 0 18px", fontSize:15, fontWeight:700, color:C.text }}>Submit a Support Ticket</h3>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
              <div>
                <label style={labelStyle}>Category *</label>
                <select value={tForm.category} onChange={e=>setTForm(f=>({...f,category:e.target.value}))} style={{ ...inputStyle, appearance:"none" }} required>
                  <option value="">Select category…</option>
                  {TICKET_CATEGORIES.map(c=><option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Priority</label>
                <select value={tForm.priority} onChange={e=>setTForm(f=>({...f,priority:e.target.value}))} style={{ ...inputStyle, appearance:"none" }}>
                  {TICKET_PRIORITIES.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={labelStyle}>Subject *</label>
              <input value={tForm.subject} onChange={e=>setTForm(f=>({...f,subject:e.target.value}))} placeholder="Brief summary of the issue" style={inputStyle} maxLength={150} required />
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={labelStyle}>Survey name <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0 }}>(optional)</span></label>
              <input value={tForm.surveyName} onChange={e=>setTForm(f=>({...f,surveyName:e.target.value}))} placeholder="e.g. Brand Awareness Study" style={inputStyle} maxLength={200} />
            </div>

            <div style={{ marginBottom:18 }}>
              <label style={labelStyle}>Description * <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0, color:C.muted }}>— min 20 characters</span></label>
              <textarea value={tForm.description} onChange={e=>setTForm(f=>({...f,description:e.target.value}))} placeholder="Describe the issue in detail. Include any error messages you saw, what you were doing when it happened, and your device/browser." style={{ ...inputStyle, minHeight:110, resize:"vertical", fontFamily:"inherit" }} maxLength={2000} required />
            </div>

            {tError && <p style={{ color:C.red, fontSize:13, margin:"0 0 14px" }}>{tError}</p>}
            <Btn type="submit" variant="primary" disabled={tSubmitting}>{tSubmitting?"Submitting…":"Submit Ticket"}</Btn>
          </form>

          {/* Past tickets */}
          <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:12 }}>Your Tickets</h3>
          {tickets === null ? (
            <p style={{ color:C.muted, fontSize:13 }}>Loading…</p>
          ) : tickets.length === 0 ? (
            <p style={{ color:C.muted, fontSize:13 }}>No tickets yet.</p>
          ) : tickets.map(t => {
            const st = STATUS_STYLES[t.status] || STATUS_STYLES.open;
            return (
              <div key={t.id} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px", marginBottom:10 }}>
                <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:14, fontWeight:600, color:C.text }}>{t.subject}</span>
                  <span style={{ fontSize:11, fontWeight:700, background:st.bg, color:st.color, border:`1px solid ${st.border}`, borderRadius:999, padding:"2px 10px", flexShrink:0 }}>{st.label}</span>
                </div>
                <div style={{ fontSize:12, color:C.muted, marginBottom:t.resolutionNote?8:0 }}>
                  {TICKET_CATEGORIES.find(c=>c.value===t.category)?.label||t.category}
                  {t.surveyName ? ` · ${t.surveyName}` : ""}
                  {" · "}{new Date(t.createdAt).toLocaleDateString()}
                </div>
                {t.resolutionNote && (
                  <div style={{ marginTop:8, padding:"8px 12px", background:`${C.teal}10`, border:`1px solid ${C.teal}30`, borderRadius:6, fontSize:12, color:C.teal }}>
                    <strong>Resolution:</strong> {t.resolutionNote}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Contact Us tab ──────────────────────────────────────────────── */}
      {tab === "contact" && (
        <div>
          {cToast && <div style={{ background:C.tealGlow, border:`1px solid ${C.teal}`, borderRadius:8, padding:"10px 16px", marginBottom:20, fontSize:13, color:C.teal }}>{cToast}</div>}
          <p style={{ fontSize:13, color:C.muted, marginBottom:20 }}>
            Send us a message and we'll respond within 1–2 business days.
            For urgent issues please also file a ticket in the <button onClick={()=>setTab("report")} style={{ background:"none", border:"none", color:C.teal, cursor:"pointer", fontSize:13, fontWeight:600, padding:0 }}>Report</button> tab.
          </p>
          <form onSubmit={submitContact} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
              <div>
                <label style={labelStyle}>Name</label>
                <input value={cForm.name} onChange={e=>setCForm(f=>({...f,name:e.target.value}))} style={inputStyle} required />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input value={cForm.email} readOnly style={{ ...inputStyle, color:C.muted, cursor:"not-allowed" }} />
              </div>
            </div>
            <div style={{ marginBottom:18 }}>
              <label style={labelStyle}>Message * <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0, color:C.muted }}>— min 20 characters</span></label>
              <textarea value={cForm.message} onChange={e=>setCForm(f=>({...f,message:e.target.value}))} placeholder="Tell us how we can help…" style={{ ...inputStyle, minHeight:130, resize:"vertical", fontFamily:"inherit" }} maxLength={3000} required />
            </div>
            {cError && <p style={{ color:C.red, fontSize:13, margin:"0 0 14px" }}>{cError}</p>}
            <Btn type="submit" variant="primary" disabled={cSubmitting}>{cSubmitting?"Sending…":"Send Message"}</Btn>
          </form>
        </div>
      )}
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  // Campaign tracking: ?ref=<uuid>  — opaque GUID mapped to a campaign in the backend DB.
  // Never expose the meaning of this value in the UI; store it silently on the participant record.
  // Referral: ?r=RIK-XXXX — participant referral code; shown in step 1 of registration.
  const urlParams = new URLSearchParams(window.location.search);
  const campaignId       = urlParams.get("ref") || null;
  const verifyToken      = urlParams.get("verifyToken");
  const referralCodeParam = urlParams.get("r") || null;

  // Supplier redirect params — present when a panel supplier redirects a participant
  const supplierSurveyId  = urlParams.get("surveyId");
  const supplierSupplierId = urlParams.get("supplierId");
  const supplierPid       = urlParams.get("pid");
  const supplierTs        = urlParams.get("ts");
  const supplierSig       = urlParams.get("sig");
  const isSupplierRedirect  = !!(supplierSurveyId && supplierSupplierId && supplierPid && supplierTs && supplierSig);
  const isWithdrawPage      = !!(urlParams.get("token") || urlParams.get("withdrawToken"));

  const [participant,         setParticipant]         = useState(null);
  const [deletionPending,     setDeletionPending]     = useState(false);
  const [deletionScheduledFor,setDeletionScheduledFor]= useState(null);
  const [consentDocuments,    setConsentDocuments]    = useState([]); // docs requiring re-acceptance
  // Skip /me check on campaign/referral/supplier redirect/withdraw links
  const [authLoading,         setAuthLoading]         = useState(!campaignId && !referralCodeParam && !isSupplierRedirect && !isWithdrawPage);
  const [multilingualSupport,  setMultilingualSupport]  = useState(false);
  const [supportedLanguages,   setSupportedLanguages]   = useState(["English"]);
  const [currentSurvey,       setCurrentSurvey]       = useState(null);
  const [currentInvitationId, setCurrentInvitationId] = useState(null);
  const [resumeSessionId,     setResumeSessionId]     = useState(null);
  // Supplier interview token — issued by /api/surveys/entry, used in place of participant JWT
  const [interviewToken,      setInterviewToken]      = useState(null);
  // Supplier exit URLs — where to redirect after session ends
  const [supplierExitUrls,    setSupplierExitUrls]    = useState(null);
  // Language chosen on SetupPage for profileless (supplier) participants
  const [interviewLanguage,   setInterviewLanguage]   = useState(null);
  const [supplierEntryError,  setSupplierEntryError]  = useState(null);
  const [profileScrollTarget,   setProfileScrollTarget]   = useState(null);
  const [supplierWithdrawalToken, setSupplierWithdrawalToken] = useState(null);

  // Campaign links AND referral links go straight to registration
  // Supplier redirect links go straight to setup (survey briefing) after token validation
  const [phase, setPhase] = useState(
    isWithdrawPage     ? "withdraw"
    : isSupplierRedirect ? "supplier_entry"
    : (campaignId || referralCodeParam) ? "register"
    : "login"
  ); // login | register | consent | dashboard | setup | live | disqualified | completion | profile | privacy | supplier_entry | withdraw

  // ── Notification bell state ──────────────────────────────────────────────
  const [notifItems, setNotifItems]   = useState([]); // pending invitations for the bell panel
  const [payoutNotifs, setPayoutNotifs] = useState([]); // UPI/payout action items for the bell
  const [notifOpen,  setNotifOpen]    = useState(false);
  const notifPanelRef                 = useRef(null);
  // Payout/UPI items surface first — they're action items the participant must resolve.
  const allNotifs = [...payoutNotifs, ...notifItems];

  // ── Supplier entry: validate HMAC redirect, fetch survey, issue interview token
  useEffect(() => {
    if (!isSupplierRedirect) return;
    const params = new URLSearchParams({
      surveyId:    supplierSurveyId,
      supplierId:  supplierSupplierId,
      pid:         supplierPid,
      ts:          supplierTs,
      sig:         supplierSig,
      // Pass exit redirect URLs from the URL if provided
      ...(urlParams.get("complete")     && { complete:     urlParams.get("complete") }),
      ...(urlParams.get("screenOut")    && { screenOut:    urlParams.get("screenOut") }),
      ...(urlParams.get("quotaFull")    && { quotaFull:    urlParams.get("quotaFull") }),
      ...(urlParams.get("securityFail") && { securityFail: urlParams.get("securityFail") }),
      ...(urlParams.get("abandon")      && { abandon:      urlParams.get("abandon") }),
    });
    fetch(`${API_BASE}/api/surveys/entry?${params}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) {
          // Already completed — redirect back to supplier if URL provided
          if (d.redirect) { window.location.href = d.redirect; return; }
          setSupplierEntryError(d.error);
          return;
        }
        setInterviewToken(d.token);
        // Carry resumeSessionId + language if server says this is a resume
        const isResume = !!d.resumeSessionId;
        if (isResume) {
          setResumeSessionId(d.resumeSessionId);
          if (d.language) setInterviewLanguage(d.language);
        }
        // Decode exit URLs from the token payload (base64 middle section)
        try {
          const payload = JSON.parse(atob(d.token.split(".")[1]));
          setSupplierExitUrls(payload.exitUrls || null);
        } catch {}
        // Fetch the survey to show the setup/briefing screen (always, including on resume)
        fetch(`${API_BASE}/api/surveys/${d.surveyId}`)
          .then(r => r.ok ? r.json() : null)
          .then(sd => { if (sd?.survey) { setCurrentSurvey(sd.survey); setPhase("setup"); } });
      })
      .catch(() => setSupplierEntryError("Unable to validate entry. Please try again."));
  }, []); // eslint-disable-line

  // Fetch feature flags from server on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/config`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        if (d.multilingualSupport !== undefined) setMultilingualSupport(d.multilingualSupport);
        if (d.supportedLanguages?.length)        setSupportedLanguages(d.supportedLanguages);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  // Scroll to top whenever the phase changes (prevents stale scroll position between screens)
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [phase]);

  // Close notification panel when clicking outside it
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [notifOpen]);

  // If participant was restored from localStorage but is missing `language` (stale cache),
  // fetch their demographics to hydrate the field so multilingual routing works correctly.
  useEffect(() => {
    if (!participant?.id || participant.language !== undefined) return;
    apiFetch(`${API_BASE}/api/participants/${participant.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.participant) {
          const lang = d.participant.demographics?.language || null;
          setParticipant(p => ({ ...p, language: lang }));
        }
      })
      .catch(() => {});
  }, [participant?.id]); // eslint-disable-line

  // Handle ?verifyToken= email verification link
  useEffect(() => {
    if (!verifyToken) return;
    window.history.replaceState({}, "", window.location.pathname); // clean URL immediately
    fetch(`${API_BASE}/api/auth/verify-email?token=${encodeURIComponent(verifyToken)}`)
      .then(r => r.json())
      .then(d => setVerifyStatus(d.ok ? "success" : "error"))
      .catch(() => setVerifyStatus("error"));
  }, []); // eslint-disable-line
  const [sessionData,   setSessionData]   = useState(null);
  const [verifyStatus,  setVerifyStatus]  = useState(null); // null | "success" | "error"

  // Session restore on mount — GET /api/auth/me re-issues __session cookie (sliding window)
  useEffect(() => {
    if (campaignId || referralCodeParam) return; // campaign/referral links always go to registration
    fetch(`${API_BASE}/api/auth/me`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.user) {
          setParticipant(d.user);
          if (d.deletionPending) {
            setDeletionPending(true);
            setDeletionScheduledFor(d.deletionScheduledFor || null);
          }
          if (d.consentRequired && d.consentDocuments?.length > 0) {
            setConsentDocuments(d.consentDocuments);
            setPhase("consent");
          } else {
            setPhase("dashboard");
          }
        }
      })
      .catch(() => {})
      .finally(() => setAuthLoading(false));
  }, []); // eslint-disable-line

  // ── apiFetch — cookie-based authenticated fetch; forces logout on 401 ─────────
  const apiFetch = useCallback(async (url, options = {}) => {
    // Inject interview token for supplier sessions (no participant cookie)
    const headers = { ...(options.headers || {}) };
    if (interviewToken && !headers["Authorization"])
      headers["Authorization"] = `Bearer ${interviewToken}`;
    const res = await fetch(url, { ...options, headers, credentials: "include" });
    if (res.status === 401 && !interviewToken) {
      setParticipant(null);
      setPhase("login");
      throw new Error("Session expired. Please log in again.");
    }
    return res;
  }, [interviewToken]); // eslint-disable-line

  const handleLogin=(p, directSurvey=null, _token=null, loginData=null)=>{
    setParticipant(p);
    if(loginData?.deletionPending){
      setDeletionPending(true);
      setDeletionScheduledFor(loginData.deletionScheduledFor || null);
    } else {
      setDeletionPending(false);
      setDeletionScheduledFor(null);
    }
    // If legal documents have been updated since last acceptance, gate on consent screen first
    if(loginData?.consentRequired && loginData.consentDocuments?.length > 0){
      setConsentDocuments(loginData.consentDocuments);
      setPhase("consent");
      return;
    }
    if(directSurvey){ setCurrentSurvey(directSurvey); setPhase("setup"); }
    else if(currentSurvey){ setPhase("setup"); }          // magic link already resolved
    else{ setPhase("dashboard"); }
  };

  const handleRegisterComplete=(p)=>{
    setParticipant(p);
    setPhase("dashboard");
  };

  const handleConsentAccepted=()=>{
    setConsentDocuments([]);
    setPhase("dashboard");
  };
  const handleLaunchSurvey=(survey, invitationId=null, existingSessionId=null, sessionLanguage=null)=>{ setCurrentSurvey(survey); setCurrentInvitationId(invitationId); setResumeSessionId(existingSessionId); setInterviewLanguage(sessionLanguage || null); setPhase("setup"); };
  const handleEnd=(data)=>{ setSessionData(data); setPhase("completion"); };
  const handleReturnHome=()=>{ setCurrentSurvey(null); setSessionData(null); setCurrentInvitationId(null); setResumeSessionId(null); setProfileScrollTarget(null); setPhase("dashboard"); };
  const handleGoToPayoutDetails=()=>{ setCurrentSurvey(null); setSessionData(null); setCurrentInvitationId(null); setResumeSessionId(null); setProfileScrollTarget("payout"); setPhase("profile"); };

  // Called when InterviewPage detects a failed screener answer mid-interview
  const handleScreenerFail = async (sessionId) => {
    // Mark the session screened_out so it is excluded from completion counts and,
    // for supplier sessions, posts a "screenout" outcome to the supplier.
    if (sessionId) {
      try {
        await apiFetch(`${API_BASE}/api/sessions/${sessionId}/report`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ endReason: "screened_out" }),
        });
      } catch {} // non-fatal — UI still navigates regardless
    }
    // Internal participants: decline their invitation so it leaves the pending pool.
    if (currentInvitationId) {
      try {
        await apiFetch(`${API_BASE}/api/invitations/${currentInvitationId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "declined" }),
        });
      } catch {} // non-fatal — UI still navigates regardless
    }
    // Supplier participants: redirect back to the supplier's screen-out URL.
    if (supplierExitUrls?.screenOut) {
      window.location.href = supplierExitUrls.screenOut;
      return;
    }
    setPhase("disqualified");
  };

  if(authLoading) return <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center" }}><div style={{ color:C.muted, fontSize:14 }}>Loading…</div></div>;
  // Supplier redirect — loading / error screens before interview starts
  if (phase === "supplier_entry") {
    return (
      <div style={{ minHeight: "100vh", background: "#06080F", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16, color: "#fff", fontFamily: "Poppins, sans-serif" }}>
        {supplierEntryError
          ? <><div style={{ fontSize: 18, color: "#F87171" }}>Unable to start survey</div><div style={{ color: "#9CA3AF", fontSize: 14 }}>{supplierEntryError}</div></>
          : <><div style={{ fontSize: 18 }}>Preparing your interview…</div><div style={{ color: "#9CA3AF", fontSize: 14 }}>Validating your entry</div></>
        }
      </div>
    );
  }

  if(phase==="register") return <RegistrationFlow onComplete={handleRegisterComplete} onBackToLogin={()=>setPhase("login")} campaignId={campaignId} referralCode={referralCodeParam} supportedLanguages={supportedLanguages} />;
  if(phase==="login")    return <LoginPage onLogin={handleLogin} onRegister={()=>setPhase("register")} verifyStatus={verifyStatus} onVerifyStatusClear={()=>setVerifyStatus(null)} />;
  if(phase==="consent")  return <ConsentGateScreen participant={participant} documents={consentDocuments} apiFetch={apiFetch} onAccepted={handleConsentAccepted} />;

  return (
    <div style={{ minHeight:"100vh", background:C.bg, fontFamily:"'Poppins','Segoe UI',Arial,sans-serif", color:C.text }}>
      <style>{`*{box-sizing:border-box;} html,body{overflow-x:hidden;} ::-webkit-scrollbar{width:4px;} ::-webkit-scrollbar-thumb{background:${C.subtle};border-radius:2px;} @keyframes pulseDot{0%,100%{opacity:1}50%{opacity:0.3}} @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}} input{outline:none;}
.voxa-header{padding:0 32px;} @media(max-width:640px){.voxa-header{padding:0 16px;}}
.signout-text{display:inline;} .signout-icon{display:none;} @media(max-width:640px){.signout-text{display:none;} .signout-icon{display:flex;}}
.voxa-main{padding:32px;} .voxa-main.live{padding:16px 24px;} @media(max-width:640px){.voxa-main{padding:20px 16px;}.voxa-main.live{padding:8px 12px;}}
.notif-panel{position:absolute;top:44px;right:0;width:300px;} @media(max-width:640px){.notif-panel{position:fixed;top:58px;left:12px;right:12px;width:auto;}}
.header-name{display:inline;} @media(max-width:480px){.header-name{display:none;}}
.header-actions{display:flex;align-items:center;gap:12px;} @media(max-width:480px){.header-actions{gap:6px;}}
.voxa-completion{max-width:560px;margin:80px auto;text-align:center;padding:0 24px;} @media(max-width:640px){.voxa-completion{margin:36px auto;}}
@keyframes savePulse{0%,100%{box-shadow:0 0 0 0 rgba(245,158,11,0.45),0 0 22px rgba(245,158,11,0.30);}60%{box-shadow:0 0 0 10px rgba(245,158,11,0),0 0 28px rgba(245,158,11,0.12);}}
@keyframes saveSpin{to{transform:rotate(360deg);}}
@keyframes savePop{from{opacity:0;transform:translateY(8px) scale(0.97);}to{opacity:1;transform:translateY(0) scale(1);}}
.profile-2col{display:grid;grid-template-columns:1fr 1fr;gap:0 24px;} .profile-2col-narrow{display:grid;grid-template-columns:1fr 1fr;gap:0 16px;} @media(max-width:540px){.profile-2col,.profile-2col-narrow{grid-template-columns:1fr;}}
.dash-card{padding:16px 18px !important;overflow:hidden;}
.dash-card-head{display:flex;align-items:center;gap:10px;margin-bottom:8px;min-width:0;}
.dash-card-icon{width:44px;height:44px;font-size:22px;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.dash-card-name{flex:1;min-width:0;font-weight:600;font-size:15px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.dash-card-btn{flex-shrink:0;white-space:nowrap;}
.dash-card-meta{display:flex;align-items:center;gap:6px;flex-wrap:wrap;padding-left:54px;}
@media(max-width:480px){.dash-card-icon{width:38px;height:38px;font-size:19px;}.dash-card-meta{padding-left:48px;}}
@media(max-width:390px){
  .dash-card{padding:12px 14px !important;}
  .dash-card-head{flex-wrap:wrap;gap:8px;}
  .dash-card-name{white-space:normal;word-break:break-word;font-size:14px;}
  .dash-card-btn{margin-left:auto;}
  .dash-card-meta{padding-left:0;margin-top:4px;}
}
`}</style>

      {phase!=="live"&&(
        <header className="voxa-header" style={{ borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", height:58, position:"sticky", top:0, background:C.bg, zIndex:100 }}>
          <div
            onClick={()=>setPhase(participant?"dashboard":"login")}
            role="button"
            aria-label="Go to home"
            tabIndex={0}
            onKeyDown={e=>{if(e.key==="Enter"||e.key===" ")setPhase(participant?"dashboard":"login");}}
            style={{ display:"flex", alignItems:"flex-end", gap:10, cursor:"pointer" }}
          >
            <img src="/rik-logo.png" alt="Rik.ai" style={{ height:36, width:"auto" }} onError={e=>{e.currentTarget.style.display="none";}} />
            <span style={{ lineHeight:1 }}>
              <span style={{ fontWeight:700, fontSize:26, fontFamily:"'Poppins',sans-serif", color:C.text, letterSpacing:"-1px" }}>Rik</span><span style={{ fontWeight:300, fontSize:26, fontFamily:"'Poppins',sans-serif", letterSpacing:"-1px", background:"linear-gradient(135deg,#8B5CF6,#3B82F6,#22D3EE)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text", paddingLeft:3 }}>.ai</span>
            </span>
          </div>
          <div className="header-actions">
            {/* ── Help & Support button ── */}
            {participant&&(
              <button
                onClick={()=>setPhase("support")}
                aria-label="Help & Support"
                title="Help & Support"
                style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:8, width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:C.muted, transition:"all 0.2s", flexShrink:0 }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=C.blue;e.currentTarget.style.color=C.blue;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.muted;}}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </button>
            )}

            {/* ── Notification bell ── */}
            {participant&&(
              <div ref={notifPanelRef} style={{ position:"relative" }}>
                <button
                  onClick={()=>setNotifOpen(o=>!o)}
                  aria-label={`Notifications${allNotifs.length>0?` (${allNotifs.length} pending)`:""}`}
                  style={{ position:"relative", background:"transparent", border:`1px solid ${allNotifs.length>0?C.blue:C.border}`, borderRadius:8, width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:allNotifs.length>0?C.blue:C.muted, transition:"all 0.2s" }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                  {allNotifs.length>0&&(
                    <span style={{ position:"absolute", top:-5, right:-5, background:C.blue, color:"#fff", fontSize:10, fontWeight:700, borderRadius:"50%", minWidth:18, height:18, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px", lineHeight:1, boxShadow:`0 0 6px ${C.blue}` }}>
                      {allNotifs.length>9?"9+":allNotifs.length}
                    </span>
                  )}
                </button>
                {notifOpen&&(
                  <div className="notif-panel" style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:`0 8px 32px #00000060`, zIndex:200, overflow:"hidden" }}>
                    <div style={{ padding:"12px 16px 10px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <span style={{ fontWeight:700, fontSize:13, color:C.text }}>Notifications</span>
                      {allNotifs.length>0&&<span style={{ fontSize:11, color:C.muted }}>{allNotifs.length} pending</span>}
                    </div>
                    {allNotifs.length===0?(
                      <div style={{ padding:"24px 16px", textAlign:"center", color:C.muted, fontSize:13 }}>No new notifications</div>
                    ):(
                      <div style={{ maxHeight:320, overflowY:"auto" }}>
                        {allNotifs.map(item=>(
                          item.type==="upi_missing"||item.type==="upi_verify" ? (
                            <div key={item.id} style={{ padding:"12px 16px", borderBottom:`1px solid ${C.border}30`, display:"flex", alignItems:"flex-start", gap:10 }}>
                              <div style={{ width:8, height:8, borderRadius:"50%", background:C.amber, marginTop:5, flexShrink:0, boxShadow:`0 0 6px ${C.amber}` }} />
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:2 }}>
                                  {item.type==="upi_missing" ? "Add your UPI to get paid" : "Verify your UPI ID"}
                                </div>
                                <div style={{ fontSize:11, color:C.muted, marginBottom:8 }}>
                                  {item.type==="upi_missing"
                                    ? `You have ${item.count} pending payout${item.count!==1?"s":""} (₹${item.amount}). Add your UPI to receive it.`
                                    : `Confirm the test deposit we sent to ${item.maskedUpiId} to start receiving payouts.`}
                                </div>
                                <Btn size="sm" variant="outline" onClick={()=>{ setNotifOpen(false); setPhase("profile"); }}>
                                  {item.type==="upi_missing" ? "Add UPI" : "Verify now"}
                                </Btn>
                              </div>
                            </div>
                          ) : (
                            <div key={item.id} style={{ padding:"12px 16px", borderBottom:`1px solid ${C.border}30`, display:"flex", alignItems:"flex-start", gap:10 }}>
                              <div style={{ width:8, height:8, borderRadius:"50%", background:C.blue, marginTop:5, flexShrink:0, boxShadow:`0 0 6px ${C.blue}` }} />
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{item.survey?.title||"Survey Invitation"}</div>
                                <div style={{ fontSize:11, color:C.muted, marginBottom:8 }}>~{item.survey?.estimatedMins||"?"} min · {item.survey?.category||""}</div>
                                <Btn size="sm" variant="outline" onClick={()=>{ setNotifItems(prev=>prev.filter(n=>n.id!==item.id)); setNotifOpen(false); handleLaunchSurvey(item.survey, item.id, null); }}>Start</Btn>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div onClick={participant&&!deletionPending?()=>setPhase("profile"):undefined} style={{ display:"flex", alignItems:"center", gap:8, cursor:participant&&!deletionPending?"pointer":"default", borderRadius:8, padding:"4px 8px", transition:"background 0.2s" }}
              onMouseEnter={e=>{ if(participant) e.currentTarget.style.background=C.subtle; }} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{ width:32, height:32, borderRadius:"50%", background:`${C.blue}20`, border:`1px solid ${C.blue}50`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:700, color:C.blue }}>
                {participant?.name?.[0]||"?"}
              </div>
              <span className="header-name" style={{ color:C.muted, fontSize:13, maxWidth:110, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{participant?.name}</span>
            </div>
            <Btn variant="ghost" size="sm" onClick={()=>{
              fetch(`${API_BASE}/api/auth/logout`,{method:"POST",credentials:"include"}).catch(()=>{});
              setParticipant(null); setNotifItems([]); setPayoutNotifs([]); setNotifOpen(false); setPhase("login");
            }} title="Sign out" aria-label="Sign out">
              <span className="signout-text">Sign out</span>
              <span className="signout-icon" style={{ alignItems:"center", justifyContent:"center" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              </span>
            </Btn>
          </div>
        </header>
      )}

      <main className={`voxa-main${phase==="live"?" live":""}`}>
        {phase==="dashboard"     && <DashboardPage participant={participant} onLaunchSurvey={handleLaunchSurvey} apiFetch={apiFetch} deletionPending={deletionPending} deletionScheduledFor={deletionScheduledFor} onInvitationsLoaded={setNotifItems} onPayoutNotifsLoaded={setPayoutNotifs} onRestoreAccount={async()=>{ try{ const r=await apiFetch(`${API_BASE}/api/participants/${participant.id}/restore`,{method:"POST",headers:{"Content-Type":"application/json"}}); if(r.ok){ fetch(`${API_BASE}/api/auth/logout`,{method:"POST",credentials:"include"}).catch(()=>{}); setParticipant(null); setDeletionPending(false); setDeletionScheduledFor(null); setPhase("login"); } }catch{} }} />}
        {phase==="setup"         && <SetupPage survey={currentSurvey} participant={participant} interviewToken={interviewToken} multilingualSupport={multilingualSupport} supportedLanguages={supportedLanguages} lockedLanguage={resumeSessionId ? interviewLanguage : null} onStart={(meta={})=>{ if(meta?.withdrawalToken) setSupplierWithdrawalToken(meta.withdrawalToken); if(meta?.language) setInterviewLanguage(meta.language); setPhase("live"); }} onBack={()=>setPhase("dashboard")} />}
        {phase==="disqualified"  && <DisqualifiedPage survey={currentSurvey} onBack={()=>{ setCurrentSurvey(null); setCurrentInvitationId(null); setPhase("dashboard"); }} />}
        {phase==="live"          && <InterviewPage survey={currentSurvey} participant={participant} onEnd={handleEnd} onDisqualified={handleScreenerFail} resumeSessionId={resumeSessionId} multilingualSupport={multilingualSupport} apiFetch={apiFetch} interviewToken={interviewToken} languageOverride={interviewLanguage} />}
        {phase==="completion"    && <CompletionPage survey={currentSurvey} sessionData={sessionData} participant={participant} onReturnHome={handleReturnHome} onGoToPayoutDetails={handleGoToPayoutDetails} apiFetch={apiFetch} supplierExitUrls={supplierExitUrls} withdrawalToken={supplierWithdrawalToken} />}
        {phase==="profile"       && !deletionPending && <ProfilePage participant={participant} onBack={()=>{ setProfileScrollTarget(null); setPhase("dashboard"); }} onNameChange={(n)=>setParticipant(p=>({...p,name:n}))} onSignOut={()=>{ fetch(`${API_BASE}/api/auth/logout`,{method:"POST",credentials:"include"}).catch(()=>{}); setParticipant(null); setPhase("login"); }} apiFetch={apiFetch} supportedLanguages={supportedLanguages} scrollToSection={profileScrollTarget} onManageConsents={()=>setPhase("privacy")} />}
        {phase==="profile"       && deletionPending  && (()=>{ setPhase("dashboard"); return null; })()}
        {phase==="privacy"       && <PrivacyPage participant={participant} apiFetch={apiFetch} onBack={()=>setPhase("profile")} />}
        {phase==="withdraw"      && <WithdrawPage />}
        {phase==="support"       && <SupportPage participant={participant} apiFetch={apiFetch} onBack={()=>setPhase("dashboard")} />}
      </main>
      {phase !== "live" && (
        <footer style={{ borderTop:`1px solid ${C.border}`, padding:"14px 32px", textAlign:"center", fontSize:11, color:C.muted }}>
          &copy; 2026 Rik Systems LLP &nbsp;&middot;&nbsp;
          <a href={LEGAL_PP} target="_blank" rel="noopener" style={{ color:C.muted, textDecoration:"underline" }}>Privacy Policy</a>
          &nbsp;&middot;&nbsp;
          <a href={LEGAL_TOS} target="_blank" rel="noopener" style={{ color:C.muted, textDecoration:"underline" }}>Terms of Service</a>
          &nbsp;&middot;&nbsp;
          <a href="mailto:contact@rikai.tech" style={{ color:C.muted, textDecoration:"underline" }}>contact@rikai.tech</a>
        </footer>
      )}
    </div>
  );
}
